/**
 * Solver Page - Layout matching reference design
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Fade,
  Backdrop,
  Modal,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

// Logo import
import logoWithText from "../assets/images/headerlogo.svg";

// Preflop (hardcoded, NOT from AWS)
import { getPreflopDecisionGrid } from "../preflop/opens";
import { loadSolverPrefs, SOLVER_DECK_OPTIONS, SOLVER_COLORS_EVENT, CHIP_COLOR_OPTIONS, hexToRgba } from "../theme/appearancePrefs";
import PageTutorial, {
  SOLVER_TUTORIAL_STORAGE_KEY,
  hasCompletedTutorial,
  markTutorialComplete,
} from "../components/PageTutorial";
import { isPaywallRequired, openSubscriptionGate } from "../utils/billingAccess";

// ============================================================================
// Config & Constants
// ============================================================================

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";
const SOLVER_DEFAULT_MODE = (process.env.REACT_APP_SOLVER_DEFAULT_MODE || "cash").toLowerCase();
const SOLVER_CASH_TABLE_SIZE = (process.env.REACT_APP_SOLVER_CASH_TABLE_SIZE || "6handed").toLowerCase();
const SOLVER_MTT_TABLE_SIZE = (process.env.REACT_APP_SOLVER_MTT_TABLE_SIZE || "8handed").toLowerCase();
const SOLVER_CASH_OPEN_SIZE = (process.env.REACT_APP_SOLVER_CASH_OPEN_SIZE || "2.5x").toLowerCase();
const SOLVER_MTT_OPEN_SIZE = (process.env.REACT_APP_SOLVER_MTT_OPEN_SIZE || "2x").toLowerCase();
const parseCsvLower = (value, fallback) =>
  String(value || fallback || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
const SOLVER_CASH_STACK_OPTIONS = parseCsvLower(process.env.REACT_APP_SOLVER_CASH_STACK_SIZES, "100bbs");
const SOLVER_MTT_STACK_OPTIONS = parseCsvLower(process.env.REACT_APP_SOLVER_MTT_STACK_SIZES, "30bbs");
const INITIAL_SOLVER_MODE = SOLVER_DEFAULT_MODE === "mtt" ? "mtt" : "cash";
const INITIAL_STACK_OPTIONS = INITIAL_SOLVER_MODE === "mtt" ? SOLVER_MTT_STACK_OPTIONS : SOLVER_CASH_STACK_OPTIONS;
const INITIAL_STACK_SIZE =
  (process.env.REACT_APP_SOLVER_DEFAULT_STACK_SIZE || "").trim().toLowerCase() ||
  INITIAL_STACK_OPTIONS[0] ||
  (INITIAL_SOLVER_MODE === "mtt" ? "30bbs" : "100bbs");
const SHOW_DEV_COMBO_TRACE = process.env.NODE_ENV !== "production";
const SHOW_DEV_HAND_DEFINITION = process.env.NODE_ENV !== "production";
const SHOW_DEV_DEBUG_UI = process.env.NODE_ENV !== "production";
const SHOW_DEV_ERROR_BANNER = process.env.NODE_ENV !== "production";
// Temporary: V6 dataset version id (uploaded as v6-upload). You can override via .env:
// REACT_APP_SOLVER_V6_VERSION_ID=v6-upload
const SOLVER_V6_VERSION_ID = process.env.REACT_APP_SOLVER_V6_VERSION_ID || "v6-upload";
const USE_V6 = (process.env.REACT_APP_SOLVER_USE_V6 || "true").toLowerCase() === "true";
const TURN_RIVER_PAYWALL_ENABLED = (process.env.REACT_APP_TURN_RIVER_PAYWALL || "false").toLowerCase() === "true";
const FLOP_PAYWALL_ENABLED = (process.env.REACT_APP_FLOP_PAYWALL || "true").toLowerCase() === "true";
const TRAINER_SOLVE_HANDOFF_KEY = "pm_trainer_solve_handoff_v1";
const PAGE_SIDE_PADDING_PX = 16;
const DESKTOP_PANEL_GAP_PX = 12;
const DESKTOP_RIGHT_STACK_GAP_PX = 8;

/** Tutorial action allowlist helpers. null = unrestricted, [] = none. */
function tutorialActionMatches(actionIdOrLabel, allowlist) {
  if (!Array.isArray(allowlist)) return true;
  if (allowlist.length === 0) return false;
  const raw = String(actionIdOrLabel || "").toLowerCase().replace(/\s+/g, "");
  return allowlist.some((a) => {
    const want = String(a || "").toLowerCase().replace(/\s+/g, "");
    if (!want) return false;
    if (want === "raise") return raw.includes("raise") || raw.includes("open") || raw.includes("bet");
    if (want === "call") return raw.includes("call") && !raw.includes("check");
    if (want === "check") return raw.includes("check") || raw === "x";
    if (want === "fold") return raw.includes("fold");
    if (want === "allin") return raw.includes("allin") || raw.includes("all-in");
    return raw.includes(want);
  });
}

function filterTutorialActions(actions, allowlist) {
  if (!Array.isArray(allowlist)) return actions;
  return (actions || []).map((a) => {
    if (allowlist.length === 0) return { ...a, enabled: false };
    const ok = tutorialActionMatches(a?.id, allowlist) || tutorialActionMatches(a?.label, allowlist);
    return { ...a, enabled: !!a.enabled && ok };
  });
}

// PokerMoose Color palette
const COLORS = {
  gold: "#B99E4F",
  green: "#56ECCF",
  silver: "#D0D0D0",
  black: "#282828",
  bg: "#101012",
  cardBg: "#222222",
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#888",
  textDark: "#555",
  red: "#c44444",
  white: "#ffffff",
};
const FUTURA_FONT = "Futura, \"Futura Md Bt\", \"Trebuchet MS\", Arial, sans-serif";
/** Book/regular face; avoids Heavy + synthetic bold blur on small UI chrome. */
const FUTURA_BOOK = "\"Futura Md Bt\", Futura, \"Trebuchet MS\", Arial, sans-serif";
const THIN_SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(95,95,95,0.38) transparent",
  "&::-webkit-scrollbar": { height: "2px", width: "2px" },
  "&::-webkit-scrollbar-track": { background: "transparent", borderRadius: "999px" },
  "&::-webkit-scrollbar-thumb": { background: "rgba(95,95,95,0.38)", borderRadius: "999px" },
  "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
  "&::-webkit-scrollbar-corner": { background: "transparent" },
};

// Action colors for grid
const ACTION_COLORS = {
  check: "#1EAB80",
  call: "#1EAB80",
  bet: "#8A7A01",
  raise: "#8A7A01",
  fold: "#821C2A",
  largeBet: "#077396",
  largest: "#48149E",
  hugeBet: "#8F2C7D",
  blocked: "#2a2a2a",
};
const ACTION_BOX_W = 72;
const ACTION_BOX_H = 132;
const ACTION_BOX_W_MOBILE = 132;
const ACTION_BOX_H_MOBILE = 248;
const ACTION_OPTION_FS = 14; // floor for Fold/Call/Bet option UI + seat names
const ACTION_OPTION_FS_DESKTOP_TILE = 11; // desktop tiles mode (half-size + 20%)
const CHIP_LABEL_FS_FLOOR = 10; // floor for chip amount numbers on the table

/** Scroll so `child` is centred in the scroller when possible.
 *  Early tiles stay flush-left (no gap before content): scrollLeft is clamped to >= 0,
 *  so centering only kicks in once the active tile would sit at/ past the midpoint.
 */
function centerChildInScroller(scroller, child, behavior = "smooth") {
  if (!scroller || !child) return;
  const sRect = scroller.getBoundingClientRect();
  const cRect = child.getBoundingClientRect();
  if (!sRect.width || !cRect.width) return;
  const delta = (cRect.left + cRect.width / 2) - (sRect.left + sRect.width / 2);
  // Only scroll when the tile is right of centre (or we need to unwind a prior centre).
  // Clamping to 0 keeps the first tiles glued next to Reset with no floating gap.
  const nextLeft = Math.max(0, Math.min(scroller.scrollWidth - scroller.clientWidth, scroller.scrollLeft + delta));
  if (Math.abs(nextLeft - scroller.scrollLeft) < 1) return;
  scroller.scrollTo({ left: nextLeft, behavior });
}

// Module-level so getActionColor / card renderers pick colours up everywhere;
// the Solver component bumps state when the global Settings dialog changes them.
let SOLVER_PREFS = loadSolverPrefs();
let SUIT_COLORS = { ...(SOLVER_DECK_OPTIONS[SOLVER_PREFS.deck] || SOLVER_DECK_OPTIONS.four).colors };

function applySolverPrefs(prefs) {
  SOLVER_PREFS = prefs;
  SUIT_COLORS = { ...(SOLVER_DECK_OPTIONS[prefs.deck] || SOLVER_DECK_OPTIONS.four).colors };
}

const SUIT_SYMBOLS = { s: "♠", h: "♥", d: "♦", c: "♣" };

// Positions in order
const POSITIONS = [
  { id: "lj", name: "LJ" }, { id: "hj", name: "HJ" }, { id: "co", name: "CO" },
  { id: "btn", name: "BTN" }, { id: "sb", name: "SB" }, { id: "bb", name: "BB" },
];
const POSITION_NAME_FULL = {
  lj: "Lojack",
  hj: "Hijack",
  co: "Cutoff",
  btn: "Button",
  sb: "Small Blind",
  bb: "Big Blind",
  lo: "Lojack",
  hi: "Hijack",
  ip: "In Position",
  oop: "Out of Position",
};

// Seat codes used in S3 spot names (LO/HI naming for LJ/HJ).
const SEAT_CODE = { lj: "LO", hj: "HI", co: "CO", btn: "BTN", sb: "SB", bb: "BB" };
// Postflop action order (OOP acts first). Higher index => more in position.
const POSTFLOP_ORDER = ["sb", "bb", "lj", "hj", "co", "btn"];
// Preflop voluntary action order (UTG first in 6-max with POSITIONS = lj..bb).
const PREFLOP_ACT_ORDER = ["lj", "hj", "co", "btn", "sb", "bb"];

// Card constants
const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const SUITS = ["s", "h", "d", "c"];
const STRAIGHT_WINDOWS = [
  [14, 13, 12, 11, 10],
  [13, 12, 11, 10, 9],
  [12, 11, 10, 9, 8],
  [11, 10, 9, 8, 7],
  [10, 9, 8, 7, 6],
  [9, 8, 7, 6, 5],
  [8, 7, 6, 5, 4],
  [7, 6, 5, 4, 3],
  [6, 5, 4, 3, 2],
  [14, 5, 4, 3, 2],
];

function handLabelFromTwoCards(handCards) {
  const c1 = handCards?.[0];
  const c2 = handCards?.[1];
  if (!c1 || !c2 || c1.length < 2 || c2.length < 2) return null;
  const r1 = c1[0], s1 = c1[1];
  const r2 = c2[0], s2 = c2[1];
  const i1 = RANKS.indexOf(r1);
  const i2 = RANKS.indexOf(r2);
  if (i1 < 0 || i2 < 0) return null;
  if (r1 === r2) return `${r1}${r2}`;
  const hi = i1 < i2 ? r1 : r2;
  const lo = i1 < i2 ? r2 : r1;
  return `${hi}${lo}${s1 === s2 ? "s" : "o"}`;
}

function comboStringFromTwoCards(handCards) {
  const c1 = handCards?.[0];
  const c2 = handCards?.[1];
  if (!c1 || !c2 || c1.length < 2 || c2.length < 2) return null;
  const r1 = c1[0], s1 = c1[1];
  const r2 = c2[0], s2 = c2[1];
  const i1 = RANKS.indexOf(r1);
  const i2 = RANKS.indexOf(r2);
  if (i1 < 0 || i2 < 0) return null;
  if (r1 === r2) {
    const a = SUITS.indexOf(s1);
    const b = SUITS.indexOf(s2);
    if (a <= b) return `${r1}${s1}${r2}${s2}`;
    return `${r2}${s2}${r1}${s1}`;
  }
  if (i1 <= i2) return `${r1}${s1}${r2}${s2}`;
  return `${r2}${s2}${r1}${s1}`;
}

function handCardsFromComboString(comboHand) {
  if (!comboHand || comboHand.length !== 4) return [null, null];
  return [comboHand.slice(0, 2), comboHand.slice(2, 4)];
}

function comboToHandCategory(combo) {
  if (combo?.length !== 4) return null;
  const rank1 = combo[0];
  const suit1 = combo[1];
  const rank2 = combo[2];
  const suit2 = combo[3];
  const r1idx = RANKS.indexOf(rank1);
  const r2idx = RANKS.indexOf(rank2);
  if (r1idx === -1 || r2idx === -1) return null;
  if (rank1 === rank2) return rank1 + rank1;
  if (suit1 === suit2) return r1idx < r2idx ? rank1 + rank2 + "s" : rank2 + rank1 + "s";
  return r1idx < r2idx ? rank1 + rank2 + "o" : rank2 + rank1 + "o";
}

function isPocketPairCombo(comboStr) {
  return comboStr?.length === 4 && comboStr[0] === comboStr[2];
}

function isPrimaryApiComboUsable(apiComboStr, comboData, desiredComboHand = null) {
  if (!apiComboStr || !comboData?.frequencies?.length) return false;
  const cat = comboToHandCategory(apiComboStr);
  const mappedIsPairCategory = !!(cat && cat.length === 2 && cat[0] === cat[1]);
  if (mappedIsPairCategory) {
    if (!isPocketPairCombo(desiredComboHand || "")) return false;
  }
  return true;
}

function findCellByLabel(grid, label) {
  if (!grid || !label) return null;
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < (grid[row] || []).length; col++) {
      if (grid[row]?.[col]?.label === label) {
        return { row, col, cell: grid[row][col] };
      }
    }
  }
  return null;
}

function pickPreferredSelectedCell(grid, userHand, prevSelectedCell) {
  if (!grid?.[0]?.[0]) return null;
  const userLabel = handLabelFromTwoCards(userHand);
  const userPick = findCellByLabel(grid, userLabel);
  if (userPick) return userPick;
  if (prevSelectedCell && typeof prevSelectedCell.row === "number" && typeof prevSelectedCell.col === "number") {
    const keep = grid?.[prevSelectedCell.row]?.[prevSelectedCell.col];
    if (keep) return { row: prevSelectedCell.row, col: prevSelectedCell.col, cell: keep };
  }
  return { row: 0, col: 0, cell: grid[0][0] };
}

// Spot mapping
const SPOT_MAPPING = {
  "lj:open,hj:call": { spot: "LOopenHIcall", name: "LJ Open, HJ Call", flops: ["Js5c2h", "AhKd3h"], positions: { oop: "LJ", ip: "HJ" } },
  "co:open,sb:3bet,co:call": { spot: "SB3betCOcall", name: "SB 3-bet, CO Call", flops: ["AhJd9c"], positions: { oop: "SB", ip: "CO" } },
  "sb:open,bb:call": { spot: "SBopenBBcall", name: "SB Open, BB Call", flops: ["Ah7d5d", "Ah7d5h", "Ah7d5c", "Ah7d6c", "Ah7d6d"], positions: { oop: "SB", ip: "BB" } },
};

function detectOpenCallSpotFromPreflopHistory(actionHistory) {
  const significant = (actionHistory || []).filter((a) => a.action !== "fold");
  if (significant.length !== 2) return null;
  const [a1, a2] = significant;
  if (a1.action !== "open" || a2.action !== "call") return null;

  const opener = a1.position;
  const caller = a2.position;
  if (!SEAT_CODE[opener] || !SEAT_CODE[caller]) return null;

  const openerCode = SEAT_CODE[opener];
  const callerCode = SEAT_CODE[caller];
  const spot = `${openerCode}open${callerCode}call`;

  const o1 = POSTFLOP_ORDER.indexOf(opener);
  const o2 = POSTFLOP_ORDER.indexOf(caller);
  const oop = o1 <= o2 ? opener : caller;
  const ip = oop === opener ? caller : opener;

  return {
    spot,
    name: `${openerCode} Open, ${callerCode} Call`,
    // We may not know the flop list ahead of time for all spots; allow any flop.
    flops: null,
    positions: { oop: SEAT_CODE[oop], ip: SEAT_CODE[ip] },
  };
}

/** S3-style: opener raises, another seat 3-bets, original opener calls (e.g. BTN3betCOcall). */
function detectThreeBetCallSpotFromPreflopHistory(actionHistory) {
  const significant = (actionHistory || []).filter((a) => a.action !== "fold");
  if (significant.length !== 3) return null;
  const [a1, a2, a3] = significant;
  if (a1.action !== "open" || a2.action !== "3bet" || a3.action !== "call") return null;
  if (a1.position !== a3.position) return null;

  const opener = a1.position;
  const threeBettor = a2.position;
  if (!SEAT_CODE[opener] || !SEAT_CODE[threeBettor]) return null;

  const openerCode = SEAT_CODE[opener];
  const threeCode = SEAT_CODE[threeBettor];
  const spot = `${threeCode}3bet${openerCode}call`;

  const o1 = POSTFLOP_ORDER.indexOf(opener);
  const o2 = POSTFLOP_ORDER.indexOf(threeBettor);
  const oop = o1 <= o2 ? opener : threeBettor;
  const ip = oop === opener ? threeBettor : opener;

  return {
    spot,
    name: `${threeCode} 3-Bet, ${openerCode} Call`,
    flops: null,
    positions: { oop: SEAT_CODE[oop], ip: SEAT_CODE[ip] },
  };
}

/** S3-style: open -> 3-bet -> 4-bet -> call (e.g. SB4betBBcall). */
function detectFourBetCallSpotFromPreflopHistory(actionHistory) {
  const significant = (actionHistory || []).filter((a) => a.action !== "fold");
  if (significant.length !== 4) return null;
  const [a1, a2, a3, a4] = significant;
  if (a1.action !== "open" || a2.action !== "3bet" || a3.action !== "4bet" || a4.action !== "call") return null;
  // Standard heads-up 4-bet tree: opener 4-bets, 3-bettor calls.
  if (a3.position !== a1.position) return null;
  if (a4.position !== a2.position) return null;

  const fourBettor = a3.position;
  const caller = a4.position;
  if (!SEAT_CODE[fourBettor] || !SEAT_CODE[caller]) return null;

  const fourBetCode = SEAT_CODE[fourBettor];
  const callerCode = SEAT_CODE[caller];
  const spot = `${fourBetCode}4bet${callerCode}call`;

  const o1 = POSTFLOP_ORDER.indexOf(fourBettor);
  const o2 = POSTFLOP_ORDER.indexOf(caller);
  const oop = o1 <= o2 ? fourBettor : caller;
  const ip = oop === fourBettor ? caller : fourBettor;

  return {
    spot,
    name: `${fourBetCode} 4-Bet, ${callerCode} Call`,
    flops: null,
    positions: { oop: SEAT_CODE[oop], ip: SEAT_CODE[ip] },
  };
}

function isSameDetectedSpot(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.spot === b.spot &&
    a.name === b.name &&
    (a.positions?.oop || null) === (b.positions?.oop || null) &&
    (a.positions?.ip || null) === (b.positions?.ip || null)
  );
}

function mapPostflopLookupSpot(spot) {
  if (!spot) return spot;
  const explicitRemaps = {
    LO4betHIcall: "HI4betCOcall",
    LO4betCOcall: "HI4betCOcall",
    LO4betBTNcall: "HI4betBTNcall",
    LO4betSBcall: "HI4betSBcall",
    LO4betBBcall: "HI4betBBcall",
  };
  if (explicitRemaps[spot]) return explicitRemaps[spot];
  // Explicit override requested by product mapping:
  // HI3betLOcall should read from CO3betHIcall.
  if (spot === "HI3betLOcall") return "CO3betHIcall";
  // For all LO spots in postflop lookups, replace LO with HI.
  if (spot.includes("LO")) return spot.replaceAll("LO", "HI");
  return spot;
}

// ============================================================================
// Suit Isomorphism / Canonicalization
// ============================================================================

// Canonical suit order: h (hearts), d (diamonds), c (clubs), s (spades)
const CANONICAL_SUITS = ['h', 'd', 'c', 's'];

// ============================================================================
// Closest-board lookup (temporary heuristic)
// ============================================================================

const RANK_VALUE = { A: 14, K: 13, Q: 12, J: 11, T: 10, "9": 9, "8": 8, "7": 7, "6": 6, "5": 5, "4": 4, "3": 3, "2": 2 };

function flopStrToCards(flopStr) {
  if (!flopStr || flopStr.length !== 6) return [];
  return [flopStr.slice(0, 2), flopStr.slice(2, 4), flopStr.slice(4, 6)];
}

function flopRankKind(cards3) {
  const ranks = cards3.map((c) => c[0]);
  const uniq = new Set(ranks).size;
  if (uniq === 1) return 0; // trips
  if (uniq === 2) return 1; // pair
  return 2; // all different
}

function flopSuitKind(cards3) {
  const suits = cards3.map((c) => c[1]);
  const counts = {};
  for (const s of suits) counts[s] = (counts[s] || 0) + 1;
  const maxCount = Math.max(...Object.values(counts));
  if (maxCount === 3) return 0; // monotone
  if (maxCount === 2) return 1; // two-tone
  return 2; // rainbow
}

function flopRankValuesDesc(cards3) {
  const vals = cards3.map((c) => RANK_VALUE[c[0]] || 0);
  return vals.sort((a, b) => b - a);
}

function pairedFlopInfo(cards3) {
  if (!cards3 || cards3.length !== 3) return null;
  const rankToCount = {};
  for (const c of cards3) {
    const r = c[0];
    rankToCount[r] = (rankToCount[r] || 0) + 1;
  }
  const entries = Object.entries(rankToCount);
  if (entries.length !== 2) return null; // Not exactly one pair + one kicker
  const pairEntry = entries.find(([, cnt]) => cnt === 2);
  const kickerEntry = entries.find(([, cnt]) => cnt === 1);
  if (!pairEntry || !kickerEntry) return null;
  return {
    pairRank: RANK_VALUE[pairEntry[0]] || 0,
    kickerRank: RANK_VALUE[kickerEntry[0]] || 0,
  };
}

function scoreFlopCandidate(requestedCards, candidateCards) {
  const rk = flopRankKind(requestedCards);
  const ck = flopRankKind(candidateCards);
  const sk = flopSuitKind(requestedCards);
  const cs = flopSuitKind(candidateCards);
  const [r1, r2, r3] = flopRankValuesDesc(requestedCards);
  const [c1, c2, c3] = flopRankValuesDesc(candidateCards);

  // Paired-board specific ordering:
  // 1) suit-count structure match (mono/two-tone/rainbow)
  // 2) paired rank closeness
  // 3) unpaired kicker rank closeness
  // Fallback terms come after these priorities.
  if (rk === 1) {
    const reqPair = pairedFlopInfo(requestedCards);
    const candPair = pairedFlopInfo(candidateCards);
    const pairMissingPenalty = candPair ? 0 : 1;
    const pairRankDist = reqPair && candPair ? Math.abs(reqPair.pairRank - candPair.pairRank) : 99;
    const kickerRankDist = reqPair && candPair ? Math.abs(reqPair.kickerRank - candPair.kickerRank) : 99;
    return [
      sk === cs ? 0 : 1,
      pairMissingPenalty,
      pairRankDist,
      kickerRankDist,
      rk === ck ? 0 : 1,
      Math.abs(r1 - c1),
      Math.abs(r2 - c2),
      Math.abs(r3 - c3),
    ];
  }

  return [
    rk === ck ? 0 : 1,
    sk === cs ? 0 : 1,
    Math.abs(r1 - c1),
    Math.abs(r2 - c2),
    Math.abs(r3 - c3),
  ];
}

function canonicalizeUserFlopTo1755(cards) {
  const sorted = (cards || [])
    .slice()
    .sort((a, b) => {
      const ra = RANK_VALUE[a?.[0]] || 0;
      const rb = RANK_VALUE[b?.[0]] || 0;
      if (rb !== ra) return rb - ra;
      const sa = SUITS.indexOf((a?.[1] || "").toLowerCase());
      const sb = SUITS.indexOf((b?.[1] || "").toLowerCase());
      return sa - sb;
    });

  // 1755 pair isomorphism: for a pair, the two pair-cards are ordered so the
  // kicker either matches the FIRST pair suit (two-tone) or uses a third suit
  // (rainbow). Keys like KhKdJd are not in the map; only KhKdJh / KhKdJc.
  // If the kicker shares the second pair suit, swap the pair cards first so
  // suit-canonicalization yields a valid key (e.g. KhJdKd → KhKdJh).
  if (sorted.length === 3) {
    const ranks = sorted.map((c) => c[0]);
    const uniq = new Set(ranks);
    if (uniq.size === 2) {
      const pairRank = ranks.find((r) => ranks.filter((x) => x === r).length === 2);
      const pairIdx = [];
      let kickerIdx = -1;
      for (let i = 0; i < 3; i++) {
        if (ranks[i] === pairRank) pairIdx.push(i);
        else kickerIdx = i;
      }
      if (pairIdx.length === 2 && kickerIdx >= 0) {
        const kickerSuit = (sorted[kickerIdx][1] || "").toLowerCase();
        const s0 = (sorted[pairIdx[0]][1] || "").toLowerCase();
        const s1 = (sorted[pairIdx[1]][1] || "").toLowerCase();
        if (kickerSuit && kickerSuit === s1 && kickerSuit !== s0) {
          const tmp = sorted[pairIdx[0]];
          sorted[pairIdx[0]] = sorted[pairIdx[1]];
          sorted[pairIdx[1]] = tmp;
        }
      }
    }
  }

  const suitMap = createSuitMapping(sorted);
  const canonicalCards = flopToCanonical(sorted, suitMap);
  return { canonicalCards, canonicalStr: canonicalCards.join("") };
}

// SPEED IMPROVEMENT: memoize flop-map lookups for the session. The canonical
// -> queried mapping is static, so repeat picks of the same flop skip a
// network round trip entirely.
const flopMapMemo = new Map();

async function lookupQueriedFlopFromApi(canonicalFlop1755) {
  const canonical = String(canonicalFlop1755 || "").trim();
  if (!canonical || canonical.length !== 6) return null;
  if (flopMapMemo.has(canonical)) return flopMapMemo.get(canonical);
  try {
    const qs = new URLSearchParams({ canonical });
    const res = await fetch(`${SOLVER_API_URL}/api/v6/flop-map?${qs.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) return null;
    const queried = String(data?.queriedFlop || "").trim();
    if (!queried || queried.length !== 6) return null;
    flopMapMemo.set(canonical, queried);
    return queried;
  } catch (_) {
    return null;
  }
}

/**
 * Create a suit mapping from user-entered flop to canonical form.
 * The first unique suit maps to 'h', second to 'd', third to 'c', fourth to 's'.
 * Returns: { toCanonical: {s: 'h', c: 'd', ...}, fromCanonical: {h: 's', d: 'c', ...} }
 */
function createSuitMapping(flopCards) {
  const userSuits = [];
  // Extract unique suits in order they appear
  for (const card of flopCards) {
    const suit = card[1].toLowerCase();
    if (!userSuits.includes(suit)) userSuits.push(suit);
  }
  
  // Create bidirectional mapping
  const toCanonical = {};
  const fromCanonical = {};
  
  // Map user suits to canonical suits in order
  for (let i = 0; i < userSuits.length; i++) {
    toCanonical[userSuits[i]] = CANONICAL_SUITS[i];
    fromCanonical[CANONICAL_SUITS[i]] = userSuits[i];
  }
  
  // Fill in remaining suits (for suits not on the flop)
  const remainingUserSuits = SUITS.filter(s => !userSuits.includes(s));
  const remainingCanonicalSuits = CANONICAL_SUITS.filter(s => !Object.values(toCanonical).includes(s));
  
  for (let i = 0; i < remainingUserSuits.length; i++) {
    toCanonical[remainingUserSuits[i]] = remainingCanonicalSuits[i];
    fromCanonical[remainingCanonicalSuits[i]] = remainingUserSuits[i];
  }
  
  return { toCanonical, fromCanonical };
}

function createSuitMappingBetweenFlops(desiredFlopCards, queriedFlopCards) {
  // Returns:
  // - toCanonical: desired suit -> queried suit
  // - fromCanonical: queried suit -> desired suit
  const desired = (desiredFlopCards || []).slice(0, 3);
  const queried = (queriedFlopCards || []).slice(0, 3);

  const suitCounts = (cards) => {
    const m = {};
    for (const c of cards) {
      const s = c?.[1]?.toLowerCase();
      if (!s) continue;
      m[s] = (m[s] || 0) + 1;
    }
    return m;
  };

  const maxSuitCount = (cards) => {
    const c = suitCounts(cards);
    const v = Object.values(c);
    return v.length ? Math.max(...v) : 0;
  };

  const sortSuitsByCountDesc = (counts) =>
    Object.entries(counts)
      .sort((a, b) => (b[1] - a[1]) || (SUITS.indexOf(a[0]) - SUITS.indexOf(b[0])))
      .map(([s]) => s);

  const assign = (toCanonical, fromCanonical, dSuit, qSuit) => {
    if (!dSuit || !qSuit) return;
    if (toCanonical[dSuit] && toCanonical[dSuit] !== qSuit) return;
    if (fromCanonical[qSuit] && fromCanonical[qSuit] !== dSuit) return;
    toCanonical[dSuit] = qSuit;
    fromCanonical[qSuit] = dSuit;
  };

  const toCanonical = {};
  const fromCanonical = {};

  const dMax = maxSuitCount(desired);
  const qMax = maxSuitCount(queried);

  // Monotone: map the single suit directly.
  if (dMax === 3 && qMax === 3) {
    assign(toCanonical, fromCanonical, desired[0]?.[1]?.toLowerCase(), queried[0]?.[1]?.toLowerCase());
  }

  // Two-tone: map 2-of-a-suit -> 2-of-a-suit and 1-of-a-suit -> 1-of-a-suit.
  if (dMax === 2 && qMax === 2) {
    const dOrder = sortSuitsByCountDesc(suitCounts(desired));
    const qOrder = sortSuitsByCountDesc(suitCounts(queried));
    assign(toCanonical, fromCanonical, dOrder[0], qOrder[0]); // majority
    assign(toCanonical, fromCanonical, dOrder[1], qOrder[1]); // minority
  }

  // Rainbow: map by rank alignment (highest->highest etc), then suits.
  if (dMax === 1 && qMax === 1) {
    const dSorted = desired
      .slice()
      .sort((a, b) => (RANK_VALUE[b[0]] - RANK_VALUE[a[0]]) || (SUITS.indexOf(a[1]?.toLowerCase()) - SUITS.indexOf(b[1]?.toLowerCase())));
    const qSorted = queried
      .slice()
      .sort((a, b) => (RANK_VALUE[b[0]] - RANK_VALUE[a[0]]) || (SUITS.indexOf(a[1]?.toLowerCase()) - SUITS.indexOf(b[1]?.toLowerCase())));
    for (let i = 0; i < 3; i++) {
      assign(toCanonical, fromCanonical, dSorted[i]?.[1]?.toLowerCase(), qSorted[i]?.[1]?.toLowerCase());
    }
  }

  // Fill any remaining suits deterministically.
  const remainingDesired = SUITS.filter((s) => !toCanonical[s]);
  const remainingQueried = SUITS.filter((s) => !fromCanonical[s]);
  for (let i = 0; i < Math.min(remainingDesired.length, remainingQueried.length); i++) {
    assign(toCanonical, fromCanonical, remainingDesired[i], remainingQueried[i]);
  }

  return { toCanonical, fromCanonical };
}

/**
 * Pair flop ranks by strength: sort both flops' ranks low→high and align (smallest↔smallest, …).
 * Example desired Th8h6c → [6,8,T], queried 9h8h4d → [4,8,9] ⇒ T↔9, 8↔8, 6↔4.
 */
function createFlopRankMappingStrengthPaired(desiredFlopCards, queriedFlopCards) {
  const desired = (desiredFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  const queried = (queriedFlopCards || []).slice(0, 3).filter((c) => c && c.length >= 2);
  if (desired.length !== 3 || queried.length !== 3) return null;

  const dRanks = desired.map((c) => c[0]);
  const qRanks = queried.map((c) => c[0]);
  const byStrength = (a, b) => (RANK_VALUE[a] || 0) - (RANK_VALUE[b] || 0);
  const dSorted = [...dRanks].sort(byStrength);
  const qSorted = [...qRanks].sort(byStrength);

  const desiredToQueried = {};
  for (let i = 0; i < 3; i++) {
    desiredToQueried[dSorted[i]] = qSorted[i];
  }
  const desiredFlopRankSet = new Set(dRanks);
  const queriedFlopRankSet = new Set(qRanks);
  const desiredOnlyRanks = new Set(dRanks.filter((r) => !queriedFlopRankSet.has(r)));
  const queriedOnlyRanks = new Set(qRanks.filter((r) => !desiredFlopRankSet.has(r)));

  return {
    desiredToQueried,
    desiredFlopRankSet,
    queriedFlopRankSet,
    desiredOnlyRanks,
    queriedOnlyRanks,
  };
}

function comboIsStraightOrStraightFlush(boardCards, comboHand) {
  const board = (boardCards || []).filter(Boolean);
  if (!comboHand || comboHand.length < 4 || board.length < 3) return false;
  const cards = [comboHand.slice(0, 2), comboHand.slice(2, 4), ...board];
  const rankSet = new Set(
    cards
      .map((c) => c?.[0])
      .filter(Boolean)
      .map((r) => RANK_VALUE[r] || 0)
      .filter((v) => v > 0)
  );
  return STRAIGHT_WINDOWS.some((w) => w.every((r) => rankSet.has(r)));
}

/**
 * Hard force to step-2 when:
 * - donor-side shifted rank is present (queried-only rank), OR
 * - straight exists on desired board but not queried board (or vice versa) for this combo.
 */
function shouldSkipStepOneDirect(comboHand, flopRankMap, desiredBoardCards, queriedBoardCards, apiComboStr) {
  if (!comboHand || comboHand.length < 4) return false;
  const r1 = comboHand[0];
  const r2 = comboHand[2];
  const donorShifted =
    !!flopRankMap?.queriedOnlyRanks &&
    (flopRankMap.queriedOnlyRanks.has(r1) || flopRankMap.queriedOnlyRanks.has(r2));
  if (donorShifted) return true;

  if (!apiComboStr || !desiredBoardCards?.length || !queriedBoardCards?.length) return false;
  const desiredStraight = comboIsStraightOrStraightFlush(desiredBoardCards, comboHand);
  const queriedStraight = comboIsStraightOrStraightFlush(queriedBoardCards, apiComboStr);
  return desiredStraight !== queriedStraight;
}

/**
 * Convert a card to canonical form using suit mapping
 */
function cardToCanonical(card, suitMapping) {
  if (!card || !suitMapping) return card;
  const rank = card[0];
  const suit = card[1].toLowerCase();
  return rank + suitMapping.toCanonical[suit];
}

const RUNOUT_RANKS = ["2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K", "A"];

/** Rank-texture of a board: unpaired | paired | twopair | trips | fullhouse | quads. */
function boardRankTexture(boardCards) {
  const counts = {};
  for (const c of boardCards || []) {
    if (!c || c.length < 2) continue;
    const r = c[0];
    counts[r] = (counts[r] || 0) + 1;
  }
  const vals = Object.values(counts).sort((a, b) => b - a);
  if (!vals.length) return "unpaired";
  if (vals[0] >= 4) return "quads";
  if (vals[0] === 3 && (vals[1] || 0) >= 2) return "fullhouse";
  if (vals[0] === 3) return "trips";
  if (vals[0] === 2 && (vals[1] || 0) === 2) return "twopair";
  if (vals[0] === 2) return "paired";
  return "unpaired";
}

function ranksByCloseness(centerRank) {
  const cv = RANK_VALUE[centerRank] || 0;
  return RUNOUT_RANKS
    .filter((r) => r !== centerRank)
    .sort((a, b) => {
      const da = Math.abs((RANK_VALUE[a] || 0) - cv);
      const db = Math.abs((RANK_VALUE[b] || 0) - cv);
      if (da !== db) return da - db;
      return (RANK_VALUE[b] || 0) - (RANK_VALUE[a] || 0);
    });
}

/**
 * Turn/river: map user card into queried DB space.
 * - Keep user rank unless it already appears on the desired board (pair/trips/FH texture);
 *   then remap via flop strength-pair map when the paired rank is a flop rank.
 * - Always apply flop suit isomorphism.
 * - Accept only if queried board rank-texture matches desired; otherwise search closest ranks.
 */
function resolveRunoutCanonicalCard(userCard, desiredBoardCards, queriedBoardCards, suitMapping, flopRankMap = null) {
  if (!userCard || userCard.length < 2) return userCard;

  const desiredBefore = (desiredBoardCards || []).filter(Boolean);
  const queriedBefore = (queriedBoardCards || []).filter(Boolean);
  const desiredRanksBefore = new Set(desiredBefore.map((c) => c[0]));
  const userRank = userCard[0];
  const userSuit = userCard[1];

  const desiredTexture = boardRankTexture([...desiredBefore, userCard]);

  // Primary rank: user rank, unless this card pairs/trips something already on the desired board.
  let primaryRank = userRank;
  if (desiredRanksBefore.has(userRank)) {
    const mapped = flopRankMap?.desiredToQueried?.[userRank];
    // Flop-rank pair → use mapped queried flop rank (e.g. T→9). Off-flop pairs (turn 2 / river 2) keep rank.
    if (mapped && flopRankMap?.desiredFlopRankSet?.has(userRank)) {
      primaryRank = mapped;
    }
  }

  const toQueriedCard = (rank) => cardToCanonical(`${rank}${userSuit}`, suitMapping);

  const isValidQueriedCard = (queriedCard) => {
    if (!queriedCard || queriedCard.length < 2) return false;
    if (queriedBefore.includes(queriedCard)) return false; // exact card already used
    const queriedTexture = boardRankTexture([...queriedBefore, queriedCard]);
    return queriedTexture === desiredTexture;
  };

  const primaryCanonical = toQueriedCard(primaryRank);
  if (isValidQueriedCard(primaryCanonical)) return primaryCanonical;

  // Texture mismatch / collision: try closest ranks by value (2→A ladder).
  for (const rank of ranksByCloseness(primaryRank)) {
    const candidate = toQueriedCard(rank);
    if (isValidQueriedCard(candidate)) return candidate;
  }

  return primaryCanonical;
}

/**
 * Convert flop array to canonical form
 */
function flopToCanonical(flopCards, suitMapping) {
  return flopCards.map(card => cardToCanonical(card, suitMapping));
}

/**
 * Find the canonical flop that matches this user-entered flop
 * Returns the canonical flop string and the suit mapping
 */
function findCanonicalFlop(userFlop, availableFlops) {
  // Create mapping based on user's flop
  const suitMapping = createSuitMapping(userFlop);
  const canonicalFlop = flopToCanonical(userFlop, suitMapping);
  const canonicalFlopStr = canonicalFlop.join('');
  
  // Check if this canonical flop exists in available flops
  if (availableFlops && availableFlops.includes(canonicalFlopStr)) {
    return { canonicalFlop: canonicalFlopStr, suitMapping };
  }
  
  // If not found, the user's flop might already be canonical
  const userFlopStr = userFlop.join('');
  if (availableFlops && availableFlops.includes(userFlopStr)) {
    // Create identity mapping
    const identityMapping = { toCanonical: {}, fromCanonical: {} };
    SUITS.forEach(s => { identityMapping.toCanonical[s] = s; identityMapping.fromCanonical[s] = s; });
    return { canonicalFlop: userFlopStr, suitMapping: identityMapping };
  }
  
  // Return canonical form anyway (API will handle if not found)
  return { canonicalFlop: canonicalFlopStr, suitMapping };
}

/**
 * Desired enumeration combo → raw API `combos[].hand` key (queried board).
 * Ranks that appear on the desired flop are swapped per createFlopRankMappingStrengthPaired, then suit isomorphism.
 */
function comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const rankMap = flopRankMap?.desiredToQueried;
  const onFlop = flopRankMap?.desiredFlopRankSet;

  const remapCard = (card) => {
    const r0 = card[0];
    const s = card[1].toLowerCase();
    const r =
      onFlop?.has(r0) && rankMap && rankMap[r0] !== undefined ? rankMap[r0] : r0;
    const mapped = `${r}${s}`;
    return suitMapping ? cardToCanonical(mapped, suitMapping) : mapped;
  };

  const q1 = remapCard(comboHand.slice(0, 2));
  const q2 = remapCard(comboHand.slice(2, 4));
  return canonicalizeComboHand(q1 + q2);
}

/**
 * Raw API grid: each `combos[].hand` lives under its **category** cell (e.g. Js9s under J9s).
 * Mapped keys (e.g. api Js9s while viewing JTs) are not in `displayCell.combos`; scan the full 13×13 grid.
 */
function findComboDataInGrid(gridData, apiHandStr) {
  if (!gridData || !apiHandStr) return null;
  for (const row of gridData) {
    for (const cell of row || []) {
      const hit = cell?.combos?.find((c) => c.hand === apiHandStr);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Resolve the strategy data for one desired-space combo exactly the way the Combos view does:
 * step-1 direct API lookup (suit/rank remap), then texture-resolution donor fallback.
 * Shared by ComboBreakdown and the hand randomizer so both always agree.
 */
function resolvePostflopComboData(comboHand, { gridData, cell, suitMapping, flopRankMap, desiredBoardCards, queriedBoardCards, textureResolutionMap }) {
  const apiComboStr = comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap);
  const primary =
    findComboDataInGrid(gridData, apiComboStr) ||
    (cell?.combos || []).find((c) => c.hand === apiComboStr) ||
    null;
  const forceStepTwo = shouldSkipStepOneDirect(comboHand, flopRankMap, desiredBoardCards, queriedBoardCards, apiComboStr);
  if (!forceStepTwo && isPrimaryApiComboUsable(apiComboStr, primary, comboHand)) {
    return { comboData: primary, kind: primary?.frequencies?.length ? "direct" : "none", donorHand: null, apiComboStr };
  }
  if (textureResolutionMap?.[comboHand]?.fallbackData?.frequencies?.some((f) => f > 0)) {
    return {
      comboData: textureResolutionMap[comboHand].fallbackData,
      kind: "texture",
      donorHand: textureResolutionMap[comboHand]?.donorHand || null,
      apiComboStr,
    };
  }
  return { comboData: null, kind: "none", donorHand: null, apiComboStr };
}

/** API `combos[].hand` used as step-1 mapped donor for some other desired combo; blank in matrix only (raw grid keeps data for resolution). */
function collectStepOneDonorHands(rawGridData, suitMapping, flopRankMap, desiredBoardCards, queriedBoardCards) {
  const donors = new Set();
  if (!rawGridData) return donors;
  for (const row of rawGridData) {
    for (const cell of row || []) {
      const label = cell?.label;
      if (!label) continue;
      for (const h of generateAllCombos(label)) {
        const qh = comboDesiredToApiLookup(h, suitMapping, flopRankMap);
        if (shouldSkipStepOneDirect(h, flopRankMap, desiredBoardCards, queriedBoardCards, qh)) continue;
        if (qh === h) continue;
        const data = findComboDataInGrid(rawGridData, qh);
        if (isPrimaryApiComboUsable(qh, data, h)) donors.add(qh);
      }
    }
  }
  return donors;
}

function applyStepOneDonorBlanking(gridData, donorSet) {
  if (!gridData || !donorSet?.size) return gridData;
  return gridData.map((row) =>
    row.map((cell) => {
      const combos = (cell.combos || []).map((c) => {
        if (!donorSet.has(c.hand)) return c;
        const ac = c.frequencies?.length || 0;
        return { ...c, frequencies: ac ? Array(ac).fill(0) : [], ev: 0 };
      });
      const ac = combos[0]?.frequencies?.length || cell.frequencies?.length || 0;
      if (!ac || !combos.length) return { ...cell, combos };
      const avg = Array(ac).fill(0);
      for (const c of combos) {
        for (let i = 0; i < ac; i++) avg[i] += c.frequencies[i] || 0;
      }
      for (let i = 0; i < ac; i++) avg[i] /= combos.length;
      return { ...cell, combos, frequencies: avg };
    })
  );
}

/** `{}` is truthy but has no weights; must not create an empty Set (that marks every combo "out of range"). */
function rangeMapWithPositiveWeights(rangeMap) {
  if (!rangeMap || typeof rangeMap !== "object") return null;
  const hasAny = Object.values(rangeMap).some((w) => Number(w) > 0);
  return hasAny ? rangeMap : null;
}

/** Range carry keys = enumeration combos; values resolved against raw API `combos` in queried suit+rank space. */
function buildCarryRangeFromRawGrid(rawGridData, suitMapping, flopRankMap, actions, broughtInRange, opts) {
  const actionRanges = {};
  (actions || []).forEach((a) => {
    actionRanges[a] = {};
  });
  const baseResolvedRange = {};
  if (!rawGridData) return { actionRanges, baseResolvedRange };

  const { desiredBoard, queriedBoard, textureResolutionMap } = opts || {};

  const effectiveRange = rangeMapWithPositiveWeights(broughtInRange);
  const activeComboSet = effectiveRange
    ? new Set(Object.entries(effectiveRange).filter(([, w]) => (w || 0) > 0).map(([h]) => h))
    : null;

  const actionCount = actions?.length || 0;

  for (const row of rawGridData) {
    for (const cell of row) {
      const label = cell?.label;
      if (!label) continue;
      for (const h of generateAllCombos(label)) {
        if (activeComboSet && !activeComboSet.has(h)) continue;
        const w = effectiveRange ? (effectiveRange[h] || 0) : 1;
        if (w <= 0) continue;
        const qh = comboDesiredToApiLookup(h, suitMapping, flopRankMap);
        const forceStepTwo = shouldSkipStepOneDirect(h, flopRankMap, desiredBoard, queriedBoard, qh);
        let comboData =
          (cell.combos || []).find((c) => c.hand === qh) || findComboDataInGrid(rawGridData, qh);
        if (forceStepTwo || !isPrimaryApiComboUsable(qh, comboData, h)) {
          const resolved = textureResolutionMap?.[h];
          comboData =
            resolved?.fallbackData && resolved.fallbackData.frequencies?.some((f) => (f || 0) > 0)
              ? resolved.fallbackData
              : null;
        }
        if (!comboData?.frequencies) continue;
        baseResolvedRange[h] = w;
        for (let i = 0; i < actionCount; i++) {
          const actName = actions[i];
          if (!actName) continue;
          const f = comboData.frequencies[i] || 0;
          actionRanges[actName][h] = (actionRanges[actName][h] || 0) + w * f;
        }
      }
    }
  }
  return { actionRanges, baseResolvedRange };
}

/**
 * Hand-matrix color model from resolved combo strategies:
 * - Combo box share at node = brought-in combo weight
 * - Action contribution = combo share * combo action frequency
 * - Cell colors = normalized action contributions across active combos in cell
 */
function buildPostflopHandMatrixGrid(rawGridData, actions, opts) {
  if (!rawGridData) return rawGridData;
  const {
    activeRange,
    usedCards,
    suitMapping,
    flopRankMap,
    desiredBoardCards,
    queriedBoardCards,
    textureResolutionMap,
  } = opts || {};

  const actionCount = actions?.length || 0;
  const effectiveRange = rangeMapWithPositiveWeights(activeRange);
  const activeComboSet = effectiveRange
    ? new Set(Object.entries(effectiveRange).filter(([, w]) => (w || 0) > 0).map(([h]) => h))
    : null;
  const usedSet = new Set((usedCards || []).filter(Boolean));
  return rawGridData.map((row) =>
    row.map((cell) => {
      const label = cell?.label;
      if (!label) return cell;

      const actionMass = new Array(actionCount).fill(0);
      let totalMass = 0;
      let evMass = 0;

      for (const comboHand of generateAllCombos(label)) {
        const card1 = comboHand.slice(0, 2);
        const card2 = comboHand.slice(2, 4);
        if (usedSet.has(card1) || usedSet.has(card2)) continue;
        if (activeComboSet && !activeComboSet.has(comboHand)) continue;

        const comboMass = effectiveRange ? Number(effectiveRange[comboHand] || 0) : 1;
        if (!(comboMass > 0)) continue;

        const apiComboStr = comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap);
        const forceStepTwo = shouldSkipStepOneDirect(comboHand, flopRankMap);
        const primary =
          findComboDataInGrid(rawGridData, apiComboStr) ||
          (cell?.combos || []).find((c) => c.hand === apiComboStr) ||
          null;

        let comboData = null;
        let resolvedKind = "none";
        if (!forceStepTwo && isPrimaryApiComboUsable(apiComboStr, primary, comboHand)) {
          comboData = primary;
          resolvedKind = "direct";
        } else if (textureResolutionMap?.[comboHand]?.fallbackData?.frequencies?.length) {
          comboData = textureResolutionMap[comboHand].fallbackData;
          if (comboData?.frequencies?.length) {
            resolvedKind = "texture";
          }
        }
        if (!comboData?.frequencies?.length) continue;

        let freqs = normalizeFrequenciesForDisplay(comboData.frequencies, actionCount);
        if (resolvedKind === "texture") {
          const sum = freqs.reduce((a, b) => a + (b || 0), 0);
          if (sum <= 0 && actionCount > 0) {
            const foldIdx = (actions || []).findIndex((a) =>
              String(a || "").toLowerCase().includes("fold")
            );
            freqs = new Array(actionCount).fill(0);
            if (foldIdx >= 0) freqs[foldIdx] = 1;
            else freqs[0] = 1;
          }
        }

        totalMass += comboMass;
        evMass += comboMass * Number(comboData?.ev || 0);
        for (let i = 0; i < actionCount; i++) {
          actionMass[i] += comboMass * (freqs[i] || 0);
        }
      }

      const normalizedCellFreqs =
        totalMass > 0 && actionCount > 0
          ? actionMass.map((m) => m / totalMass)
          : new Array(actionCount).fill(0);

      return {
        ...cell,
        inRange: totalMass > 0,
        frequencies: normalizedCellFreqs,
        ev: totalMass > 0 ? evMass / totalMass : Number(cell?.ev || 0),
      };
    })
  );
}

/**
 * Ensure all 2-card combo strings match our frontend's canonical ordering.
 *
 * Why: suit-isomorphism mapping can swap suits not on the flop (e.g. s<->c),
 * which can turn a pair combo like "AsAc" into "AcAs". Our UI generates pair
 * combos using i<j suit ordering, so we must canonicalize to match or we’ll
 * show only 1/6 pair combos and blank out the wrong one.
 *
 * Canonical rules (matching `generateAllCombos`):
 * - Highest rank card first (A..2)
 * - For pairs, suit order follows `SUITS` (["s","h","d","c"]) with i<j
 */
function canonicalizeComboHand(comboHand) {
  if (!comboHand || comboHand.length < 4) return comboHand;
  const r1 = comboHand[0], s1 = comboHand[1].toLowerCase();
  const r2 = comboHand[2], s2 = comboHand[3].toLowerCase();

  const rankOrder = (r) => RANKS.indexOf(r);
  const suitOrder = (s) => SUITS.indexOf(s);

  const a = { r: r1, s: s1 };
  const b = { r: r2, s: s2 };

  // Put higher rank first (lower index in RANKS array).
  if (rankOrder(b.r) !== -1 && rankOrder(a.r) !== -1 && rankOrder(b.r) < rankOrder(a.r)) {
    return `${b.r}${b.s}${a.r}${a.s}`;
  }

  // For pairs, enforce suit i<j ordering so we match `generateAllCombos`.
  if (a.r === b.r) {
    const sa = suitOrder(a.s), sb = suitOrder(b.s);
    if (sa !== -1 && sb !== -1 && sa > sb) {
      return `${b.r}${b.s}${a.r}${a.s}`;
    }
  }

  return `${a.r}${a.s}${b.r}${b.s}`;
}

// Generate all combos for a hand type
function generateAllCombos(label) {
  const combos = [];
  if (label.length === 2) {
    const rank = label[0];
    for (let i = 0; i < SUITS.length; i++) {
      for (let j = i + 1; j < SUITS.length; j++) {
        combos.push(`${rank}${SUITS[i]}${rank}${SUITS[j]}`);
      }
    }
  } else if (label.endsWith("s")) {
    const r1 = label[0], r2 = label[1];
    for (const suit of SUITS) combos.push(`${r1}${suit}${r2}${suit}`);
  } else if (label.endsWith("o")) {
    const r1 = label[0], r2 = label[1];
    for (let i = 0; i < SUITS.length; i++) {
      for (let j = 0; j < SUITS.length; j++) {
        if (i !== j) combos.push(`${r1}${SUITS[i]}${r2}${SUITS[j]}`);
      }
    }
  }
  return combos;
}

// ============================================================================
// Preflop Logic
// ============================================================================

const OPEN_SIZE = 2.5, THREEB_SIZE = 7.5, THREEB_SIZE_BLINDS = 10, FOURB_SIZE = 22;

function isBlindPosition(posId) { return posId === "sb" || posId === "bb"; }

function getRaiseSize(posId, raiseCount, context = {}) {
  // Special-case: SB opens to 3bb.
  if (raiseCount === 0 && posId === "sb") return 3;
  if (raiseCount === 0) return OPEN_SIZE;
  // Blind 3-bets (SB and BB) are always 10bb.
  if (raiseCount === 1) return isBlindPosition(posId) ? THREEB_SIZE_BLINDS : THREEB_SIZE;
  if (raiseCount === 2) return FOURB_SIZE;
  return FOURB_SIZE * 2;
}

function getRaiseName(raiseCount) {
  if (raiseCount === 0) return "open";
  if (raiseCount === 1) return "3bet";
  if (raiseCount === 2) return "4bet";
  return "5bet";
}

function getRaiseLabel(posId, raiseCount, context = {}) {
  const size = getRaiseSize(posId, raiseCount, context);
  if (raiseCount === 0) return `Bet ${size}`;
  if (raiseCount === 1) return `3-Bet ${size}`;
  if (raiseCount === 2) return `4-Bet ${size}`;
  return `5-Bet`;
}

/**
 * Preflop betting round: each non-folded player must act until all contributions match
 * currentStake and everyone has acted since the last raise. Blinds = partial contributions.
 */
function processPreflop(actionHistory) {
  const positionStates = {};
  POSITIONS.forEach((p) => {
    positionStates[p.id] = {
      folded: false,
      contrib: 0,
      actedThisRound: false,
      lastAction: null,
      hasRaised: false,
    };
  });
  positionStates.sb.contrib = 0.5;
  positionStates.bb.contrib = 1;

  let currentStake = 1;
  let raiseCount = 0;
  let lastRaiser = null;

  const decisionBoxes = [];
  POSITIONS.forEach((pos) => {
    decisionBoxes.push({
      position: pos.id,
      positionName: pos.name,
      action: null,
      isInitialPosition: true,
      boxIndex: decisionBoxes.length,
    });
  });

  const isAggressive = (a) => ["open", "3bet", "4bet", "5bet", "raise", "allin"].includes(a);

  for (const action of actionHistory) {
    const state = positionStates[action.position];
    const boxIndex = decisionBoxes.findIndex((b) => b.position === action.position && b.action === null);
    if (boxIndex >= 0) {
      decisionBoxes[boxIndex].action = action.action;
      decisionBoxes[boxIndex].raiseSize = action.raiseSize;
    }

    state.lastAction = action.action;

    if (action.action === "fold") {
      state.folded = true;
      state.actedThisRound = true;
    } else if (action.action === "call") {
      state.contrib = currentStake;
      state.actedThisRound = true;
    } else if (action.action === "check") {
      state.actedThisRound = true;
    } else if (isAggressive(action.action)) {
      const raiseSize = action.raiseSize || getRaiseSize(action.position, raiseCount, { lastRaiser });
      state.contrib = raiseSize;
      currentStake = raiseSize;
      state.hasRaised = true;
      lastRaiser = action.position;
      raiseCount++;
      state.actedThisRound = true;
      POSITIONS.forEach((p) => {
        if (p.id === action.position) return;
        if (positionStates[p.id].folded) return;
        positionStates[p.id].actedThisRound = false;
        const existingBox = decisionBoxes.find((b) => b.position === p.id && b.action === null);
        if (!existingBox && positionStates[p.id].lastAction && positionStates[p.id].lastAction !== "fold") {
          decisionBoxes.push({
            position: p.id,
            positionName: POSITIONS.find((pos) => pos.id === p.id).name,
            action: null,
            isInitialPosition: false,
            boxIndex: decisionBoxes.length,
            facingRaiseFrom: lastRaiser,
          });
        }
      });
    }
  }

  const activePlayers = POSITIONS.filter((p) => !positionStates[p.id].folded);
  if (activePlayers.length <= 1) {
    return { position: null, isComplete: true, decisionBoxes, currentBet: currentStake, raiseCount };
  }

  const needsToAct = (posId) => {
    const s = positionStates[posId];
    if (s.folded) return false;
    if (s.contrib < currentStake) return true;
    if (!s.actedThisRound) return true;
    return false;
  };

  // Choose next actor in table order starting immediately after the last action.
  // Example fixed: BTN open, SB 3bet -> next is BB (not BTN).
  const lastActionPos = actionHistory?.length ? actionHistory[actionHistory.length - 1]?.position : null;
  const startIdxRaw = lastActionPos ? PREFLOP_ACT_ORDER.indexOf(lastActionPos) : -1;
  const startIdx = startIdxRaw >= 0 ? startIdxRaw : PREFLOP_ACT_ORDER.indexOf("bb");
  const orderedToCheck = [
    ...PREFLOP_ACT_ORDER.slice(startIdx + 1),
    ...PREFLOP_ACT_ORDER.slice(0, startIdx + 1),
  ];

  for (const posId of orderedToCheck) {
    if (!needsToAct(posId)) continue;
    const s = positionStates[posId];
    const existingBox = decisionBoxes.find((b) => b.position === posId && b.action === null);
    if (!existingBox) {
      decisionBoxes.push({
        position: posId,
        positionName: POSITIONS.find((p) => p.id === posId).name,
        action: null,
        isInitialPosition: false,
        boxIndex: decisionBoxes.length,
        facingRaiseFrom: lastRaiser,
      });
    }
    const short = s.contrib < currentStake;
    const isPreOpen = raiseCount === 0;
    return {
      position: posId,
      canFold: true,
      canCall: short || (!short && !s.actedThisRound),
      canRaise: true,
      canAllin: true,
      isOpen: isPreOpen,
      raiseLabel: getRaiseLabel(posId, raiseCount, { lastRaiser }),
      raiseName: getRaiseName(raiseCount),
      currentBet: currentStake,
      raiseCount,
      lastRaiser,
      isComplete: false,
      decisionBoxes,
    };
  }

  return { position: null, isComplete: true, decisionBoxes, currentBet: currentStake, raiseCount, lastRaiser };
}

/** Preflop street commitment per seat in bb (blinds start at 0.5 / 1). */
function getPreflopCommittedBySeat(actionHistory) {
  const committed = { lj: 0, hj: 0, co: 0, btn: 0, sb: 0.5, bb: 1 };
  let currentStake = 1;
  let raiseCount = 0;
  let lastRaiser = null;
  const isAggressive = (a) => ["open", "3bet", "4bet", "5bet", "raise", "allin"].includes(a);
  for (const action of actionHistory || []) {
    const pos = action?.position;
    if (!pos || !(pos in committed)) continue;
    if (action.action === "fold" || action.action === "check") continue;
    if (action.action === "call") {
      committed[pos] = currentStake;
    } else if (isAggressive(action.action)) {
      const raiseSize = action.raiseSize || getRaiseSize(pos, raiseCount, { lastRaiser });
      committed[pos] = raiseSize;
      currentStake = raiseSize;
      lastRaiser = pos;
      raiseCount += 1;
    }
  }
  return committed;
}

/** Postflop street commitment per seat in half-bb units (same as action labels / pot math). */
function getPostflopStreetCommittedHalfBb(actionHistory, oopSeatId, ipSeatId) {
  const contrib = { 0: 0, 1: 0 };
  let stake = 0;
  const isAggressiveLabel = (a) => {
    const s = (a || "").toLowerCase();
    return s.includes("bet") || s.includes("raise") || s.includes("all-in") || s.includes("allin") || s.includes("all in");
  };
  const isCallLabel = (a) => {
    const s = String(a || "").toLowerCase().trim();
    return s === "c" || s.includes("call");
  };
  for (const entry of actionHistory || []) {
    const p = entry?.player;
    if (p !== 0 && p !== 1) continue;
    const raw = String(entry?.action || "");
    if (isCallLabel(raw)) {
      contrib[p] = stake;
      continue;
    }
    if (isAggressiveLabel(raw)) {
      const size = extractFacingAmountFromActionLabel(raw);
      if (Number.isFinite(size) && size >= 0) {
        contrib[p] = size;
        stake = size;
      }
    }
  }
  const out = {};
  if (oopSeatId && contrib[0] > 0) out[oopSeatId] = contrib[0];
  if (ipSeatId && contrib[1] > 0) out[ipSeatId] = contrib[1];
  return out;
}

function actionHistoryToSpotKey(actionHistory) {
  const significantActions = actionHistory.filter(a => a.action !== "fold");
  return significantActions.map(a => `${a.position}:${a.action}`).join(",");
}

function getPreflopVoluntaryParticipants(actionHistory) {
  const participants = new Set();
  for (const action of actionHistory || []) {
    const pos = String(action?.position || "").toLowerCase();
    const act = String(action?.action || "").toLowerCase();
    if (!pos) continue;
    if (act === "fold") continue;
    participants.add(pos);
  }
  return participants;
}

function getActionDisplayName(action, raiseSize) {
  if (action === "open") return `Bet ${raiseSize || OPEN_SIZE}`;
  if (action === "3bet") return `3-Bet ${raiseSize || THREEB_SIZE}`;
  if (action === "4bet") return `4-Bet ${raiseSize || FOURB_SIZE}`;
  if (action === "5bet") return "5-Bet";
  if (action === "raise") return "Raise";
  if (action === "allin") return "All in";
  if (action === "call") return "Call";
  if (action === "fold") return "Fold";
  if (action === "check") return "Check";
  return action;
}

function formatPostflopHalfBb(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const halved = n / 2;
  if (Math.abs(halved - Math.round(halved)) < 1e-9) return String(Math.round(halved));
  return halved.toFixed(1).replace(/\.0$/, "");
}

function formatPostflopActionLabel(actionLabel, isPreflop = false) {
  const action = String(actionLabel || "");
  if (isPreflop || !action) return action;
  const lower = action.toLowerCase();
  // Keep all-in labels in their native stack-based sizing.
  if (lower.includes("all-in") || lower.includes("allin") || lower.includes("all in")) return "All in";
  return action.replace(/\d+(?:\.\d+)?/g, (m) => formatPostflopHalfBb(m));
}

// ============================================================================
// Helper Functions
// ============================================================================

function getActionColor(actionName, index, totalActions, opts = {}) {
  const lower = actionName?.toLowerCase() || "";
  const isPreflop = !!opts?.isPreflop;
  const preflopFoldRed = !!opts?.preflopFoldRed;
  const actionList = opts?.actionList || [];

  // Passive actions
  if (lower.includes("check") || lower.includes("call")) return SOLVER_PREFS.checkCall; // green by default
  // Preflop: folds for yet-to-act decisions should show no fill color.
  if (lower.includes("fold")) return (isPreflop && !preflopFoldRed) ? "transparent" : SOLVER_PREFS.fold; // red except first preflop action for seat

  // Aggressive actions: colour by bet size rank (smallest→bet1/blue, mid→bet2/purple, largest→bet3/pink),
  // matching the grid/legend. Display order must not affect colour.
  if (lower.includes("raise") || lower.includes("bet") || lower.includes("all-in") || lower.includes("allin") || lower.includes("all in") || lower.includes("open")) {
    const isAllInLabel = (a) => a.includes("all-in") || a.includes("allin") || a.includes("all in");
    const isAggrLabel = (a) =>
      a.includes("raise") || a.includes("bet") || isAllInLabel(a) || a.includes("open");
    const aggr = (actionList || [])
      .map((a, i) => {
        const al = String(a || "").toLowerCase();
        return {
          i,
          amount: isAllInLabel(al) ? Number.POSITIVE_INFINITY : (extractFacingAmountFromActionLabel(a) || 0),
          al,
        };
      })
      .filter(({ al }) => isAggrLabel(al));
    aggr.sort((x, y) => (x.amount - y.amount) || (x.i - y.i));
    let rank = aggr.findIndex((x) => x.i === index);
    // Fallback when caller passes a display-only index / truncated list: match by size among list.
    if (rank < 0) {
      const selfAmount = isAllInLabel(lower) ? Number.POSITIVE_INFINITY : (extractFacingAmountFromActionLabel(actionName) || 0);
      rank = aggr.findIndex((x) => x.amount === selfAmount);
    }
    if (rank < 0) rank = Math.max(0, Math.min(index, Math.max(0, aggr.length - 1)));
    if (rank <= 0) return SOLVER_PREFS.bet1;
    if (rank === 1) return SOLVER_PREFS.bet2;
    return SOLVER_PREFS.bet3;
  }

  return SOLVER_PREFS.bet1;
}

function actionHasAnyWeight(grid, actionIndex) {
  if (!grid || actionIndex < 0) return false;
  for (const row of grid) {
    for (const cell of row || []) {
      const v = Number(cell?.frequencies?.[actionIndex] ?? 0);
      if (Number.isFinite(v) && v > 0) return true;
    }
  }
  return false;
}

function buildSelectorActions(displayActions, displayGrid) {
  const actions = displayActions || [];
  return actions.filter((_, idx) => actionHasAnyWeight(displayGrid, idx));
}

function normalizeFrequenciesForDisplay(frequencies, totalActions) {
  const len = Math.max(0, Number(totalActions || 0));
  if (!len) return [];
  const out = new Array(len).fill(0);
  for (let i = 0; i < len; i++) {
    const f = Number(frequencies?.[i] ?? 0);
    out[i] = Number.isFinite(f) ? Math.max(0, f) : 0;
  }
  const sum = out.reduce((a, b) => a + b, 0);
  if (sum <= 0) return out;
  if (sum > 1) return out.map((f) => f / sum);
  return out;
}

function getRenderIndexOrder(actions, totalActions, options = {}) {
  const { isPreflop = false } = options;
  const len = Math.max(0, Number(totalActions || 0));
  const order = Array.from({ length: len }, (_, i) => i);
  const actionMeta = order.map((i) => {
    const label = String(actions?.[i] || "");
    const lower = label.toLowerCase();
    if (lower.includes("check")) return { i, bucket: isPreflop ? 0 : 2, amount: 0 };
    if (
      lower.includes("bet") ||
      lower.includes("raise") ||
      lower.includes("all-in") ||
      lower.includes("allin") ||
      lower.includes("all in") ||
      lower.includes("open")
    ) {
      return {
        i,
        bucket: isPreflop ? 1 : 0,
        amount: extractFacingAmountFromActionLabel(label) || 0,
      };
    }
    if (lower.includes("call")) return { i, bucket: isPreflop ? 2 : 1, amount: 0 };
    if (lower.includes("fold")) return { i, bucket: 3, amount: 0 };
    return { i, bucket: isPreflop ? 2 : 1, amount: 0 };
  });
  actionMeta.sort((a, b) => {
    if (a.bucket !== b.bucket) return a.bucket - b.bucket;
    if (!isPreflop && a.bucket === 0) return b.amount - a.amount || a.i - b.i; // Postflop: largest bet first.
    return a.amount - b.amount || a.i - b.i;
  });
  return actionMeta.map((x) => x.i);
}

function actionToPathSegment(action) {
  const lower = action.toLowerCase();
  if (lower === "check") return "x";
  if (lower === "call") return "c";
  if (lower === "fold") return "f";
  const betMatch = action.match(/bet\s*(\d+)/i);
  if (betMatch) return `b${betMatch[1]}`;
  const raiseMatch = action.match(/raise\s*(\d+)/i);
  if (raiseMatch) return `r${raiseMatch[1]}`;
  if (lower.includes("all-in") || lower.includes("allin") || lower.includes("all in")) {
    const allinMatch = action.match(/(\d+)/);
    if (allinMatch) return `a${allinMatch[1]}`;
    return "a";
  }
  return lower.replace(/\s+/g, "");
}

function extractFacingAmountFromActionLabel(actionStr) {
  const nums = String(actionStr || "").match(/\d+(?:\.\d+)?/g);
  if (!nums?.length) return 0;
  return parseFloat(nums[nums.length - 1], 10) || 0;
}

/**
 * Heads-up postflop street: replay history with stake / contrib / actedThisRound (same rules as preflop).
 * Returns true when both players are still in the hand and the betting round is closed.
 */
function needsStreetTransition(actionHistory) {
  if (!actionHistory?.length) return false;

  const folded = { 0: false, 1: false };
  const contrib = { 0: 0, 1: 0 };
  let acted = { 0: false, 1: false };
  let stake = 0;

  const isAggressiveLabel = (a) => {
    const s = (a || "").toLowerCase();
    return s.includes("bet") || s.includes("raise") || s.includes("all-in") || s.includes("allin") || s.includes("all in");
  };

  for (const entry of actionHistory) {
    const p = entry.player;
    if (p !== 0 && p !== 1) continue;
    const raw = entry.action || "";
    const a = raw.toLowerCase();

    if (a.includes("fold")) {
      folded[p] = true;
      acted[p] = true;
    } else if (a.includes("check")) {
      acted[p] = true;
    } else if (a.includes("call")) {
      contrib[p] = stake;
      acted[p] = true;
    } else if (isAggressiveLabel(raw)) {
      const size = extractFacingAmountFromActionLabel(raw);
      contrib[p] = size;
      stake = size;
      acted[p] = true;
      const other = p === 0 ? 1 : 0;
      if (!folded[other]) acted[other] = false;
    }
  }

  const active = [0, 1].filter((pl) => !folded[pl]);
  if (active.length < 2) return false;

  for (const pl of active) {
    if (contrib[pl] < stake) return false;
    if (!acted[pl]) return false;
  }
  return true;
}

function computeStreetEndPot(startPot, actionHistory) {
  const basePot = Number(startPot);
  if (!Number.isFinite(basePot)) return null;
  if (!Array.isArray(actionHistory) || actionHistory.length === 0) return basePot;

  const contrib = { 0: 0, 1: 0 };
  let stake = 0;

  const isAggressiveLabel = (a) => {
    const s = (a || "").toLowerCase();
    return s.includes("bet") || s.includes("raise") || s.includes("all-in") || s.includes("allin") || s.includes("all in");
  };
  const isCallLabel = (a) => {
    const s = String(a || "").toLowerCase().trim();
    return s === "c" || s.includes("call");
  };

  for (const entry of actionHistory) {
    const p = entry?.player;
    if (p !== 0 && p !== 1) continue;
    const raw = String(entry?.action || "");
    const a = raw.toLowerCase();
    if (isCallLabel(raw)) {
      // Calls should always match the latest stake, so pot captures both bet and call.
      contrib[p] = stake;
      continue;
    }
    if (isAggressiveLabel(raw)) {
      const size = extractFacingAmountFromActionLabel(raw);
      if (Number.isFinite(size) && size >= 0) {
        contrib[p] = size;
        stake = size;
      }
    }
  }

  return basePot + contrib[0] + contrib[1];
}


// ============================================================================
// Components
// ============================================================================

const Header = () => (
  <Box
    sx={{
      width: "100%",
      py: 1.5,
      px: 2,
      pl: { xs: 1.2, sm: 2.5 },
      pr: { xs: 14, sm: 17 },
      borderBottom: `1px solid ${COLORS.border}`,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: { xs: 0.6, sm: 2 },
      minWidth: 0,
    }}
  >
    <a href="/" style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      <img src={logoWithText} alt="PokerMoose" style={{ height: 32, width: 32, opacity: 0.5, filter: "grayscale(1)", cursor: "pointer" }} />
    </a>
    <Typography
      component="a"
      href="/"
      sx={{
        display: { xs: "none", sm: "inline" },
        color: COLORS.white,
        fontSize: "13px",
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontFamily: "Play, Arial, sans-serif",
        lineHeight: 1,
        mr: 0.4,
        userSelect: "none",
        textDecoration: "none",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      PokerMoose
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.35, sm: 1.2 },
        userSelect: "none",
        flexWrap: "nowrap",
        minWidth: 0,
        flex: 1,
        overflowX: "auto",
        ...THIN_SCROLLBAR_SX,
      }}
    >
      <Button
        component="a"
        href="/"
        size="small"
        variant="text"
        sx={{
          minWidth: "unset",
          flexShrink: 0,
          px: { xs: 1.1, sm: 2.1 },
          py: { xs: 0.45, sm: 0.6 },
          borderRadius: "999px",
          border: "none",
          backgroundColor: "rgba(255,255,255,0.02)",
          color: COLORS.textMuted,
          fontSize: { xs: "11px", sm: "12px" },
          fontWeight: 600,
          textTransform: "none",
          lineHeight: 1.2,
          letterSpacing: "0.01em",
          transition: "all 160ms ease",
          "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.07)" },
        }}
      >
        Home
      </Button>
      <Button
        component="a"
        href="/cash"
        size="small"
        variant="text"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          minWidth: "unset",
          flexShrink: 0,
          px: { xs: 1.1, sm: 2.1 },
          py: { xs: 0.45, sm: 0.6 },
          borderRadius: "999px",
          border: "none",
          backgroundColor: "rgba(185,158,79,0.14)",
          color: COLORS.white,
          fontSize: { xs: "11px", sm: "12px" },
          fontWeight: 700,
          textTransform: "none",
          letterSpacing: "0.01em",
          lineHeight: 1.2,
          transition: "all 160ms ease",
          "&:hover": { color: COLORS.white, backgroundColor: "rgba(185,158,79,0.22)" },
        }}
      >
        Solve
      </Button>
      <Button
        component="a"
        href="/trainer"
        size="small"
        variant="text"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          minWidth: "unset",
          flexShrink: 0,
          px: { xs: 1.1, sm: 2.1 },
          py: { xs: 0.45, sm: 0.6 },
          borderRadius: "999px",
          border: "none",
          backgroundColor: "rgba(255,255,255,0.02)",
          color: COLORS.textMuted,
          fontSize: { xs: "11px", sm: "12px" },
          fontWeight: 600,
          textTransform: "none",
          lineHeight: 1.2,
          letterSpacing: "0.01em",
          transition: "all 160ms ease",
          "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.07)" },
        }}
      >
        Train
      </Button>
      <Button
        component="a"
        href="/play"
        size="small"
        variant="text"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          minWidth: "unset",
          flexShrink: 0,
          px: { xs: 1.1, sm: 2.1 },
          py: { xs: 0.45, sm: 0.6 },
          borderRadius: "999px",
          border: "none",
          backgroundColor: "rgba(255,255,255,0.02)",
          color: COLORS.textMuted,
          fontSize: { xs: "11px", sm: "12px" },
          fontWeight: 600,
          textTransform: "none",
          lineHeight: 1.2,
          letterSpacing: "0.01em",
          transition: "all 160ms ease",
          "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.07)" },
        }}
      >
        Play
      </Button>
      <Button
        component="a"
        href="/analysis"
        size="small"
        variant="text"
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          minWidth: "unset",
          flexShrink: 0,
          px: { xs: 1.1, sm: 2.1 },
          py: { xs: 0.45, sm: 0.6 },
          borderRadius: "999px",
          border: "none",
          backgroundColor: "rgba(255,255,255,0.02)",
          color: COLORS.textMuted,
          fontSize: { xs: "11px", sm: "12px" },
          fontWeight: 600,
          textTransform: "none",
          lineHeight: 1.2,
          letterSpacing: "0.01em",
          transition: "all 160ms ease",
          "&:hover": { color: COLORS.white, backgroundColor: "rgba(255,255,255,0.07)" },
        }}
      >
        Analyze
      </Button>
    </Box>
  </Box>
);

const StreetCardsInline = ({ cards, count = 3, isMobile = false }) => {
  const displayCards = cards || [];
  const emptySlots = count - displayCards.length;
  // Desktop tiles mode: board cards at 2× the compact tile-card size. Phone unchanged.
  const cardW = isMobile ? 22 : 44;
  const cardH = isMobile ? 32 : 60;
  return (
    <Box sx={{ display: "flex", gap: isMobile ? 0.3 : 0.45, mr: isMobile ? 0.5 : 0.65 }}>
      {displayCards.map((card, idx) => (
        <Box key={idx} sx={{ width: cardW, height: cardH, backgroundColor: COLORS.black, border: `1px solid ${SUIT_COLORS[card[1]]}`, borderRadius: "3px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: isMobile ? 11 : 18, fontWeight: 700, color: SUIT_COLORS[card[1]], lineHeight: 1 }}>{card[0]}</Typography>
          <Typography sx={{ fontSize: isMobile ? 9 : 14, color: SUIT_COLORS[card[1]], lineHeight: 1 }}>{SUIT_SYMBOLS[card[1]]}</Typography>
        </Box>
      ))}
      {[...Array(emptySlots)].map((_, idx) => (
        <Box key={`empty-${idx}`} sx={{ width: cardW, height: cardH, backgroundColor: COLORS.bg, border: `1px dashed ${COLORS.border}`, borderRadius: "3px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: COLORS.border, fontSize: isMobile ? 11 : 16 }}>?</Typography>
        </Box>
      ))}
    </Box>
  );
};

// Temporary debug UI: show selected board vs canonical (AWS queried) board.
const BoardQueryDebug = ({ flop, turn, river, canonicalFlop, canonicalTurn, canonicalRiver }) => {
  const userBoard = [...(flop || []), ...(turn ? [turn] : []), ...(river ? [river] : [])];
  const canonicalBoard = [...(canonicalFlop || []), ...(canonicalTurn ? [canonicalTurn] : []), ...(canonicalRiver ? [canonicalRiver] : [])];
  return (
    <Box sx={{ mt: 1, p: 1, border: `1px solid ${COLORS.border}`, borderRadius: "6px", backgroundColor: COLORS.bg }}>
      <Typography sx={{ color: COLORS.textMuted, fontSize: "9px", fontWeight: 600, mb: 0.5 }}>
        Board (selected)
      </Typography>
      <StreetCardsInline cards={userBoard} count={5} />
      <Typography sx={{ color: COLORS.textMuted, fontSize: "9px", fontWeight: 600, mt: 0.75, mb: 0.5 }}>
        Board (queried / canonical)
      </Typography>
      <StreetCardsInline cards={canonicalBoard} count={5} />
    </Box>
  );
};

// Temporary debug UI: show how queried suits map to selected suits.
const SuitMappingDebug = ({ suitMapping }) => {
  if (!suitMapping?.fromCanonical) return null;
  const queriedSuits = SUITS; // queried/canonical suit letters

  const SuitLabel = ({ suit }) => (
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: SUIT_COLORS[suit] }}>
      {SUIT_SYMBOLS[suit]}
    </Typography>
  );

  return (
    <Box sx={{ mt: 1, p: 1, border: `1px solid ${COLORS.border}`, borderRadius: "6px", backgroundColor: COLORS.bg }}>
      <Typography sx={{ color: COLORS.textMuted, fontSize: "9px", fontWeight: 600, mb: 0.5 }}>
        Suit mapping (queried → selected)
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {queriedSuits.map((qs) => {
          const ds = suitMapping.fromCanonical?.[qs];
          return (
            <Box key={`q2d-${qs}`} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.25, border: `1px solid ${COLORS.border}`, borderRadius: "999px" }}>
              <SuitLabel suit={qs} />
              <Typography sx={{ color: COLORS.textMuted, fontSize: "9px" }}>→</Typography>
              {ds ? <SuitLabel suit={ds} /> : <Typography sx={{ color: COLORS.border, fontSize: "9px" }}>?</Typography>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const RangeCarryDebug = ({ playerRanges, positions, viewMode = "graph", onViewModeChange, equityMap = {}, usedCards = [] }) => {
  const CELL = 12.5;
  const [comboSortModeP1, setComboSortModeP1] = React.useState("weight"); // hand | ev | weight
  const [comboSortModeP2, setComboSortModeP2] = React.useState("weight"); // hand | ev | weight
  const buildMiniGrid = (rangeMap) => {
    return RANKS.map((r1, rowIdx) =>
      RANKS.map((r2, colIdx) => {
        const label = rowIdx === colIdx ? `${r1}${r1}` : rowIdx < colIdx ? `${r1}${r2}s` : `${r2}${r1}o`;
        const combos = generateAllCombos(label);
        let sum = 0;
        for (const c of combos) sum += rangeMap?.[c] || 0;
        const avg = combos.length ? sum / combos.length : 0; // 0..1
        const pct = Math.max(0, Math.min(1, avg));
        return { label, pct };
      })
    );
  };

  const summarize = (rangeMap) => {
    const entries = Object.entries(rangeMap || {}).filter(([, w]) => (w || 0) > 0);
    const total = entries.reduce((acc, [, w]) => acc + (w || 0), 0);
    return {
      count: entries.length,
      total,
      entries,
      grid: buildMiniGrid(rangeMap || {}),
    };
  };

  const p1 = summarize(playerRanges?.[0]);
  const p2 = summarize(playerRanges?.[1]);
  const p1Label = positions?.oop || "P1";
  const p2Label = positions?.ip || "P2";
  const formatEquity = (combo) => {
    const ev = Number(equityMap?.[combo]);
    if (!Number.isFinite(ev)) return "-";
    return ev.toFixed(2);
  };
  const getEquityColor = (combo) => {
    const ev = Number(equityMap?.[combo]);
    if (!Number.isFinite(ev)) return COLORS.textMuted;
    if (ev <= 0.4) return "#c44444";
    if (ev <= 3.4) return "#cf8a3a";
    if (ev <= 7) return "#97b548";
    return "#56ECCF";
  };
  const usedSet = React.useMemo(() => new Set((usedCards || []).filter(Boolean)), [usedCards]);
  const isBlockedCombo = React.useCallback(
    (combo) => {
      if (!combo || combo.length < 4) return false;
      const c1 = combo.slice(0, 2);
      const c2 = combo.slice(2, 4);
      return usedSet.has(c1) || usedSet.has(c2);
    },
    [usedSet]
  );
  const compareByHand = React.useCallback((aCombo, bCombo) => {
    const aCat = comboToHandCategory(aCombo) || "";
    const bCat = comboToHandCategory(bCombo) || "";
    const aR1 = RANKS.indexOf(aCat[0] || "2");
    const bR1 = RANKS.indexOf(bCat[0] || "2");
    if (aR1 !== bR1) return aR1 - bR1; // A first (index 0)
    const aR2 = RANKS.indexOf(aCat[1] || "2");
    const bR2 = RANKS.indexOf(bCat[1] || "2");
    if (aR2 !== bR2) return aR2 - bR2;
    const aType = aCat[2] || "";
    const bType = bCat[2] || "";
    if (aType !== bType) {
      if (aType === "s") return -1;
      if (bType === "s") return 1;
      if (aType === "o") return -1;
      if (bType === "o") return 1;
    }
    return String(aCombo).localeCompare(String(bCombo));
  }, []);
  const sortEntries = React.useCallback(
    (entries, mode) => {
      const arr = [...(entries || [])];
      if (mode === "hand") {
        arr.sort((a, b) => compareByHand(a[0], b[0]));
        return arr;
      }
      if (mode === "ev") {
        arr.sort((a, b) => {
          const aBlocked = isBlockedCombo(a[0]);
          const bBlocked = isBlockedCombo(b[0]);
          if (aBlocked !== bBlocked) return aBlocked ? 1 : -1;
          const aEv = Number(equityMap?.[a[0]]);
          const bEv = Number(equityMap?.[b[0]]);
          const av = Number.isFinite(aEv) ? aEv : -Infinity;
          const bv = Number.isFinite(bEv) ? bEv : -Infinity;
          return bv - av || compareByHand(a[0], b[0]);
        });
        return arr;
      }
      arr.sort((a, b) => (Number(b[1]) - Number(a[1])) || compareByHand(a[0], b[0]));
      return arr;
    },
    [equityMap, compareByHand, isBlockedCombo]
  );
  const p1EntriesSorted = React.useMemo(() => sortEntries(p1.entries, comboSortModeP1), [sortEntries, p1.entries, comboSortModeP1]);
  const p2EntriesSorted = React.useMemo(() => sortEntries(p2.entries, comboSortModeP2), [sortEntries, p2.entries, comboSortModeP2]);
  const SORT_HEADER_SX = {
    color: COLORS.textDark,
    fontSize: "7.5px",
    cursor: "pointer",
    userSelect: "none",
  };
  const ComboMiniHand = ({ combo }) => {
    if (!combo || combo.length < 4) {
      return <Typography sx={{ color: COLORS.border, fontSize: "7px" }}>--</Typography>;
    }
    const c1 = combo.slice(0, 2);
    const c2 = combo.slice(2, 4);
    return (
      <Box sx={{ display: "flex", gap: 0.2 }}>
        {[c1, c2].map((c, i) => (
          <Box
            key={`${combo}-${i}`}
            sx={{
              width: 14,
              height: 20,
              borderRadius: "3px",
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.black,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            <Typography sx={{ color: SUIT_COLORS[c[1]] || COLORS.textMuted, fontSize: "8px", fontWeight: 700, lineHeight: 1 }}>
              {c[0]}
            </Typography>
            <Typography sx={{ color: SUIT_COLORS[c[1]] || COLORS.textMuted, fontSize: "7px", lineHeight: 1 }}>
              {SUIT_SYMBOLS[c[1]] || "?"}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0.75, border: `1px solid ${COLORS.border}`, borderRadius: "6px", backgroundColor: COLORS.bg, height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 0.5, gap: 0.8 }}>
        <Typography sx={{ color: COLORS.textMuted, fontSize: "9px", fontWeight: 600 }}>
          Range
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(e, v) => v && onViewModeChange?.(v)}
          sx={{
            "& .MuiToggleButton-root": {
              color: COLORS.textMuted,
              borderColor: COLORS.border,
              fontSize: "8px",
              py: 0.1,
              px: 0.7,
              textTransform: "none",
              "&.Mui-selected": { backgroundColor: COLORS.cardBg, color: COLORS.silver, borderColor: COLORS.silver },
            },
          }}
        >
          <ToggleButton value="graph">Graph</ToggleButton>
          <ToggleButton value="combos">Combos</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.5 }}>
        <Typography sx={{ color: COLORS.silver, fontSize: "8px", fontWeight: 600 }}>{`Player 1 (${p1Label})`}</Typography>
        <Typography sx={{ color: COLORS.silver, fontSize: "8px", fontWeight: 600 }}>{`Player 2 (${p2Label})`}</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.6 }}>
        <Typography sx={{ color: COLORS.textMuted, fontSize: "8px" }}>combos: {p1.count} | weight: {p1.total.toFixed(2)}</Typography>
        <Typography sx={{ color: COLORS.textMuted, fontSize: "8px" }}>combos: {p2.count} | weight: {p2.total.toFixed(2)}</Typography>
      </Box>

      {viewMode === "graph" ? (
        <Box sx={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, overflow: "hidden" }}>
            {[p1, p2].map((summary, colIdx) => (
              <Box
                key={`range-grid-${colIdx}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(13, ${CELL}px)`,
                  gap: "1px",
                  backgroundColor: COLORS.black,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "3px",
                  overflow: "hidden",
                  width: "fit-content",
                  alignSelf: "start",
                }}
              >
                {summary.grid.flat().map((cell, idx) => (
                  <Box key={`cell-${colIdx}-${idx}`} sx={{ width: `${CELL}px`, height: `${CELL}px`, backgroundColor: COLORS.bg, position: "relative" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${cell.pct * 100}%`,
                        backgroundColor: COLORS.green,
                        opacity: 0.9,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
          {!(p1.count > 0 && p2.count > 0) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                px: 1.5,
                backgroundColor: "rgba(26,26,26,0.62)",
              }}
            >
              <Typography
                sx={{
                  color: COLORS.silver,
                  fontSize: "11px",
                  fontWeight: 700,
                  textAlign: "center",
                  lineHeight: 1.35,
                  textShadow: "0 1px 2px rgba(0,0,0,0.85)",
                  maxWidth: 220,
                }}
              >
                Graphs shown when two players have selected ranges
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", borderTop: `1px solid ${COLORS.border}`, pt: 0.4, ...THIN_SCROLLBAR_SX }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.35 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
              <Typography onClick={() => setComboSortModeP1("hand")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP1 === "hand" ? COLORS.silver : COLORS.textDark }}>
                combo
              </Typography>
              <Typography sx={SORT_HEADER_SX}>|</Typography>
              <Typography onClick={() => setComboSortModeP1("ev")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP1 === "ev" ? COLORS.silver : COLORS.textDark }}>
                ev
              </Typography>
              <Typography sx={SORT_HEADER_SX}>|</Typography>
              <Typography onClick={() => setComboSortModeP1("weight")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP1 === "weight" ? COLORS.silver : COLORS.textDark }}>
                wt
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
              <Typography onClick={() => setComboSortModeP2("hand")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP2 === "hand" ? COLORS.silver : COLORS.textDark }}>
                combo
              </Typography>
              <Typography sx={SORT_HEADER_SX}>|</Typography>
              <Typography onClick={() => setComboSortModeP2("ev")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP2 === "ev" ? COLORS.silver : COLORS.textDark }}>
                ev
              </Typography>
              <Typography sx={SORT_HEADER_SX}>|</Typography>
              <Typography onClick={() => setComboSortModeP2("weight")} sx={{ ...SORT_HEADER_SX, color: comboSortModeP2 === "weight" ? COLORS.silver : COLORS.textDark }}>
                wt
              </Typography>
            </Box>
          </Box>
          {Array.from({ length: Math.max(p1EntriesSorted.length, p2EntriesSorted.length) }).map((_, idx) => {
            const l = p1EntriesSorted[idx];
            const r = p2EntriesSorted[idx];
            const lBlocked = !!(l && isBlockedCombo(l[0]));
            const rBlocked = !!(r && isBlockedCombo(r[0]));
            const lHasAdjusted = !!(l && Number.isFinite(Number(equityMap?.[l[0]])));
            const rHasAdjusted = !!(r && Number.isFinite(Number(equityMap?.[r[0]])));
            return (
              <Box key={`range-row-${idx}`} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, py: 0.08, borderBottom: `1px solid rgba(255,255,255,0.03)` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, minWidth: 0 }}>
                  {l ? (
                    <>
                      <Box onClick={() => setComboSortModeP1("hand")} sx={{ cursor: "pointer", display: "inline-flex" }}>
                        <ComboMiniHand combo={l[0]} />
                      </Box>
                      {lBlocked || !lHasAdjusted ? (
                        <>
                          <Typography
                            onClick={() => setComboSortModeP1("ev")}
                            sx={{ color: COLORS.textDark, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            -
                          </Typography>
                          <Typography
                            onClick={() => setComboSortModeP1("weight")}
                            sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            -
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            onClick={() => setComboSortModeP1("ev")}
                            sx={{ color: getEquityColor(l[0]), fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            {formatEquity(l[0])}
                          </Typography>
                          <Typography
                            onClick={() => setComboSortModeP1("weight")}
                            sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            {(Number(l[1]) || 0).toFixed(2)}
                          </Typography>
                        </>
                      )}
                    </>
                  ) : (
                    <Typography sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace" }}>-</Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, minWidth: 0 }}>
                  {r ? (
                    <>
                      <Box onClick={() => setComboSortModeP2("hand")} sx={{ cursor: "pointer", display: "inline-flex" }}>
                        <ComboMiniHand combo={r[0]} />
                      </Box>
                      {rBlocked || !rHasAdjusted ? (
                        <>
                          <Typography
                            onClick={() => setComboSortModeP2("ev")}
                            sx={{ color: COLORS.textDark, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            -
                          </Typography>
                          <Typography
                            onClick={() => setComboSortModeP2("weight")}
                            sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            -
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            onClick={() => setComboSortModeP2("ev")}
                            sx={{ color: getEquityColor(r[0]), fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            {formatEquity(r[0])}
                          </Typography>
                          <Typography
                            onClick={() => setComboSortModeP2("weight")}
                            sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace", minWidth: 28, cursor: "pointer" }}
                          >
                            {(Number(r[1]) || 0).toFixed(2)}
                          </Typography>
                        </>
                      )}
                    </>
                  ) : (
                    <Typography sx={{ color: COLORS.textMuted, fontSize: "7.5px", fontFamily: "monospace" }}>-</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const ActionBox = ({ label, actions, selectedAction, raiseSize, onAction, isActive, isFolded, onClick, isClickable, isSelected, isMobile = false, demoFxEnabled = false, isPreflop = false, flashSignal = 0 }) => {
  const actionList = (actions || []).map((a) => a?.label || a?.id || "");
  const [flashing, setFlashing] = React.useState(false);
  // Desktop tiles are half-size; keep phone / demo at the full action-option floor.
  const fs = isMobile
    ? Math.max(ACTION_OPTION_FS, demoFxEnabled ? ACTION_OPTION_FS + 1 : ACTION_OPTION_FS)
    : Math.max(ACTION_OPTION_FS_DESKTOP_TILE, demoFxEnabled ? ACTION_OPTION_FS_DESKTOP_TILE + 1 : ACTION_OPTION_FS_DESKTOP_TILE);
  const btnMinH = Math.max(isMobile ? 30 : 18, Math.round(fs * (isMobile ? 1.85 : 1.55)));
  React.useEffect(() => {
    if (!isActive || !flashSignal) return undefined;
    setFlashing(true);
    const t = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(t);
  }, [flashSignal, isActive]);
  return (
  <Box
    onClick={isClickable ? onClick : undefined}
    data-pm-active-tile={isActive ? "true" : undefined}
    sx={{
      width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W,
      height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H,
      backgroundColor: isActive ? COLORS.cardBg : COLORS.bg,
      borderRadius: "4px",
      border: isActive ? `2px solid ${COLORS.white}` : isSelected ? `2px solid ${COLORS.border}` : `1px solid ${COLORS.border}`,
      p: isMobile ? 0.45 : 0.3,
      opacity: isFolded ? 0.35 : 1,
      cursor: isClickable ? "pointer" : "default",
      transition: "border-color 0.15s",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      "@keyframes pmActionTileFlash": {
        "0%, 100%": { borderColor: COLORS.white },
        "50%": { borderColor: COLORS.green },
      },
      animation: flashing ? "pmActionTileFlash 0.4s ease 3" : "none",
      "&:hover": isClickable && !isActive ? { borderColor: COLORS.white } : {},
    }}>
    <Typography sx={{ color: isActive ? COLORS.white : COLORS.textDark, fontSize: `${fs}px`, fontWeight: 700, textAlign: "center", mb: isMobile ? 0.35 : 0.15, lineHeight: 1.1 }}>{label}</Typography>
    {selectedAction && (
      <Box sx={{ py: isMobile ? 0.3 : 0.15, backgroundColor: selectedAction === "fold" ? COLORS.bg : COLORS.cardBg, borderRadius: "2px", textAlign: "center", mb: isMobile ? 0.35 : 0.15 }}>
        <Typography sx={{ color: selectedAction === "fold" ? "#444" : COLORS.silver, fontSize: `${fs}px`, fontWeight: 500, lineHeight: 1.1 }}>{getActionDisplayName(selectedAction, raiseSize)}</Typography>
      </Box>
    )}
    <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "4px" : "2px", flex: 1, minWidth: 0, width: "100%", overflow: "hidden" }}>
      {actions.map((act, actIdx) => {
        const fill = isActive
          ? getActionColor(act.label || act.id, actIdx, actionList.length, { isPreflop, preflopFoldRed: true, actionList })
          : null;
        return (
        <Button key={act.id} onClick={(e) => { e.stopPropagation(); if (act.enabled) onAction(act.id, act.size); }} disabled={!act.enabled}
          sx={{ minHeight: btnMinH, flex: 1, minWidth: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box",
            fontSize: `${fs}px`, fontWeight: 700, textTransform: "none",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.1,
            backgroundColor: isActive && act.enabled
              ? fill
              : (selectedAction === act.id ? COLORS.border : (act.enabled ? "rgba(255,255,255,0.05)" : "transparent")),
            color: isActive && act.enabled ? "#111" : (act.enabled ? COLORS.text : COLORS.border),
            border: isActive && act.enabled ? `1px solid ${fill}` : (act.enabled ? `1px solid ${COLORS.silver}44` : `1px solid transparent`),
            borderRadius: "3px", p: isMobile ? "4px 6px" : "2px 3px",
            "&:hover": { backgroundColor: isActive && act.enabled ? fill : (act.enabled ? COLORS.border : "transparent"), borderColor: act.enabled ? (isActive ? fill : COLORS.silver) : "transparent", filter: isActive && act.enabled ? "brightness(1.08)" : "none" },
            "&.Mui-disabled": { color: COLORS.border } }}>{act.label}</Button>
        );
      })}
    </Box>
  </Box>
  );
};

const PlaceholderBox = ({ label, isMobile = false }) => (
  <Box sx={{ width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W, height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H, backgroundColor: COLORS.bg, borderRadius: "4px", border: `1px dashed ${COLORS.border}`, p: isMobile ? 0.45 : 0.3, opacity: 0.5, display: "flex", flexDirection: "column" }}>
    <Typography sx={{ color: COLORS.textDark, fontSize: `${isMobile ? ACTION_OPTION_FS : ACTION_OPTION_FS_DESKTOP_TILE}px`, fontWeight: 700, textAlign: "center", mb: 0.15, lineHeight: 1.1 }}>{label}</Typography>
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Typography sx={{ color: COLORS.border, fontSize: `${isMobile ? ACTION_OPTION_FS : ACTION_OPTION_FS_DESKTOP_TILE}px` }}>-</Typography></Box>
  </Box>
);

/** Seat centers sit on the rounded-square border (fractions along each edge). */
const TABLE_SEAT_EDGE = {
  lj: { edge: "top", t: 0.28 },
  hj: { edge: "top", t: 0.72 },
  co: { edge: "right", t: 0.5 },
  btn: { edge: "bottom", t: 0.72 },
  sb: { edge: "bottom", t: 0.28 },
  bb: { edge: "left", t: 0.5 },
};

/** After each finished hand, hero moves earlier: BTN → CO → HI → LO → BB → SB → BTN… */
const HERO_ROTATION_ORDER = ["btn", "co", "hj", "lj", "bb", "sb"];
const HERO_SEAT_OPTIONS = [
  { id: "btn", label: "BTN" },
  { id: "sb", label: "SB" },
  { id: "bb", label: "BB" },
  { id: "lj", label: "LO" },
  { id: "hj", label: "HI" },
  { id: "co", label: "CO" },
];
const SOLVER_LAYOUT_STORAGE_KEY = "pm_solver_layout_v1";
const TABLE_SEAT_ORDER = ["lj", "hj", "co", "btn", "sb", "bb"];

function nextHeroSeat(current) {
  const id = String(current || "btn").toLowerCase();
  const idx = HERO_ROTATION_ORDER.indexOf(id);
  if (idx < 0) return "btn";
  return HERO_ROTATION_ORDER[(idx + 1) % HERO_ROTATION_ORDER.length];
}

/** Rotate geometry so `heroSeat` always sits at the bottom-right (BTN visual slot). */
function visualEdgeForHeroRotatedSeat(logicalSeat, heroSeat) {
  const order = TABLE_SEAT_ORDER;
  const heroIdx = order.indexOf(String(heroSeat || "btn").toLowerCase());
  const seatIdx = order.indexOf(String(logicalSeat || "").toLowerCase());
  const btnIdx = order.indexOf("btn");
  if (heroIdx < 0 || seatIdx < 0 || btnIdx < 0) return TABLE_SEAT_EDGE[logicalSeat] || TABLE_SEAT_EDGE.btn;
  const visualIdx = (seatIdx - heroIdx + btnIdx + order.length) % order.length;
  return TABLE_SEAT_EDGE[order[visualIdx]] || TABLE_SEAT_EDGE.btn;
}

function loadSolverLayoutMode() {
  try {
    const v = String(localStorage.getItem(SOLVER_LAYOUT_STORAGE_KEY) || "").toLowerCase();
    if (v === "tiles" || v === "table") return v;
  } catch (_) {}
  return "table";
}

function seatCenterOnBorder(edgeSpec, tableW, tableH, seatSize) {
  const half = seatSize / 2;
  const innerW = Math.max(0, tableW - seatSize);
  const innerH = Math.max(0, tableH - seatSize);
  const t = Math.min(1, Math.max(0, edgeSpec?.t ?? 0.5));
  switch (edgeSpec?.edge) {
    case "top":
      return { x: half + t * innerW, y: half };
    case "bottom":
      return { x: half + t * innerW, y: tableH - half };
    case "left":
      return { x: half, y: half + t * innerH };
    case "right":
      return { x: tableW - half, y: half + t * innerH };
    default:
      return { x: tableW / 2, y: tableH / 2 };
  }
}

/** Push a rectangle-border seat point out onto an oval of the same bounding box. */
function projectPointOntoEllipse(x, y, tableW, tableH, seatSize) {
  const cx = tableW / 2;
  const cy = tableH / 2;
  const rx = Math.max(1, (tableW - seatSize) / 2);
  const ry = Math.max(1, (tableH - seatSize) / 2);
  const dx = x - cx;
  const dy = y - cy;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return { x: cx, y: cy };
  const scale = 1 / Math.sqrt((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry));
  return { x: cx + dx * scale, y: cy + dy * scale };
}

/** Spot codes (LO/HI/SB/…) → POSITIONS id (lj/hj/sb/…). */
function seatIdFromSpotCode(code) {
  if (!code && code !== 0) return null;
  const key = String(code).toLowerCase();
  const id = key === "lo" ? "lj" : key === "hi" ? "hj" : key;
  return POSITIONS.some((p) => p.id === id) ? id : null;
}

/** Moose-branded hole-card back for table seats. */
const MooseTableCardBack = ({ width = 34, height = 48 }) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: "6px",
      border: "1px solid rgba(180,180,180,0.55)",
      backgroundColor: "#15161a",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${logoWithText})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: `${Math.max(12, Math.floor(width * 0.72))}px auto`,
        filter: "grayscale(1)",
        opacity: 0.9,
      }}
    />
    <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(70, 90, 120, 0.28)" }} />
  </Box>
);

/** Trainer-matching poker chip; Solver table always uses gold. */
const PokerChip = ({ size = 16, alt = false, sx = {} }) => {
  const chip = CHIP_COLOR_OPTIONS.gold;
  const fill = alt ? chip.a : chip.b;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `1px solid ${chip.border}`,
        background: fill,
        boxShadow: `0 0 0 1px ${hexToRgba(chip.border, 0.18)}`,
        ...sx,
      }}
    />
  );
};

/** Trainer-style bet pile: overlapping chips + amount label. */
const SeatBetChips = ({ amountBb = 0, seatId = null, size = 16, showLabel = true, isPot = false, labelFontSize = null }) => {
  const amt = Number(amountBb);
  if (!(amt > 0)) return null;
  const isBlindOnly =
    !isPot && (
      (seatId === "sb" && amt <= 0.5001)
      || (seatId === "bb" && amt <= 1.0001)
    );
  const count = isPot ? 3 : (isBlindOnly ? 1 : 3);
  const step = Math.max(7, Math.round(size * 0.56));
  const label = Number.isInteger(amt) ? String(amt) : amt.toFixed(1).replace(/\.0$/, "");
  const chipFs = labelFontSize != null
    ? labelFontSize
    : Math.max(CHIP_LABEL_FS_FLOOR, size >= 16 ? 10 : 9);
  // Buffer between chip icons and amount so scaled floors never collide.
  const labelGapPx = Math.max(6, Math.round(chipFs * 0.45));
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: `${labelGapPx}px`,
        pointerEvents: "none",
      }}
    >
      <Box sx={{ position: "relative", width: step * (count - 1) + size, height: size, flexShrink: 0 }}>
        {Array.from({ length: count }).map((_, i) => (
          <PokerChip
            key={`chip-${i}`}
            size={size}
            alt={i % 2 === 1}
            sx={{ position: "absolute", top: 0, left: `${i * step}px` }}
          />
        ))}
      </Box>
      {showLabel && (
        <Typography
          sx={{
            fontSize: chipFs,
            color: "#ffffff",
            fontWeight: 400,
            fontFamily: FUTURA_BOOK,
            lineHeight: 1,
            textShadow: "0 1px 2px rgba(0,0,0,0.65)",
            mt: 0,
            pt: 0,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

/** Mini board strip for table center (flop/turn/river + pot). */
const TableBoardMini = ({
  cards = [],
  pot = null,
  potLabel = null,
  streetLabel = "",
  showPotChips = false,
  potBb = null,
  large = false,
  isMobile = false,
  chipLabelFontSize = null,
  chipSize = null,
}) => {
  const cardW = large ? (isMobile ? 30 : 42) : 16;
  const cardH = large ? (isMobile ? 42 : 58) : 22;
  const rankFs = large ? (isMobile ? 14 : 18) : 9;
  const streetFs = large ? (isMobile ? 11 : 13) : 9;
  const potChipSize = chipSize != null ? chipSize : (large ? (isMobile ? 16 : 18) : 18);
  const potAmountFs = chipLabelFontSize != null
    ? chipLabelFontSize
    : Math.max(CHIP_LABEL_FS_FLOOR, large ? (isMobile ? 10 : 11) : 9);
  const hasPotChips = showPotChips && Number(potBb) > 0;
  const potText = potLabel || (Number.isFinite(Number(pot)) ? `${formatPostflopHalfBb(pot)}bb` : "");
  return (
  <Box
    sx={{
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: large ? 0.55 : 0.35,
      pointerEvents: "none",
      zIndex: 0,
      maxWidth: "78%",
    }}
  >
    {hasPotChips && (
      <SeatBetChips
        amountBb={potBb}
        size={potChipSize}
        isPot
        showLabel
        labelFontSize={potAmountFs}
      />
    )}
    {(streetLabel || (!hasPotChips && potText)) && (
      <Typography sx={{
        fontSize: hasPotChips ? streetFs : potAmountFs,
        fontWeight: 400,
        color: "#e8e8e8",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        lineHeight: 1,
        fontFamily: FUTURA_BOOK,
        textShadow: "0 1px 3px rgba(0,0,0,0.55)",
      }}>
        {streetLabel}
        {!hasPotChips && potText ? `${streetLabel ? " · " : ""}${potText}` : ""}
      </Typography>
    )}
    <Box sx={{ display: "flex", gap: large ? "5px" : "2px", flexWrap: "nowrap", justifyContent: "center" }}>
      {(cards || []).map((card, idx) => (
        <Box
          key={`${card}-${idx}`}
          sx={{
            width: cardW,
            height: cardH,
            borderRadius: large ? "6px" : "2px",
            backgroundColor: COLORS.black,
            border: `${large ? 2 : 1}px solid ${SUIT_COLORS[card[1]] || COLORS.border}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: large ? "0 3px 8px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Typography sx={{ fontSize: rankFs, fontWeight: large ? 400 : 800, color: SUIT_COLORS[card[1]] || COLORS.silver, lineHeight: 1, fontFamily: large ? FUTURA_BOOK : FUTURA_FONT }}>
            {card[0]}
          </Typography>
          {large && (
            <Typography sx={{ fontSize: rankFs * 0.75, color: SUIT_COLORS[card[1]] || COLORS.silver, lineHeight: 1 }}>
              {SUIT_SYMBOLS[card[1]]}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  </Box>
  );
};

/**
 * Whole-hand birds-eye table: preflop → river on one rounded square.
 * Seats on the border; action panel beside; back control top-left.
 */
/** Shrink seat-circle text until it fits without ellipsis. */
const FitSeatLabel = ({
  text,
  maxFontSize,
  minFontSize = 7,
  color,
  fontFamily = FUTURA_BOOK,
  fontWeight = 400,
}) => {
  const ref = React.useRef(null);
  const [fontSize, setFontSize] = React.useState(maxFontSize);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let size = maxFontSize;
    el.style.fontSize = `${size}px`;
    // With width:100% + nowrap, scrollWidth grows when the label won't fit.
    while (size > minFontSize && el.scrollWidth > el.clientWidth + 0.5) {
      size -= 0.5;
      el.style.fontSize = `${size}px`;
    }
    setFontSize(size);
    return undefined;
  }, [text, maxFontSize, minFontSize, fontFamily, fontWeight]);

  return (
    <Typography
      ref={ref}
      component="div"
      sx={{
        fontFamily,
        fontSize,
        fontWeight,
        color,
        lineHeight: 1,
        maxWidth: "100%",
        width: "100%",
        whiteSpace: "nowrap",
        overflow: "visible",
        textAlign: "center",
        textOverflow: "clip",
      }}
    >
      {text}
    </Typography>
  );
};

const HandTableView = ({
  seats, // [{ id, name, actionLabel, folded, isActive, isSelected, clickable, onClick, isHero, holeCards }]
  panelTitle,
  panelActions, // [{ id, label, enabled, onClick }]
  panelEmptyText,
  onReturnToCurrent,
  showReturnToCurrent = false,
  onBack,
  canGoBack = false,
  boardCards = [],
  pot = null,
  potLabel = null,
  potBbForChips = null,
  streetLabel = "",
  isMobile = false,
  isPreflop = false,
  readOnly = false,
  onHintClick = null,
  // Beginner / table-first mode
  variant = "compact", // "compact" | "beginner"
  heroSeatId = null,
  seatEdgeById = null, // optional { seatId: edgeSpec }
  hideSidePanel = false,
  actionsBesideActiveSeat = false,
  committedBbBySeat = null, // { seatId: amountBb }
  heroHandProps = null, // HandSelector props for table-inline picker + randomizer
  handSelectorRef = null,
  heroActionsRef = null,
  onHeroSeatChange = null,
  heroSeatOptions = HERO_SEAT_OPTIONS,
  onReset = null, // unused on-table; Reset lives in Tutorial chrome
}) => {
  const isBeginner = variant === "beginner";
  // Phone beginner: fixed design stage + uniform CSS scale (taller felt).
  // Desktop beginner: fluid width (drawW = measuredW), fixed boxH ~460 - no scale stage.
  const useScaleStage = isBeginner && isMobile;
  const TABLE_DESIGN_W = 560;
  const TABLE_DESIGN_H = 600;
  const boxH = isBeginner
    ? (isMobile ? TABLE_DESIGN_H : 460)
    : (isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H);
  const tableW = isBeginner
    ? (isMobile ? TABLE_DESIGN_W : "100%")
    : (isMobile ? Math.max(ACTION_BOX_W_MOBILE * 1.6, 240) : 320);
  const seatSize = isBeginner
    ? (isMobile ? 96 : 80)
    : (isMobile ? Math.min(52, Math.floor(boxH * 0.34)) : 42);
  const halfSeat = seatSize / 2;
  // Match Trainer "Rounded" table style (44px) on beginner Solve felt.
  const tableRadius = isBeginner ? 44 : (isMobile ? 16 : 12);
  const hasActions = (panelActions || []).length > 0;
  const actionTileW = isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W + 8;
  const BACK_BLUE = "#4C8DFF";
  const actionList = (panelActions || []).map((a) => a?.label || a?.id || "");
  const rootRef = React.useRef(null);
  const tableWrapRef = React.useRef(null);
  const [measuredW, setMeasuredW] = React.useState(isBeginner ? (isMobile ? 340 : 560) : (typeof tableW === "number" ? tableW : 320));
  const showHeroActionBar = isBeginner && !readOnly;

  React.useEffect(() => {
    if (!isBeginner) return undefined;
    const el = useScaleStage ? rootRef.current : tableWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const sync = () => {
      const w = el.clientWidth;
      if (Number.isFinite(w) && w > 40) setMeasuredW(w);
    };
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, [isBeginner, isMobile, useScaleStage]);

  // Phone: width-driven uniform scale (capped at 1.2). Desktop beginner: no scale.
  const tableScale = useScaleStage
    ? Math.max(0.42, Math.min(1.2, measuredW / TABLE_DESIGN_W))
    : 1;
  const drawW = useScaleStage
    ? TABLE_DESIGN_W
    : (isBeginner ? measuredW : (typeof tableW === "number" ? tableW : 320));
  const stageOuterH = useScaleStage ? TABLE_DESIGN_H * tableScale : boxH;
  // Fonts live inside the scaled stage on phone - size in design space so floors hold.
  const tableInvScale = useScaleStage ? (1 / Math.max(tableScale, 0.01)) : 1;
  const tableFsFloorScreen = ACTION_OPTION_FS;
  const tableFsFloorDesign = useScaleStage
    ? Math.max(tableFsFloorScreen * tableInvScale, tableFsFloorScreen)
    : tableFsFloorScreen;
  const chipFsFloorDesign = useScaleStage
    ? Math.max(CHIP_LABEL_FS_FLOOR * tableInvScale, CHIP_LABEL_FS_FLOOR)
    : CHIP_LABEL_FS_FLOOR;
  const resolveSeatCenter = (edge) => seatCenterOnBorder(edge, drawW, boxH, seatSize);
  const [heroSeatMenuOpen, setHeroSeatMenuOpen] = React.useState(false);
  const activeSeat = (seats || []).find((s) => s.isActive) || null;
  const activeEdge = activeSeat
    ? (seatEdgeById?.[activeSeat.id] || TABLE_SEAT_EDGE[activeSeat.id] || TABLE_SEAT_EDGE.btn)
    : null;
  const activeCenter = activeSeat && activeEdge
    ? resolveSeatCenter(activeEdge)
    : null;
  const heroSeatObj = (seats || []).find((s) => s.isHero) || null;
  const heroEdge = heroSeatObj
    ? (seatEdgeById?.[heroSeatObj.id] || TABLE_SEAT_EDGE[heroSeatObj.id] || TABLE_SEAT_EDGE.btn)
    : null;
  const heroCenter = heroSeatObj && heroEdge ? resolveSeatCenter(heroEdge) : null;
  const heroHoleCards = Array.isArray(heroSeatObj?.holeCards) && heroSeatObj.holeCards[0] && heroSeatObj.holeCards[1]
    ? heroSeatObj.holeCards
    : null;
  const inwardByPixels = (center, alongPx, perpPx = 0) => {
    if (!center) return null;
    const cx = drawW / 2;
    const cy = boxH / 2;
    const dx = cx - center.x;
    const dy = cy - center.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // Perpendicular unit (keeps chips off the card cluster when space is tight).
    const px = -uy;
    const py = ux;
    return {
      left: center.x + ux * alongPx + px * perpPx,
      top: center.y + uy * alongPx + py * perpPx,
    };
  };
  const heroCardW = 34;
  const heroCardH = 48;
  const chipSizePx = 16;
  // Seat → cards → chips toward pot, with enough radial clearance that piles never overlap cards.
  const gapSeatToCards = 8;
  const gapCardsToChips = 18;
  const chipLabelGapPx = useScaleStage ? Math.max(6, Math.round(chipFsFloorDesign * 0.45)) : 0;
  const chipStackH = useScaleStage
    ? chipSizePx + chipLabelGapPx + chipFsFloorDesign
    : chipSizePx + 14; // chip + white amount label
  // Desktop: perfect-play bar sits on the felt above hero - bump cards/chips inward a touch.
  const heroHandAlongBump = isBeginner && heroHandProps && !isMobile ? 10 : 0;
  const cardClearPx = halfSeat + heroCardH * 0.5 + gapSeatToCards;
  const chipClearPx = cardClearPx + heroCardH * 0.5 + gapCardsToChips + chipStackH * 0.5;
  // Cap how far toward center we go so chips stay near the seat and away from the pot.
  const maxAlong = Math.max(cardClearPx + 8, Math.min(drawW, boxH) * 0.26);
  const clampAlong = (v) => Math.min(v, maxAlong);
  const heroCardsPos = inwardByPixels(heroCenter, clampAlong(cardClearPx + heroHandAlongBump), 0);
  const heroChipsPos = inwardByPixels(
    heroCenter,
    Math.max(
      clampAlong(chipClearPx + heroHandAlongBump),
      clampAlong(cardClearPx + heroHandAlongBump) + heroCardH * 0.5 + gapCardsToChips + chipStackH * 0.5
    ),
    isMobile ? 12 : 14
  );
  const showCenterPot = isBeginner && (
    (Number.isFinite(Number(potBbForChips)) && Number(potBbForChips) > 0.5)
    || (Number.isFinite(Number(pot)) && Number(pot) > 1)
    || !!potLabel
  );
  const heroIsActive = !!(activeSeat && activeSeat.isHero);
  const showBesideSeatActions = actionsBesideActiveSeat && !heroIsActive;
  const heroActionPlaceholders = [
    { id: "ph-fold", label: "Fold" },
    { id: "ph-call", label: "Call" },
    { id: "ph-raise", label: "Raise" },
  ];
  const heroActionsLive = heroIsActive && hasActions;
  // Opponent options sit inside the scaled table stage - size in design space so
  // on-screen fonts never drop below ACTION_OPTION_FS, and grow when tableScale > 1.
  const optInvScale = useScaleStage ? tableInvScale : 1;
  const optFs = useScaleStage ? tableFsFloorDesign : (isMobile ? 12 : 13);
  const optBoxW = useScaleStage ? Math.max(160 * optInvScale, 160) : (isMobile ? 118 : 132);
  const optBtnMinH = useScaleStage ? Math.max(36 * optInvScale, 36) : (isMobile ? 28 : 32);
  const optPad = useScaleStage ? Math.max(0.55 * optInvScale, 0.55) : 0.55;
  const optGap = useScaleStage ? Math.max(4 * optInvScale, 4) : 4;
  const optRadius = useScaleStage ? Math.max(8 * optInvScale, 8) : 8;

  // Place action popover toward table center from the active seat.
  const actionPopoverSx = (() => {
    if (!activeCenter || !activeEdge) return null;
    const pad = 8;
    const edge = activeEdge.edge;
    if (edge === "top") {
      return { left: activeCenter.x, top: activeCenter.y + seatSize * 0.55 + pad, transform: "translate(-50%, 0)" };
    }
    if (edge === "bottom") {
      return { left: activeCenter.x, top: activeCenter.y - seatSize * 0.55 - pad, transform: "translate(-50%, -100%)" };
    }
    if (edge === "left") {
      return { left: activeCenter.x + seatSize * 0.55 + pad, top: activeCenter.y, transform: "translate(0, -50%)" };
    }
    return { left: activeCenter.x - seatSize * 0.55 - pad, top: activeCenter.y, transform: "translate(-100%, -50%)" };
  })();

  return (
    <Box
      ref={useScaleStage ? rootRef : undefined}
      onClick={readOnly ? (e) => { e.stopPropagation(); onHintClick?.(); } : undefined}
      sx={{
        display: "flex",
        flexDirection: isBeginner ? "column" : "row",
        alignItems: isBeginner ? "stretch" : "stretch",
        gap: isBeginner ? 1 : 0.75,
        height: isBeginner ? "auto" : boxH,
        width: isBeginner ? "100%" : "max-content",
        maxWidth: "100%",
        pointerEvents: "auto",
        userSelect: readOnly ? "none" : "auto",
        cursor: readOnly && onHintClick ? "pointer" : "default",
        fontFamily: isBeginner ? FUTURA_BOOK : undefined,
      }}
    >
      <Box
        ref={tableWrapRef}
        sx={{
          position: "relative",
          width: useScaleStage ? TABLE_DESIGN_W * tableScale : (isBeginner ? "100%" : tableW),
          maxWidth: useScaleStage ? "100%" : undefined,
          height: stageOuterH,
          flexShrink: 0,
          alignSelf: useScaleStage ? "center" : undefined,
          minWidth: isBeginner && !useScaleStage ? 0 : undefined,
          backgroundColor: "transparent",
          borderRadius: 0,
          overflow: isBeginner ? "visible" : "hidden",
          pointerEvents: readOnly ? "none" : "auto",
          border: "none",
        }}
      >
        <Box
          sx={{
            position: useScaleStage || isBeginner ? "absolute" : "relative",
            left: 0,
            top: 0,
            width: useScaleStage ? TABLE_DESIGN_W : "100%",
            height: boxH,
            transform: useScaleStage ? `scale(${tableScale})` : "none",
            transformOrigin: "top left",
          }}
        >
        <Box
          sx={{
            position: "absolute",
            left: halfSeat - 0.75,
            top: halfSeat - 0.75,
            width: drawW - seatSize + 1.5,
            height: boxH - seatSize + 1.5,
            borderRadius: `${tableRadius}px`,
            border: isBeginner
              ? "3px solid rgba(90, 120, 160, 0.9)"
              : "1.5px solid rgba(200,200,200,0.55)",
            backgroundColor: isBeginner ? "#1a4f86" : "transparent",
            backgroundImage: isBeginner
              ? "radial-gradient(ellipse at 50% 42%, #2a6fb0 0%, #1a4f86 50%, #123a64 100%)"
              : "none",
            boxShadow: isBeginner
              ? "inset 0 0 42px rgba(0,0,0,0.35), 0 10px 28px rgba(0,0,0,0.35)"
              : "none",
            pointerEvents: "none",
            boxSizing: "border-box",
          }}
        />

        {(boardCards?.length > 0 || streetLabel || showCenterPot) && (
          <TableBoardMini
            cards={boardCards}
            pot={pot}
            potLabel={potLabel}
            streetLabel={streetLabel}
            showPotChips={showCenterPot}
            potBb={Number.isFinite(Number(potBbForChips)) ? Number(potBbForChips) : null}
            large={isBeginner}
            isMobile={isMobile}
            chipLabelFontSize={useScaleStage ? chipFsFloorDesign : null}
            chipSize={useScaleStage ? chipSizePx : null}
          />
        )}

        {(seats || []).map((seat) => {
          const edge = seatEdgeById?.[seat.id] || TABLE_SEAT_EDGE[seat.id];
          const center = resolveSeatCenter(edge);
          const alongCards = clampAlong(cardClearPx);
          const alongChips = Math.max(
            clampAlong(chipClearPx),
            alongCards + heroCardH * 0.5 + gapCardsToChips + chipStackH * 0.5
          );
          // Small tangential nudge so chip piles clear the two-card span on tight phone layouts.
          const chipPerp = isMobile ? 8 : 0;
          const cardsPos = inwardByPixels(center, alongCards, 0);
          const chipsPos = seat.isHero
            ? heroChipsPos
            : inwardByPixels(center, alongChips, chipPerp);
          const seatAmt = seat.folded ? 0 : Number(committedBbBySeat?.[seat.id] || 0);
          const showOppBacks = isBeginner && !seat.isHero && !seat.folded;
          return (
            <React.Fragment key={seat.id}>
            {showOppBacks && cardsPos && (
              <Box
                sx={{
                  position: "absolute",
                  left: cardsPos.left,
                  top: cardsPos.top,
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  gap: 0.45,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <MooseTableCardBack width={heroCardW} height={heroCardH} />
                <MooseTableCardBack width={heroCardW} height={heroCardH} />
              </Box>
            )}
            {isBeginner && seatAmt > 0 && chipsPos && (
              <Box
                sx={{
                  position: "absolute",
                  left: chipsPos.left,
                  top: chipsPos.top,
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <SeatBetChips amountBb={seatAmt} seatId={seat.id} size={chipSizePx} labelFontSize={useScaleStage ? chipFsFloorDesign : undefined} />
              </Box>
            )}
            <Box
              onClick={() => {
                if (readOnly) return;
                if (isBeginner && seat.isHero && onHeroSeatChange) {
                  setHeroSeatMenuOpen((open) => !open);
                  return;
                }
                if (seat.clickable) seat.onClick?.();
              }}
              sx={{
                position: "absolute",
                left: center.x,
                top: center.y,
                transform: "translate(-50%, -50%)",
                width: seatSize,
                height: seatSize,
                borderRadius: "50%",
                backgroundColor: COLORS.bg,
                border: seat.isActive || seat.isSelected
                  ? `2px solid ${COLORS.white}`
                  : (isBeginner && seat.isHero)
                    ? `2px solid ${COLORS.green}`
                    : `1px solid rgba(160,160,160,0.55)`,
                boxShadow: seat.isActive ? "0 0 0 1px rgba(255,255,255,0.2)" : "none",
                opacity: seat.folded ? 0.4 : seat.dimmed ? 0.45 : 1,
                cursor: (!readOnly && (seat.clickable || (isBeginner && seat.isHero && onHeroSeatChange))) ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: isBeginner ? 0.35 : 0.1,
                px: isBeginner ? 0.35 : 0.45,
                pt: isBeginner ? 0.3 : 0,
                pb: isBeginner ? 0.45 : 0,
                boxSizing: "border-box",
                overflow: "hidden",
                zIndex: seat.isActive ? 6 : (isBeginner && seat.isHero) ? 5 : 4,
                fontFamily: isBeginner ? FUTURA_BOOK : FUTURA_FONT,
              }}
            >
              <FitSeatLabel
                text={(isBeginner && seat.isHero) ? `Hero · ${seat.name}` : seat.name}
                maxFontSize={isBeginner ? (useScaleStage ? Math.max(tableFsFloorDesign, 16) : 14) : (isMobile ? 10 : 9)}
                minFontSize={isBeginner ? (useScaleStage ? tableFsFloorDesign : 8) : 6}
                fontFamily={isBeginner ? FUTURA_BOOK : FUTURA_FONT}
                fontWeight={isBeginner ? 400 : 800}
                color={seat.isActive
                  ? COLORS.white
                  : (isBeginner && seat.isHero)
                    ? COLORS.green
                    : "#d0d0d0"}
              />
              <FitSeatLabel
                text={seat.folded ? "Fold" : seat.actionLabel || (seat.isActive ? "Act" : "-")}
                maxFontSize={isBeginner ? (useScaleStage ? Math.max(tableFsFloorDesign, 14) : 12) : (isMobile ? 8 : 7)}
                minFontSize={isBeginner ? (useScaleStage ? tableFsFloorDesign : 7) : 5}
                fontFamily={isBeginner ? FUTURA_BOOK : FUTURA_FONT}
                fontWeight={isBeginner ? 400 : 600}
                color={seat.folded ? "#666" : seat.actionLabel ? "#c8c8c8" : COLORS.textMuted}
              />
            </Box>
            </React.Fragment>
          );
        })}

        {isBeginner && heroSeatMenuOpen && heroCenter && onHeroSeatChange && (
          <Box
            sx={{
              position: "absolute",
              left: heroCenter.x,
              top: Math.max(8, heroCenter.y - seatSize * 0.55 - 8),
              transform: "translate(-50%, -100%)",
              zIndex: 12,
              p: 0.7,
              borderRadius: "10px",
              border: `2px solid ${COLORS.white}`,
              backgroundColor: "rgba(16,18,20,0.96)",
              boxShadow: "0 8px 22px rgba(0,0,0,0.45)",
              display: "flex",
              flexDirection: "column",
              gap: 0.55,
              minWidth: isMobile ? 150 : 180,
              fontFamily: FUTURA_BOOK,
              pointerEvents: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography sx={{ color: COLORS.white, fontSize: isMobile ? 11 : 13, fontWeight: 400, textAlign: "center", fontFamily: FUTURA_BOOK }}>
              Hero position
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.45 }}>
              {(heroSeatOptions || HERO_SEAT_OPTIONS).map((opt) => {
                const selected = String(heroSeatId || "").toLowerCase() === opt.id;
                return (
                  <Button
                    key={opt.id}
                    size="small"
                    onClick={() => {
                      onHeroSeatChange(opt.id);
                      setHeroSeatMenuOpen(false);
                    }}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      py: 0.45,
                      textTransform: "none",
                      fontSize: isMobile ? 11 : 13,
                      fontWeight: 400,
                      fontFamily: FUTURA_BOOK,
                      borderRadius: "6px",
                      border: `1px solid ${COLORS.white}`,
                      color: selected ? "#111" : COLORS.white,
                      backgroundColor: selected ? COLORS.white : "transparent",
                      "&:hover": {
                        borderColor: COLORS.white,
                        color: selected ? "#111" : COLORS.white,
                        backgroundColor: selected ? COLORS.white : "rgba(255,255,255,0.12)",
                      },
                    }}
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Desktop: perfect-play bar + hole cards on the felt above the hero seat. */}
        {isBeginner && heroCardsPos && heroHandProps && !isMobile && (
          <Box
            ref={handSelectorRef}
            sx={{
              position: "absolute",
              left: heroCardsPos.left,
              top: heroCardsPos.top,
              transform: "translate(-50%, -50%)",
              zIndex: 1,
              pointerEvents: "auto",
            }}
          >
            <HandSelector
              {...heroHandProps}
              variant="tableInline"
              isMobile={isMobile}
              randomizerActive={heroIsActive}
            />
          </Box>
        )}

        {/* Phone / no hand props: hole cards only on the felt. */}
        {isBeginner && heroCardsPos && (isMobile || !heroHandProps) && (
          <Box
            sx={{
              position: "absolute",
              left: heroCardsPos.left,
              top: heroCardsPos.top,
              transform: "translate(-50%, -50%)",
              display: "flex",
              gap: 0.45,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {(heroHoleCards || heroHandProps?.selectedHand || [null, null]).map((card, i) => {
              const suitCode = card ? card[1] : null;
              return (
                <Box
                  key={`hero-table-card-${i}`}
                  sx={{
                    width: heroCardW,
                    height: heroCardH,
                    borderRadius: "6px",
                    backgroundColor: card ? COLORS.black : COLORS.bg,
                    border: card
                      ? `2px solid ${SUIT_COLORS[suitCode]}`
                      : `2px dashed ${COLORS.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
                  }}
                >
                  {card ? (
                    <>
                      <Typography sx={{ fontFamily: FUTURA_BOOK, fontSize: 15, fontWeight: 400, color: SUIT_COLORS[suitCode], lineHeight: 1 }}>
                        {card[0]}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: SUIT_COLORS[suitCode], lineHeight: 1 }}>
                        {SUIT_SYMBOLS[suitCode]}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ color: COLORS.border, fontSize: 14 }}>?</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {showBesideSeatActions && !readOnly && hasActions && actionPopoverSx && (
          <Box
            sx={{
              position: "absolute",
              ...actionPopoverSx,
              zIndex: 8,
              width: optBoxW,
              p: optPad,
              borderRadius: `${optRadius}px`,
              border: `2px solid ${COLORS.white}`,
              backgroundColor: "rgba(16,18,20,0.96)",
              boxShadow: "0 8px 22px rgba(0,0,0,0.45)",
              display: "flex",
              flexDirection: "column",
              gap: `${optGap}px`,
              fontFamily: FUTURA_BOOK,
            }}
          >
            <Typography sx={{ color: COLORS.white, fontSize: optFs, fontWeight: 400, textAlign: "center", fontFamily: FUTURA_BOOK, lineHeight: 1.15 }}>
              {panelTitle || "Act"}
            </Typography>
            {panelActions.map((act, actIdx) => {
              const fill = getActionColor(act.label || act.id, actIdx, actionList.length, {
                isPreflop,
                preflopFoldRed: true,
                actionList,
              });
              return (
                <Button
                  key={act.id}
                  onClick={() => act.enabled && act.onClick?.()}
                  disabled={!act.enabled}
                  sx={{
                    minHeight: optBtnMinH,
                    fontSize: optFs,
                    fontWeight: 400,
                    textTransform: "none",
                    fontFamily: FUTURA_BOOK,
                    lineHeight: 1.15,
                    px: 1,
                    backgroundColor: act.enabled ? fill : "rgba(255,255,255,0.04)",
                    color: act.enabled ? "#111" : COLORS.border,
                    border: `1px solid ${act.enabled ? fill : "rgba(180,180,180,0.35)"}`,
                    borderRadius: "4px",
                    "&:hover": act.enabled
                      ? { backgroundColor: fill, filter: "brightness(1.08)", borderColor: fill }
                      : {},
                  }}
                >
                  {act.label}
                </Button>
              );
            })}
          </Box>
        )}

        {showBesideSeatActions && !readOnly && !hasActions && activeSeat && actionPopoverSx && (
          <Box
            sx={{
              position: "absolute",
              ...actionPopoverSx,
              zIndex: 8,
              px: Math.max(1 * optInvScale, 1),
              py: Math.max(0.6 * optInvScale, 0.6),
              borderRadius: `${optRadius}px`,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: "rgba(16,18,20,0.92)",
            }}
          >
            <Typography sx={{ color: COLORS.textMuted, fontSize: optFs, fontWeight: 400, fontFamily: FUTURA_BOOK, lineHeight: 1.15 }}>
              {panelEmptyText || "-"}
            </Typography>
          </Box>
        )}
        </Box>
      </Box>

      {/* Phone: cards + compact randomizer under the table, full width with actions. */}
      {isBeginner && isMobile && heroHandProps && (
        <Box
          ref={handSelectorRef}
          sx={{
            display: "flex",
            justifyContent: "stretch",
            width: "100%",
            px: 0.5,
            mt: 0.25,
          }}
        >
          <HandSelector
            {...heroHandProps}
            variant="tableInline"
            isMobile={isMobile}
            compactUnderTable
            fullWidthRow
            randomizerActive={heroIsActive}
          />
        </Box>
      )}

      {showHeroActionBar && (
        <Box
          ref={heroActionsRef}
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: { xs: 0.45, sm: 0.85 },
            justifyContent: isMobile ? "stretch" : "center",
            alignItems: "stretch",
            width: "100%",
            px: 0.5,
            fontFamily: FUTURA_BOOK,
            opacity: heroActionsLive ? 1 : 0.55,
            flexShrink: 0,
          }}
        >
          {(heroActionsLive ? panelActions : heroActionPlaceholders).map((act, actIdx) => {
            const fill = heroActionsLive
              ? getActionColor(act.label || act.id, actIdx, actionList.length, {
                isPreflop,
                preflopFoldRed: true,
                actionList,
              })
              : "rgba(255,255,255,0.06)";
            const enabled = heroActionsLive && !!act.enabled;
            return (
              <Button
                key={act.id}
                variant="contained"
                onClick={() => enabled && act.onClick?.()}
                disabled={!enabled}
                sx={{
                  textTransform: "none",
                  flex: "1 1 0",
                  width: "auto",
                  minWidth: 0,
                  maxWidth: isMobile ? "none" : { xs: "none", sm: 180 },
                  minHeight: isMobile ? { xs: 40, sm: 48 } : { xs: 36, sm: 44 },
                  px: isMobile ? { xs: 0.8, sm: 1.6 } : { xs: 0.6, sm: 1.6 },
                  py: isMobile ? { xs: 0.45, sm: 0.55 } : { xs: 0.35, sm: 0.55 },
                  fontSize: isMobile
                    ? { xs: `${ACTION_OPTION_FS}px`, sm: `${ACTION_OPTION_FS + 1}px` }
                    : { xs: 12, sm: 15 },
                  fontWeight: 400,
                  borderRadius: "10px",
                  fontFamily: FUTURA_BOOK,
                  backgroundColor: enabled ? fill : "rgba(255,255,255,0.06)",
                  color: enabled ? "#111" : COLORS.textMuted,
                  border: `1px solid ${enabled ? fill : "rgba(180,180,180,0.28)"}`,
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(255,255,255,0.06)",
                    color: COLORS.textMuted,
                    border: "1px solid rgba(180,180,180,0.28)",
                    opacity: 1,
                  },
                  "&:hover": enabled
                    ? { backgroundColor: fill, filter: "brightness(1.05)", boxShadow: "none" }
                    : {},
                }}
              >
                {act.label}
              </Button>
            );
          })}
        </Box>
      )}

      {/* Input panel (Back + actions); hidden in read-only / beginner beside-seat mode */}
      {!readOnly && !hideSidePanel && !actionsBesideActiveSeat && (
        <>
          <Box
            component="button"
            type="button"
            onClick={() => canGoBack && onBack?.()}
            disabled={!canGoBack}
            sx={{
              width: actionTileW,
              minWidth: actionTileW,
              height: "100%",
              borderRadius: "8px",
              border: `1px solid ${canGoBack ? BACK_BLUE : "rgba(76,141,255,0.25)"}`,
              backgroundColor: "transparent",
              p: 0.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.25,
              cursor: canGoBack ? "pointer" : "default",
              opacity: canGoBack ? 1 : 0.45,
              fontFamily: "inherit",
              "&:hover": canGoBack
                ? { backgroundColor: "rgba(76,141,255,0.08)", borderColor: BACK_BLUE }
                : {},
            }}
          >
            <Typography sx={{ color: BACK_BLUE, fontSize: isMobile ? 14 : 12, fontWeight: 800, lineHeight: 1 }}>
              ←
            </Typography>
            <Typography sx={{ color: BACK_BLUE, fontSize: isMobile ? 12 : 10, fontWeight: 800, lineHeight: 1.1 }}>
              Back
            </Typography>
          </Box>

          <Box
            sx={{
              width: actionTileW,
              minWidth: actionTileW,
              height: "100%",
              borderRadius: "8px",
              border: hasActions ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.border}`,
              backgroundColor: "transparent",
              p: 0.65,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography sx={{ color: COLORS.white, fontSize: `${ACTION_OPTION_FS}px`, fontWeight: 800, textAlign: "center", mb: 0.4, lineHeight: 1.15 }}>
              {panelTitle || "-"}
            </Typography>
            {hasActions ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, minHeight: 0 }}>
                {panelActions.map((act, actIdx) => {
                  const fill = getActionColor(act.label || act.id, actIdx, actionList.length, {
                    isPreflop,
                    preflopFoldRed: true,
                    actionList,
                  });
                  return (
                  <Button
                    key={act.id}
                    onClick={() => act.enabled && act.onClick?.()}
                    disabled={!act.enabled}
                    sx={{
                      flex: 1,
                      minHeight: Math.max(30, Math.round(ACTION_OPTION_FS * 1.85)),
                      minWidth: 0,
                      width: "100%",
                      fontSize: `${ACTION_OPTION_FS}px`,
                      fontWeight: 800,
                      textTransform: "none",
                      lineHeight: 1.15,
                      backgroundColor: act.enabled ? fill : "rgba(255,255,255,0.04)",
                      color: act.enabled ? "#111" : COLORS.border,
                      border: `1px solid ${act.enabled ? fill : "rgba(180,180,180,0.35)"}`,
                      borderRadius: "4px",
                      p: "4px 6px",
                      "&:hover": act.enabled
                        ? { backgroundColor: fill, filter: "brightness(1.08)", borderColor: fill }
                        : {},
                      "&.Mui-disabled": { color: COLORS.border },
                    }}
                  >
                    {act.label}
                  </Button>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
                <Typography sx={{ color: COLORS.textMuted, fontSize: `${ACTION_OPTION_FS}px`, textAlign: "center", px: 0.5, lineHeight: 1.15 }}>
                  {panelEmptyText || "-"}
                </Typography>
                {showReturnToCurrent && (
                  <Button
                    size="small"
                    onClick={() => onReturnToCurrent?.()}
                    sx={{ mt: 0.5, textTransform: "none", fontSize: `${ACTION_OPTION_FS}px`, color: COLORS.silver, border: `1px dashed ${COLORS.silver}`, py: 0.35 }}
                  >
                    ↩ Current
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

/** Preflop-only adapter kept for tiles-row internal table fallback. */
const PreflopTableView = ({
  decisionBoxes,
  activeBoxIndex,
  activePosition,
  getActionsForBox,
  onAction,
  onNavigate,
  onReturnToFrontier,
  isReviewing,
  selectedIndex,
  chainCurrentPosition,
  isMobile = false,
}) => {
  const activeBox = activeBoxIndex >= 0 ? decisionBoxes[activeBoxIndex] : null;
  const activeActions = (activeBox ? getActionsForBox(activeBox, activeBoxIndex) : []).map((act) => ({
    ...act,
    onClick: () => onAction?.(act.id, act.size),
  }));
  const latestBySeat = React.useMemo(() => {
    const map = {};
    for (const box of decisionBoxes || []) {
      const pos = String(box?.position || "").toLowerCase();
      if (!pos) continue;
      map[pos] = box;
    }
    return map;
  }, [decisionBoxes]);

  const seats = POSITIONS.map((pos) => {
    const box = latestBySeat[pos.id];
    const isActiveSeat = activePosition === pos.id && (!isReviewing || (activeBox && activeBox.position === pos.id));
    const isSelectedSeat = isReviewing && selectedIndex >= 0 && decisionBoxes[selectedIndex]?.position === pos.id;
    const folded = box?.action === "fold";
    const actionLabel = box?.action ? getActionDisplayName(box.action, box.raiseSize) : null;
    const clickable = !!(box?.action != null || pos.id === chainCurrentPosition);
    const navIdx = (() => {
      if (!clickable) return -1;
      const openIdx = (decisionBoxes || []).findIndex((b) => b.position === pos.id && b.action === null);
      if (openIdx >= 0) return openIdx;
      for (let i = (decisionBoxes || []).length - 1; i >= 0; i--) {
        if (decisionBoxes[i].position === pos.id) return i;
      }
      return -1;
    })();
    return {
      id: pos.id,
      name: pos.name,
      actionLabel,
      folded,
      isActive: isActiveSeat,
      isSelected: isSelectedSeat,
      clickable,
      onClick: () => navIdx >= 0 && onNavigate?.("preflop", navIdx),
    };
  });

  return (
    <HandTableView
      seats={seats}
      panelTitle={activePosition ? (POSITIONS.find((p) => p.id === activePosition)?.name || activePosition.toUpperCase()) : "-"}
      panelActions={activeActions}
      panelEmptyText={preflopCompleteLabel(isReviewing)}
      showReturnToCurrent={isReviewing}
      onReturnToCurrent={() => onReturnToFrontier?.("preflop")}
      canGoBack={false}
      isMobile={isMobile}
      isPreflop
    />
  );
};

/**
 * Single table for the whole hand when Table mode is on.
 * Preflop uses seat decision boxes; postflop highlights OOP/IP only.
 */
const WholeHandTable = ({
  viewingStreet,
  preflopHistory,
  preflopState,
  preflopViewState,
  selectedNavigation,
  selectorActions,
  onPreflopAction,
  onPostflopAction,
  onNavigate,
  onReturnToFrontier,
  onBack,
  disablePreflopActions = false,
  player = 0,
  positions = null,
  flop = [],
  turn = null,
  river = null,
  flopActionHistory = [],
  turnActionHistory = [],
  riverActionHistory = [],
  displayStreetPot = {},
  isPostflopActive = false,
  isWaitingForGrid = false,
  needsTurnCard = false,
  needsRiverCard = false,
  isMobile = false,
  readOnly = false,
  onHintClick = null,
  // Beginner table-first mode
  beginnerMode = false,
  heroSeatId = "btn",
  heroHoleCards = null,
  heroHandProps = null,
  handSelectorRef = null,
  heroActionsRef = null,
  onHeroSeatChange = null,
  onReset = null,
  actionAllowlist = null,
}) => {
  const isPreflop = viewingStreet === "preflop";
  // Beginner table mode: no reviewing / going back; always play the live frontier.
  const isReviewing = !beginnerMode && selectedNavigation?.street === viewingStreet && selectedNavigation?.index >= 0;

  // ── Preflop derivation (mirrors PreflopActionsRow) ──
  const renderState = (!beginnerMode && selectedNavigation?.street === "preflop" && selectedNavigation.index >= 0)
    ? preflopViewState
    : preflopState;
  const {
    position: activePosition,
    canFold,
    canCall,
    canRaise,
    canAllin,
    raiseLabel,
    raiseName,
    raiseCount: renderRaiseCount,
    lastRaiser: renderLastRaiser,
  } = renderState || {};
  const chainCurrentPosition = preflopState?.position;
  const allDecisionBoxes = preflopState?.decisionBoxes || [];
  const preflopReviewing = !beginnerMode && selectedNavigation?.street === "preflop" && selectedNavigation.index >= 0;
  const decisionBoxes = preflopReviewing
    ? allDecisionBoxes.slice(0, selectedNavigation.index + 1)
    : allDecisionBoxes;

  let activeBoxIndex = -1;
  if (preflopReviewing) {
    activeBoxIndex = selectedNavigation.index;
  } else {
    for (let i = decisionBoxes.length - 1; i >= 0; i--) {
      const b = decisionBoxes[i];
      if (b.position === activePosition && b.action === null) {
        activeBoxIndex = i;
        break;
      }
    }
  }
  const activeBox = activeBoxIndex >= 0 ? decisionBoxes[activeBoxIndex] : null;

  const getPreflopActions = () => {
    if (!isPreflop || !activeBox) return [];
    const isActiveBox = preflopReviewing
      ? activeBoxIndex === selectedNavigation.index
      : (activeBox.position === activePosition && activeBox.action === null);
    if (!isActiveBox || disablePreflopActions) return [];
    const base = [
      { id: "fold", label: "Fold", enabled: canFold },
      { id: "call", label: "Call", enabled: canCall },
      {
        id: raiseName || "raise",
        label: raiseLabel || "Raise",
        enabled: canRaise,
        size: getRaiseSize(activeBox.position, renderRaiseCount || 0, { lastRaiser: renderLastRaiser }),
      },
      { id: "allin", label: "All in", enabled: canAllin },
    ];
    // Tutorial: keep the full action set visible and grey non-allowed options.
    const keepAllForTutorial = Array.isArray(actionAllowlist);
    if (!selectorActions || selectorActions.length === 0) {
      const mapped = base
        .filter((a) => keepAllForTutorial || a.enabled)
        .map((a) => ({
          ...a,
          onClick: () => onPreflopAction?.(a.id, a.size),
        }));
      return filterTutorialActions(mapped, actionAllowlist);
    }
    const allowed = new Set(selectorActions.map((a) => (a || "").toLowerCase()));
    const isAllowed = (act) => {
      const l = (act.label || "").toLowerCase();
      if (l.includes("fold")) return true;
      if (l.includes("call") || l.includes("check")) return allowed.has("call") || allowed.has("check");
      if (l.includes("all-in") || l.includes("allin") || l.includes("all in")) {
        return Array.from(allowed).some((x) => x.includes("all-in") || x.includes("allin") || x.includes("all in"));
      }
      return Array.from(allowed).some((x) => x.includes("open") || x.includes("bet") || x.includes("raise"));
    };
    const mapped = base
      .filter((a) => keepAllForTutorial || (a.enabled && isAllowed(a)))
      .map((a) => ({
        ...a,
        // When tutorial keeps all buttons, still respect spot legality before allowlist greying.
        enabled: keepAllForTutorial ? !!a.enabled && isAllowed(a) : a.enabled,
        onClick: () => onPreflopAction?.(a.id, a.size),
      }));
    return filterTutorialActions(mapped, actionAllowlist);
  };

  const latestPreflopBySeat = React.useMemo(() => {
    const map = {};
    for (const box of decisionBoxes || []) {
      const pos = String(box?.position || "").toLowerCase();
      if (!pos) continue;
      map[pos] = box;
    }
    return map;
  }, [decisionBoxes]);

  // ── Postflop derivation ──
  const oopSeatId = seatIdFromSpotCode(positions?.oop);
  const ipSeatId = seatIdFromSpotCode(positions?.ip);
  const streetHist =
    viewingStreet === "flop" ? flopActionHistory
      : viewingStreet === "turn" ? turnActionHistory
        : viewingStreet === "river" ? riverActionHistory
          : [];
  const postflopReviewIdx = selectedNavigation?.street === viewingStreet ? selectedNavigation.index : -1;
  const visibleStreetHist = postflopReviewIdx >= 0
    ? streetHist.slice(0, postflopReviewIdx + 1)
    : streetHist;
  const reviewedEntry = postflopReviewIdx >= 0 ? streetHist[postflopReviewIdx] : null;
  const actingPlayer = isReviewing && reviewedEntry
    ? reviewedEntry.player
    : player;
  const activePostflopSeatId = actingPlayer === 0 ? oopSeatId : ipSeatId;

  const postflopOrdered = React.useMemo(() => {
    const order = getRenderIndexOrder(selectorActions || [], (selectorActions || []).length, { isPreflop: false });
    return order.map((i) => (selectorActions || [])[i]);
  }, [selectorActions]);

  const getPostflopActions = () => {
    if (isPreflop) return [];
    if (isWaitingForGrid || needsTurnCard || needsRiverCard) return [];
    if (!isPostflopActive && !isReviewing) return [];
    const mapped = postflopOrdered.map((action) => {
      const id = String(action).toLowerCase().replace(/\s+/g, "");
      return {
        id,
        label: formatPostflopActionLabel(action, false),
        enabled: true,
        onClick: () => onPostflopAction?.(action, isReviewing ? (reviewedEntry?.player ?? player) : player),
      };
    });
    return filterTutorialActions(mapped, actionAllowlist);
  };

  const lastPostflopLabelForSeat = (seatId) => {
    const p = seatId === oopSeatId ? 0 : seatId === ipSeatId ? 1 : -1;
    if (p < 0) return null;
    for (let i = visibleStreetHist.length - 1; i >= 0; i--) {
      if (visibleStreetHist[i]?.player === p) {
        return formatPostflopActionLabel(visibleStreetHist[i].action, false);
      }
    }
    return null;
  };

  const findPostflopNavForSeat = (seatId) => {
    const p = seatId === oopSeatId ? 0 : seatId === ipSeatId ? 1 : -1;
    if (p < 0) return null;
    // Prefer current street history; else walk earlier streets.
    const streets = [
      { street: viewingStreet, hist: streetHist },
      ...(viewingStreet === "river" ? [{ street: "turn", hist: turnActionHistory }, { street: "flop", hist: flopActionHistory }] : []),
      ...(viewingStreet === "turn" ? [{ street: "flop", hist: flopActionHistory }] : []),
    ];
    // Deduplicate viewing street first entry
    const seen = new Set();
    for (const { street, hist } of streets) {
      if (!street || street === "preflop" || seen.has(street)) continue;
      seen.add(street);
      for (let i = (hist || []).length - 1; i >= 0; i--) {
        if (hist[i]?.player === p) return { street, index: i };
      }
    }
    return null;
  };

  const seats = POSITIONS.map((pos) => {
    const pfBox = latestPreflopBySeat[pos.id];
    const folded = pfBox?.action === "fold";
    const isHero = String(heroSeatId || "").toLowerCase() === pos.id;
    if (isPreflop) {
      const isActiveSeat = activePosition === pos.id && (!preflopReviewing || (activeBox && activeBox.position === pos.id));
      const isSelectedSeat = preflopReviewing && decisionBoxes[selectedNavigation.index]?.position === pos.id;
      const actionLabel = pfBox?.action ? getActionDisplayName(pfBox.action, pfBox.raiseSize) : null;
      const clickable = beginnerMode ? false : !!(pfBox?.action != null || pos.id === chainCurrentPosition);
      const navIdx = (() => {
        if (!clickable) return -1;
        const openIdx = decisionBoxes.findIndex((b) => b.position === pos.id && b.action === null);
        if (openIdx >= 0) return openIdx;
        for (let i = decisionBoxes.length - 1; i >= 0; i--) {
          if (decisionBoxes[i].position === pos.id) return i;
        }
        return -1;
      })();
      return {
        id: pos.id,
        name: pos.name,
        actionLabel,
        folded,
        isActive: isActiveSeat,
        isSelected: isSelectedSeat,
        isHero,
        holeCards: isHero ? heroHoleCards : null,
        clickable: readOnly ? false : clickable,
        onClick: () => !readOnly && !beginnerMode && navIdx >= 0 && onNavigate?.("preflop", navIdx),
      };
    }

    // Postflop: only OOP/IP are in; others stay folded/dimmed.
    const inHand = pos.id === oopSeatId || pos.id === ipSeatId;
    const isActiveSeat = inHand && pos.id === activePostflopSeatId && (isPostflopActive || isReviewing);
    const isSelectedSeat = isReviewing && inHand && (
      (reviewedEntry?.player === 0 && pos.id === oopSeatId) ||
      (reviewedEntry?.player === 1 && pos.id === ipSeatId)
    );
    const actionLabel = inHand ? lastPostflopLabelForSeat(pos.id) : null;
    const nav = !beginnerMode && inHand && !folded ? findPostflopNavForSeat(pos.id) : null;
    return {
      id: pos.id,
      name: pos.name,
      actionLabel: inHand ? actionLabel : null,
      folded: folded || !inHand,
      dimmed: !inHand,
      isActive: isActiveSeat,
      isSelected: isSelectedSeat,
      isHero,
      holeCards: isHero ? heroHoleCards : null,
      clickable: readOnly ? false : !!nav,
      onClick: () => !readOnly && !beginnerMode && nav && onNavigate?.(nav.street, nav.index),
    };
  });

  const seatEdgeById = React.useMemo(() => {
    if (!beginnerMode) return null;
    const map = {};
    for (const pos of POSITIONS) {
      map[pos.id] = visualEdgeForHeroRotatedSeat(pos.id, heroSeatId);
    }
    return map;
  }, [beginnerMode, heroSeatId]);

  const panelActions = isPreflop ? getPreflopActions() : getPostflopActions();
  const panelTitle = isPreflop
    ? (activePosition ? (POSITIONS.find((p) => p.id === activePosition)?.name || activePosition.toUpperCase()) : "-")
    : (activePostflopSeatId
      ? (POSITIONS.find((p) => p.id === activePostflopSeatId)?.name || (actingPlayer === 0 ? "OOP" : "IP"))
      : "-");

  let panelEmptyText = "-";
  if (isPreflop) {
    panelEmptyText = preflopCompleteLabel(preflopReviewing);
  } else if (needsTurnCard) {
    panelEmptyText = "Select Turn";
  } else if (needsRiverCard) {
    panelEmptyText = "Select River";
  } else if (isWaitingForGrid) {
    panelEmptyText = "Waiting";
  } else if (isReviewing) {
    panelEmptyText = "Reviewing";
  } else {
    panelEmptyText = "Waiting";
  }

  const boardCards = [...(flop || []), ...(turn ? [turn] : []), ...(river ? [river] : [])];
  const streetStartPotValue =
    viewingStreet === "flop" ? displayStreetPot?.flop
      : viewingStreet === "turn" ? displayStreetPot?.turn
        : viewingStreet === "river" ? displayStreetPot?.river
          : null;

  const committedBbBySeat = React.useMemo(() => {
    if (!beginnerMode) return null;
    if (isPreflop) {
      const committed = getPreflopCommittedBySeat(preflopHistory);
      const out = {};
      Object.entries(committed).forEach(([seatId, bb]) => {
        if (Number(bb) > 0) out[seatId] = Number(bb);
      });
      return out;
    }
    const halfBySeat = getPostflopStreetCommittedHalfBb(streetHist, oopSeatId, ipSeatId);
    const out = {};
    Object.entries(halfBySeat).forEach(([seatId, halfBb]) => {
      const bb = Number(halfBb) / 2;
      if (bb > 0) out[seatId] = bb;
    });
    return out;
  }, [beginnerMode, isPreflop, preflopHistory, streetHist, oopSeatId, ipSeatId]);

  const { pot, potLabel, potBbForChips } = React.useMemo(() => {
    if (isPreflop) {
      const committed = getPreflopCommittedBySeat(preflopHistory);
      const potBb = Object.values(committed).reduce((sum, v) => sum + (Number(v) || 0), 0);
      const label = potBb > 0.5 ? `${Number.isInteger(potBb) ? potBb : potBb.toFixed(1).replace(/\.0$/, "")}bb` : null;
      return { pot: null, potLabel: label, potBbForChips: potBb };
    }
    const livePot = computeStreetEndPot(streetStartPotValue, streetHist);
    return {
      pot: Number.isFinite(Number(livePot)) ? livePot : streetStartPotValue,
      potLabel: null,
      potBbForChips: Number.isFinite(Number(livePot)) ? Number(livePot) / 2
        : (Number.isFinite(Number(streetStartPotValue)) ? Number(streetStartPotValue) / 2 : null),
    };
  }, [isPreflop, preflopHistory, streetStartPotValue, streetHist]);

  const canGoBack =
    !beginnerMode && (
      (preflopHistory || []).length > 0
      || (flopActionHistory || []).length > 0
      || (turnActionHistory || []).length > 0
      || (riverActionHistory || []).length > 0
      || isReviewing
      || preflopReviewing
    );

  return (
    <HandTableView
      seats={seats}
      panelTitle={panelTitle}
      panelActions={readOnly ? [] : panelActions}
      panelEmptyText={panelEmptyText}
      showReturnToCurrent={!beginnerMode && !readOnly && (isReviewing || preflopReviewing)}
      onReturnToCurrent={() => onReturnToFrontier?.(viewingStreet)}
      onBack={beginnerMode ? undefined : onBack}
      canGoBack={!readOnly && canGoBack}
      boardCards={!isPreflop || boardCards.length ? boardCards : []}
      pot={!isPreflop ? pot : null}
      potLabel={beginnerMode ? potLabel : null}
      potBbForChips={beginnerMode ? potBbForChips : null}
      streetLabel={!isPreflop ? viewingStreet : ""}
      isMobile={isMobile}
      isPreflop={isPreflop}
      readOnly={readOnly}
      onHintClick={onHintClick}
      variant={beginnerMode ? "beginner" : "compact"}
      heroSeatId={heroSeatId}
      seatEdgeById={seatEdgeById}
      hideSidePanel={beginnerMode}
      actionsBesideActiveSeat={beginnerMode}
      committedBbBySeat={committedBbBySeat}
      heroHandProps={beginnerMode ? heroHandProps : null}
      handSelectorRef={beginnerMode ? handSelectorRef : null}
      heroActionsRef={beginnerMode ? heroActionsRef : null}
      onHeroSeatChange={beginnerMode ? onHeroSeatChange : null}
      heroSeatOptions={HERO_SEAT_OPTIONS}
      onReset={beginnerMode ? onReset : null}
    />
  );
};

const EXPLAINER_VIDEO_URL = "https://www.youtube.com/watch?v=-qvm_2ImrbU";
const YOUTUBE_RED = "#E88A8A";
const EXPLAINER_PURPLE = "#B57BFF";

const TutorialChromeButtons = ({ onTutorial, layoutMode = "table", onLayoutModeChange, onReset = null }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: { xs: 0.35, sm: 0.5 },
      minWidth: 0,
      flexShrink: 0,
      width: typeof onReset === "function" ? "100%" : undefined,
    }}
  >
    <Button
      size="small"
      onClick={onTutorial}
      sx={{
        minWidth: "unset",
        minHeight: 24,
        height: 24,
        px: { xs: 1, sm: 1.4 },
        py: 0,
        mx: 0,
        textTransform: "none",
        fontSize: "11px",
        fontWeight: 400,
        fontFamily: FUTURA_BOOK,
        color: COLORS.green,
        border: `1px solid ${COLORS.green}`,
        borderRadius: "999px",
        lineHeight: 1,
        flexShrink: 0,
        "&:hover": { backgroundColor: "rgba(86,236,207,0.12)" },
      }}
    >
      Tutorial
    </Button>
    <Button
      size="small"
      component="a"
      href={EXPLAINER_VIDEO_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        minWidth: "unset",
        minHeight: 24,
        height: 24,
        px: { xs: 1, sm: 1.4 },
        py: 0,
        mx: 0,
        textTransform: "none",
        fontSize: "11px",
        fontWeight: 400,
        fontFamily: FUTURA_BOOK,
        color: EXPLAINER_PURPLE,
        border: `1px solid ${EXPLAINER_PURPLE}`,
        borderRadius: "999px",
        lineHeight: 1,
        textDecoration: "none",
        flexShrink: 0,
        "&:hover": { backgroundColor: "rgba(181,123,255,0.16)" },
      }}
    >
      Explainer{/* shorter on phone so chrome fits one row */}
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}> Video</Box>
    </Button>
    {typeof onLayoutModeChange === "function" && (
      <Box
        sx={{
          ml: { xs: 0.25, sm: 0.5 },
          display: "inline-flex",
          alignItems: "center",
          gap: 0.55,
          userSelect: "none",
          height: 24,
          fontFamily: FUTURA_BOOK,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 400, letterSpacing: "0.02em", fontFamily: FUTURA_BOOK, lineHeight: 1 }}>
          Table
        </Typography>
        <Box
          role="switch"
          aria-checked={layoutMode === "tiles"}
          onClick={() => onLayoutModeChange(layoutMode === "table" ? "tiles" : "table")}
          sx={{
            width: 42,
            height: 24,
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.85)",
            backgroundColor: "rgba(255,255,255,0.14)",
            position: "relative",
            cursor: "pointer",
            flexShrink: 0,
            boxSizing: "border-box",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 2,
              left: layoutMode === "tiles" ? 20 : 2,
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
              transition: "left 160ms ease",
            }}
          />
        </Box>
        <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 400, letterSpacing: "0.02em", fontFamily: FUTURA_BOOK, lineHeight: 1 }}>
          Tiles
        </Typography>
      </Box>
    )}
    {typeof onReset === "function" && (
      <Button
        size="small"
        onClick={onReset}
        sx={{
          minWidth: "unset",
          minHeight: 24,
          height: 24,
          px: { xs: 1, sm: 1.4 },
          py: 0,
          mx: 0,
          ml: "auto",
          textTransform: "none",
          fontSize: "11px",
          fontWeight: 400,
          fontFamily: FUTURA_BOOK,
          color: YOUTUBE_RED,
          border: `1px solid ${YOUTUBE_RED}`,
          borderRadius: "999px",
          lineHeight: 1,
          flexShrink: 0,
          "&:hover": { backgroundColor: "rgba(232,138,138,0.16)" },
        }}
      >
        Reset
      </Button>
    )}
  </Box>
);

/** Normalize spot seat codes (LO/HI/…) to full table labels (Lojack/Hijack/…). */
function fullTableSeatLabel(seatCode) {
  if (!seatCode && seatCode !== 0) return null;
  const key = String(seatCode).toLowerCase();
  const id = key === "lo" ? "lj" : key === "hi" ? "hj" : key;
  if (POSITION_NAME_FULL[id]) return POSITION_NAME_FULL[id];
  if (id === "oop") return "Out of Position";
  if (id === "ip") return "In Position";
  return String(seatCode);
}

const STRATEGY_VIEW_TOGGLE_SX = {
  backgroundColor: COLORS.bg,
  borderRadius: "4px",
  fontFamily: FUTURA_BOOK,
  "& .MuiToggleButton-root": {
    color: COLORS.textMuted,
    borderColor: COLORS.border,
    fontFamily: FUTURA_BOOK,
    fontSize: { xs: "8px", md: "9px" },
    fontWeight: 400,
    py: { xs: 0.15, md: 0.2 },
    px: { xs: 0.55, md: 0.75 },
    minHeight: { xs: 22, md: 24 },
    lineHeight: 1.1,
    "&.Mui-selected": { backgroundColor: COLORS.cardBg, color: COLORS.silver, borderColor: COLORS.silver },
    "&.Mui-disabled": { color: COLORS.textDark, borderColor: COLORS.border, opacity: 0.45 },
  },
};

/** Strategy/EV toggle with the seat whose range/strategy the grid is currently showing. */
const StrategyViewToggle = ({ value, onChange, evDisabled = false, seatLabel = null, containerRef = null }) => (
  <Box ref={containerRef} sx={{ display: "inline-flex", alignItems: "center", gap: 0.65, fontFamily: FUTURA_BOOK }}>
    <ToggleButtonGroup value={value} exclusive onChange={onChange} size="small" sx={STRATEGY_VIEW_TOGGLE_SX}>
      <ToggleButton value="strategy">Strategy</ToggleButton>
      <ToggleButton value="ev" disabled={evDisabled}>EV</ToggleButton>
    </ToggleButtonGroup>
    {seatLabel ? (
      <Typography
        sx={{
          color: COLORS.silver,
          fontSize: { xs: "10px", md: "11px" },
          fontWeight: 400,
          lineHeight: 1,
          whiteSpace: "nowrap",
          fontFamily: FUTURA_BOOK,
        }}
      >
        {seatLabel}
      </Typography>
    ) : null}
  </Box>
);

const PREFLOP_MUST_FOLD_OVERLAY =
  "Two players have entered the hand, this player must fold for PokerMoose to provide postflop solutions - press fold to continue";

function isBlankPreflopStrategyGrid(grid) {
  if (!grid) return true;
  for (const row of grid) {
    for (const cell of row || []) {
      if (cell?.inRange) return false;
    }
  }
  return true;
}

function preflopCompleteLabel(isReviewing) {
  return isReviewing ? "Reviewing" : "Preflop done";
}

const PreflopActionsRow = ({ actionHistory, preflopState, viewState, selectorActions, onAction, onNavigate, onReturnToFrontier, selectedIndex, disableActions = false, isMobile = false, demoFxEnabled = false, flashSignal = 0 }) => {
  // While reviewing (selectedIndex >= 0), viewState reflects the position BEFORE that box's
  // action -- i.e. exactly the decision being reviewed, so it (and only it) becomes editable.
  const isReviewing = selectedIndex >= 0;
  const renderState = isReviewing ? viewState : preflopState;
  const { position: activePosition, canFold, canCall, canRaise, canAllin, raiseLabel, raiseName, raiseCount: renderRaiseCount, lastRaiser: renderLastRaiser } = renderState || {};
  const chainCurrentPosition = preflopState?.position;
  const { decisionBoxes: allDecisionBoxes } = preflopState;
  // Boxes after the one being reviewed belong to a branch that gets replaced the moment
  // a different action is chosen, so they're hidden until you return to the live frontier.
  const decisionBoxes = isReviewing ? allDecisionBoxes.slice(0, selectedIndex + 1) : allDecisionBoxes;
  const preflopScrollRef = React.useRef(null);
  const TILE_GAP_PX = 4;
  const boxW = isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W;

  const getActionsForBox = (box, idx) => {
    const isActiveBox = isReviewing
      ? idx === selectedIndex
      : (box.position === activePosition && box.action === null);
    if (!isActiveBox) return [];
    if (disableActions) return [];
    const base = [
      { id: "fold", label: "Fold", enabled: canFold },
      { id: "call", label: "Call", enabled: canCall },
      {
        id: raiseName || "raise",
        label: raiseLabel || "Raise",
        enabled: canRaise,
        size: getRaiseSize(box.position, renderRaiseCount || 0, { lastRaiser: renderLastRaiser }),
      },
      { id: "allin", label: "All in", enabled: canAllin },
    ];
    if (!selectorActions || selectorActions.length === 0) return base.filter((a) => a.enabled);
    const allowed = new Set(selectorActions.map((a) => (a || "").toLowerCase()));
    const isAllowed = (act) => {
      const l = (act.label || "").toLowerCase();
      // Preflop: fold should always be offered for every seat.
      if (l.includes("fold")) return true;
      if (l.includes("call") || l.includes("check")) return allowed.has("call") || allowed.has("check");
      if (l.includes("all-in") || l.includes("allin") || l.includes("all in")) {
        return Array.from(allowed).some((x) => x.includes("all-in") || x.includes("allin") || x.includes("all in"));
      }
      return Array.from(allowed).some((x) => x.includes("open") || x.includes("bet") || x.includes("raise"));
    };
    return base.filter((a) => a.enabled && isAllowed(a));
  };

  // Only the newest unresolved box for the active seat is highlighted (or, while
  // reviewing, the exact box being reviewed).
  let activeBoxIndex = -1;
  if (isReviewing) {
    activeBoxIndex = selectedIndex;
  } else {
    for (let i = decisionBoxes.length - 1; i >= 0; i--) {
      const b = decisionBoxes[i];
      if (b.position === activePosition && b.action === null) {
        activeBoxIndex = i;
        break;
      }
    }
  }

  React.useEffect(() => {
    const el = preflopScrollRef.current;
    if (!el) return;
    // Nested scroller (mobile viewport); centre locally; desktop parent strip handles it.
    if (el.scrollWidth <= el.clientWidth + 2) return;
    const active = el.querySelector('[data-pm-active-tile="true"]');
    if (active) {
      centerChildInScroller(el, active);
      return;
    }
    const idx = Math.max(0, activeBoxIndex);
    const tileSpan = boxW + TILE_GAP_PX;
    const left = Math.max(0, idx * tileSpan - (el.clientWidth - boxW) / 2);
    el.scrollTo({ left, behavior: "smooth" });
  }, [decisionBoxes.length, activeBoxIndex, boxW, isReviewing]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, alignItems: "flex-start", minWidth: 0, width: "max-content" }}>
      <Box
        ref={preflopScrollRef}
        sx={{
          width: "max-content",
          overflowX: "visible",
          overflowY: "hidden",
          pb: 0.35,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap", width: "max-content" }}>
          {decisionBoxes.map((box, idx) => (
            <ActionBox
              key={idx}
              label={POSITION_NAME_FULL[(box.position || "").toLowerCase()] || POSITION_NAME_FULL[(box.positionName || "").toLowerCase()] || box.positionName}
              actions={getActionsForBox(box, idx)}
              selectedAction={box.action}
              raiseSize={box.raiseSize}
              onAction={(actionId, size) => onAction(actionId, size)}
              isActive={idx === activeBoxIndex}
              isFolded={box.action === "fold"}
              onClick={() => onNavigate("preflop", idx)}
              isClickable={box.action !== null || box.position === chainCurrentPosition}
              isSelected={selectedIndex === idx}
              isMobile={isMobile}
              demoFxEnabled={demoFxEnabled}
              isPreflop
              flashSignal={idx === activeBoxIndex ? flashSignal : 0}
            />
          ))}
          {isReviewing && (
            <Box
              onClick={() => onReturnToFrontier?.("preflop")}
              sx={{
                width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W,
                height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H,
                backgroundColor: "transparent",
                borderRadius: "4px",
                border: `1px dashed ${COLORS.silver}`,
                p: 0.5,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "2px",
                "&:hover": { borderColor: COLORS.white, backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "11px" : "9px", fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>↩</Typography>
              <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "11px" : "9px", fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>Current</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const PostflopActionsRow = ({ title, streetCards, cardCount, pot, solverActions, actionHistory, onAction, isActive, isWaitingForGrid = false, player, positions, onNavigate, onReturnToFrontier, street, selectedActionIndex, needsTurnCard, isLiveStreet = false, isMobile = false, demoFxEnabled = false, flashSignal = 0 }) => {
  const postflopRenderOrder = getRenderIndexOrder(solverActions, solverActions.length, { isPreflop: false });
  const orderedSolverActions = postflopRenderOrder.map((i) => solverActions[i]);
  const actionButtons = orderedSolverActions.map(action => ({ id: action.toLowerCase().replace(/\s+/g, ""), label: formatPostflopActionLabel(action, false), enabled: isActive }));
  const oopName = positions?.oop || "OOP", ipName = positions?.ip || "IP";
  const oopLabel = POSITION_NAME_FULL[String(oopName || "").toLowerCase()] || oopName;
  const ipLabel = POSITION_NAME_FULL[String(ipName || "").toLowerCase()] || ipName;
  const currentPlayer = player === 0 ? oopLabel : ipLabel;
  const TILE_GAP_PX = 4;
  const boxW = isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W;
  const boxH = isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H;
  const streetSlotCount = Math.max(1, Number(cardCount) || 0);
  const STREET_CARD_W = isMobile ? 22 : 44;
  const STREET_CARD_GAP_PX = isMobile ? 2 : 4;
  const STREET_CARD_TRAILING_SPACE_PX = isMobile ? 4 : 8;
  // Wide enough for "RIVER" + inline pot text (e.g. "Pot 22.5bb") on one line.
  const STREET_LABEL_MIN_W = isMobile ? 78 : 112;
  const streetCardsWidth =
    (streetSlotCount * STREET_CARD_W) +
    (Math.max(0, streetSlotCount - 1) * STREET_CARD_GAP_PX) +
    STREET_CARD_TRAILING_SPACE_PX;
  const BOARD_SLOT_W = Math.max(STREET_LABEL_MIN_W, streetCardsWidth);
  const postflopScrollRef = React.useRef(null);
  // Reviewing an earlier decision: the tile at that index becomes the editable one (offering
  // every option again for that same player) instead of sitting next to a duplicate live tile.
  const isReviewing = selectedActionIndex >= 0;
  const priorTiles = (isReviewing ? actionHistory.slice(0, selectedActionIndex) : actionHistory).map((action, idx) => {
    const playerName = action.player === 0 ? oopLabel : ipLabel;
    return (
      <Box key={idx} onClick={() => onNavigate(street, idx)}
        sx={{ width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W, height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H, backgroundColor: COLORS.bg, borderRadius: "4px", border: `1px solid ${COLORS.border}`,
          p: isMobile ? 0.35 : 0.5, cursor: "pointer", display: "flex", flexDirection: "column", "&:hover": { borderColor: COLORS.white } }}>
        <Typography sx={{ color: COLORS.textDark, fontSize: isMobile ? "11px" : "11px", fontWeight: 700, textAlign: "center", mb: 0.25, lineHeight: 1.1 }}>{playerName}</Typography>
        <Box sx={{ py: 0.25, backgroundColor: COLORS.cardBg, borderRadius: "2px", textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "11px" : "9px", fontWeight: 600, lineHeight: 1.1 }}>{formatPostflopActionLabel(action.action, false)}</Typography>
        </Box>
      </Box>
    );
  });
  const reviewedEntry = isReviewing ? actionHistory[selectedActionIndex] : null;
  const reviewedPlayerLabel = reviewedEntry ? (reviewedEntry.player === 0 ? oopLabel : ipLabel) : currentPlayer;
  const tileCountForScroll = isReviewing ? selectedActionIndex + 1 : actionHistory.length;

  React.useEffect(() => {
    const el = postflopScrollRef.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth + 2) return;
    const active = el.querySelector('[data-pm-active-tile="true"]');
    if (active) {
      centerChildInScroller(el, active);
      return;
    }
    const liveTileVisible = isActive && !needsTurnCard && !isWaitingForGrid;
    const targetIndex = liveTileVisible || isReviewing
      ? tileCountForScroll
      : Math.max(0, tileCountForScroll - 1);
    const tileSpan = boxW + TILE_GAP_PX;
    const left = Math.max(0, targetIndex * tileSpan - (el.clientWidth - boxW) / 2);
    el.scrollTo({ left, behavior: "smooth" });
  }, [tileCountForScroll, isActive, needsTurnCard, isWaitingForGrid, isReviewing, boxW]);

  const CurrentTile = ({ onClick }) => (
    <Box onClick={onClick}
      sx={{ width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W, height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H, backgroundColor: "transparent", borderRadius: "4px", border: `1px dashed ${COLORS.silver}`,
        p: isMobile ? 0.35 : 0.5, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", "&:hover": { borderColor: COLORS.white, backgroundColor: "rgba(255,255,255,0.04)" } }}>
      <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "12px" : "9px", fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>↩</Typography>
      <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "11px" : "9px", fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>Current</Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "max-content" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: `${TILE_GAP_PX}px`, position: "relative", isolation: "isolate" }}>
        <Box sx={{ width: `${BOARD_SLOT_W}px`, minWidth: `${BOARD_SLOT_W}px`, height: `${boxH}px`, display: "flex", justifyContent: "flex-start", overflow: "hidden", position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ color: COLORS.textMuted, fontSize: isMobile ? "8px" : "9px", fontWeight: 700, textTransform: "uppercase", lineHeight: 1.1 }}>
                {street}
              </Typography>
              {Number.isFinite(Number(pot)) && (
                <Typography sx={{ color: COLORS.white, fontSize: isMobile ? "8px" : "10px", fontWeight: 700, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                  Pot {formatPostflopHalfBb(pot)}bb
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: "auto" }}>
              <StreetCardsInline cards={streetCards} count={cardCount} isMobile={isMobile} />
            </Box>
          </Box>
        </Box>
        <Box
          ref={postflopScrollRef}
          sx={{
            width: "max-content",
            minWidth: 0,
            overflowX: "visible",
            overflowY: "hidden",
            pb: 0.35,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "nowrap",
              width: "max-content",
              "& > :first-of-type": { position: "relative", zIndex: 3 },
            }}
          >
            {priorTiles}
            {isReviewing && (
              <ActionBox label={reviewedPlayerLabel} actions={actionButtons} selectedAction={reviewedEntry?.action} raiseSize={reviewedEntry?.raiseSize}
                onAction={(actionId) => { const action = orderedSolverActions.find(a => a.toLowerCase().replace(/\s+/g, "") === actionId); if (action) onAction(action, reviewedEntry?.player ?? player); }}
                isActive={true} isFolded={false} isClickable={false} isSelected={true} isMobile={isMobile} demoFxEnabled={demoFxEnabled} flashSignal={flashSignal} />
            )}
            {!isReviewing && isActive && !needsTurnCard && !isWaitingForGrid && (
              <ActionBox label={currentPlayer} actions={actionButtons} selectedAction={null}
                onAction={(actionId) => { const action = orderedSolverActions.find(a => a.toLowerCase().replace(/\s+/g, "") === actionId); if (action) onAction(action, player); }}
                isActive={true} isFolded={false} isClickable={false} isSelected={false} isMobile={isMobile} demoFxEnabled={demoFxEnabled} flashSignal={flashSignal} />
            )}
            {!isReviewing && needsTurnCard && (
              <Box sx={{ width: isMobile ? ACTION_BOX_W_MOBILE : ACTION_BOX_W, height: isMobile ? ACTION_BOX_H_MOBILE : ACTION_BOX_H, backgroundColor: COLORS.cardBg, borderRadius: "4px", border: `2px solid ${COLORS.silver}`, p: isMobile ? 0.25 : 0.5, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: COLORS.silver, fontSize: isMobile ? "10px" : "10px", fontWeight: 600, textAlign: "center" }}>Select Turn</Typography>
              </Box>
            )}
            {!isReviewing && !isActive && !needsTurnCard && actionHistory.length === 0 && (<><PlaceholderBox label={oopLabel} isMobile={isMobile} /><PlaceholderBox label={ipLabel} isMobile={isMobile} /></>)}
            {isReviewing && (
              <CurrentTile onClick={() => onReturnToFrontier?.(street)} />
            )}
            {!isReviewing && !isActive && isLiveStreet && (
              <CurrentTile onClick={() => onReturnToFrontier?.(street)} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const CardSelectorPopup = ({ open, onClose, onSelect, title, usedCards, requiredCard = null }) => {
  if (!open) return null;
  const required = requiredCard ? String(requiredCard) : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.85)" } } }}
    >
      <Fade in={open}>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: "calc(100vw - 16px)", sm: 360 },
            maxWidth: "calc(100vw - 16px)",
            backgroundColor: COLORS.cardBg,
            borderRadius: "12px",
            p: { xs: 1, sm: 2 },
            border: `1px solid ${COLORS.border}`,
            mx: "auto",
            outline: "none",
            zIndex: 10001,
          }}
        >
          <Typography sx={{ color: COLORS.text, fontSize: { xs: "13px", sm: "12px" }, fontWeight: 600, mb: 1, textAlign: "center" }}>
            {required ? `${title}, pick ${required}` : title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "4px", sm: "2px" } }}>
            {SUITS.map((suit) => (
              <Box key={suit} sx={{ display: "grid", gridTemplateColumns: "repeat(13, minmax(0, 1fr))", gap: { xs: "3px", sm: "2px" }, width: "100%" }}>
                {RANKS.map((rank) => {
                  const card = `${rank}${suit}`;
                  const isUsed = usedCards.includes(card);
                  const blockedByTutorial = !!(required && card !== required);
                  const disabled = isUsed || blockedByTutorial;
                  return (
                    <Button key={card} onClick={() => !disabled && onSelect(card)} disabled={disabled}
                      sx={{ minWidth: 0, width: "100%", height: { xs: 48, sm: 28 }, p: 0, backgroundColor: disabled ? COLORS.border : (required && card === required ? COLORS.green : COLORS.black), border: "none", borderRadius: "3px",
                        color: disabled ? "#444" : (required && card === required ? "#111" : SUIT_COLORS[suit]), fontSize: { xs: "14px", sm: "11px" }, fontWeight: 700, opacity: disabled ? 0.3 : 1,
                        "&:hover": { backgroundColor: disabled ? COLORS.border : COLORS.cardBg }, "&.Mui-disabled": { color: "#333" } }}>{rank}</Button>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

// ─── Flop Quota Paywall Modal ───────────────────────────────────────────────
const FlopQuotaModal = ({ open, onClose, quotaInfo, onUpgrade }) => {
  if (!open) return null;
  const used = quotaInfo?.used ?? 5;
  const dailyMax = quotaInfo?.dailyMax ?? 5;

  const nowD = new Date();
  const msUntilMidnightUtc = (
    (24 - nowD.getUTCHours()) * 3600 * 1000
    - nowD.getUTCMinutes() * 60 * 1000
    - nowD.getUTCSeconds() * 1000
    - nowD.getUTCMilliseconds()
  );
  const nextResetH = Math.floor(msUntilMidnightUtc / 3600000);
  const nextResetM = Math.floor((msUntilMidnightUtc % 3600000) / 60000);
  const resetLabel = nextResetH > 0 ? `${nextResetH}h ${nextResetM}m` : `${nextResetM}m`;

  return (
    <Backdrop open={open} onClick={onClose} sx={{ zIndex: 1400, backgroundColor: "rgba(0,0,0,0.6)" }}>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 420,
          maxWidth: "92vw",
          p: 2,
          borderRadius: 2,
          backgroundColor: "#1f1f1f",
          border: "1px solid #333",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 0.2 }}>
          <img src={logoWithText} alt="PokerMoose" style={{ width: 130, maxWidth: "60%", opacity: 0.5, filter: "grayscale(1)" }} />
        </Box>

        <Typography sx={{ color: "#ffffff", fontSize: 11.5, fontWeight: 600, textAlign: "center", mb: 0.45 }}>
          Daily flop solve limit reached
        </Typography>

        <Box
          sx={{
            border: "1px solid #56ECCF",
            borderRadius: 2,
            width: "100%",
            px: 1.2,
            py: 0.75,
            textAlign: "center",
            mb: 0.45,
          }}
        >
          <Typography sx={{ color: "#d0d0d0", fontSize: 12.2, lineHeight: 1.25 }}>
            You've used <Box component="span" sx={{ color: "#ffffff", fontWeight: 700 }}>{used}/{dailyMax}</Box> free flop solves today
          </Typography>
          <Typography sx={{ color: "#d0d0d0", fontSize: 12.2, lineHeight: 1.25, mt: 0.3 }}>
            Resets in <Box component="span" sx={{ color: "#ffffff", fontWeight: 700 }}>{resetLabel}</Box>
          </Typography>
        </Box>

        <Typography sx={{ color: "#bcbcbc", fontSize: 12.1, textAlign: "center", mb: 0.9 }}>
          Unlock all Pro features free for 3 days
        </Typography>

        <Button
          variant="contained"
          onClick={() => { onClose(); if (typeof onUpgrade === "function") onUpgrade(); }}
          sx={{
            backgroundColor: "#56ECCF",
            borderColor: "#56ECCF",
            color: "#111",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            "&:hover": { backgroundColor: "rgb(45, 179, 155)", borderColor: "rgb(45, 179, 155)" },
          }}
        >
          Start pro free trial now
        </Button>

        <Button
          variant="text"
          onClick={onClose}
          sx={{ color: "#9a9a9a", textTransform: "none", fontSize: 11, alignSelf: "center", mt: 0.4 }}
        >
          Wait until tomorrow (free)
        </Button>
      </Box>
    </Backdrop>
  );
};

const FlopCardPopup = ({ open, onClose, onSelect, selectedCards, setSelectedCards, availableFlops, requiredCards = null }) => {
  const required = Array.isArray(requiredCards) && requiredCards.length === 3 ? requiredCards : null;
  const handleCardClick = (card) => {
    if (required && !required.includes(card)) return;
    if (selectedCards.includes(card)) setSelectedCards(selectedCards.filter(c => c !== card));
    else if (selectedCards.length < 3) {
      const newCards = [...selectedCards, card];
      setSelectedCards(newCards);
      // Auto-close when 3 cards are selected - suit isomorphism will handle mapping
      // SPEED IMPROVEMENT: fire immediately (was a 200ms artificial delay) so the
      // flop-map + grid requests start as soon as the 3rd card is picked.
      if (newCards.length === 3) {
        if (required) {
          const got = newCards.slice().sort().join("|");
          const want = required.slice().sort().join("|");
          if (got !== want) return;
        }
        setTimeout(() => onSelect(newCards), 0);
      }
    }
  };
  if (!open) return null;

  return (
    // Portaled Modal so table seat z-index / transforms cannot cover the picker
    <Modal
      open={open}
      onClose={onClose}
      sx={{ zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.85)" } } }}
    >
      <Fade in={open}>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: { xs: "calc(100vw - 16px)", sm: 360 },
            maxWidth: "calc(100vw - 16px)",
            backgroundColor: COLORS.cardBg,
            borderRadius: "12px",
            p: { xs: 1, sm: 2 },
            border: `1px solid ${COLORS.border}`,
            mx: "auto",
            outline: "none",
            zIndex: 10001,
          }}
        >
          <Typography sx={{ color: COLORS.text, fontSize: { xs: "13px", sm: "12px" }, fontWeight: 600, mb: 1, textAlign: "center" }}>
            {required ? `Tutorial flop: select ${required.join(" ")}` : "Select Flop (any 3 cards)"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2, justifyContent: "center" }}>
            {[0, 1, 2].map((i) => {
              const card = selectedCards[i], suitCode = card ? card[1] : null;
              return (
                <Box key={i} onClick={() => card && setSelectedCards(selectedCards.filter((_, idx) => idx !== i))}
                  sx={{ width: { xs: 34, sm: 40 }, height: { xs: 69, sm: 54 }, backgroundColor: card ? COLORS.black : COLORS.bg, border: card ? `2px solid ${SUIT_COLORS[suitCode]}` : `2px dashed ${COLORS.border}`,
                    borderRadius: "6px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: card ? "pointer" : "default" }}>
                  {card ? (<><Typography sx={{ fontSize: { xs: 18, sm: 18 }, fontWeight: 700, color: SUIT_COLORS[suitCode], lineHeight: 1 }}>{card[0]}</Typography>
                    <Typography sx={{ fontSize: { xs: 14, sm: 14 }, color: SUIT_COLORS[suitCode] }}>{SUIT_SYMBOLS[suitCode]}</Typography></>) : (<Typography sx={{ color: COLORS.border, fontSize: { xs: 18, sm: 16 } }}>?</Typography>)}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "4px", sm: "2px" } }}>
            {SUITS.map((suit) => (
              <Box key={suit} sx={{ display: "grid", gridTemplateColumns: "repeat(13, minmax(0, 1fr))", gap: { xs: "3px", sm: "2px" }, width: "100%" }}>
                {RANKS.map((rank) => {
                  const card = `${rank}${suit}`, isSelected = selectedCards.includes(card);
                  const allowed = !required || required.includes(card);
                  return (
                    <Button key={card} onClick={() => allowed && handleCardClick(card)} disabled={!allowed}
                      sx={{ minWidth: 0, width: "100%", height: { xs: 48, sm: 28 }, p: 0, backgroundColor: isSelected ? SUIT_COLORS[suit] : (!allowed ? COLORS.border : COLORS.black), border: "none", borderRadius: "3px",
                        color: isSelected ? "#000" : (!allowed ? "#444" : SUIT_COLORS[suit]), fontSize: { xs: "14px", sm: "11px" }, fontWeight: 700, opacity: allowed ? 1 : 0.28,
                        "&:hover": { backgroundColor: isSelected ? SUIT_COLORS[suit] : COLORS.border }, "&.Mui-disabled": { color: "#444" } }}>{rank}</Button>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

// Legend for the grid
const GridLegend = ({ actions, isPreflop = false, preflopFoldRed = false }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
    {actions && getRenderIndexOrder(actions, actions.length, { isPreflop }).map((idx) => {
      const action = actions[idx];
      return (
        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: getActionColor(action, idx, actions.length, { isPreflop, preflopFoldRed, actionList: actions }) }} />
          <Typography sx={{ color: COLORS.textMuted, fontSize: "8px" }}>{formatPostflopActionLabel(action, isPreflop)}</Typography>
        </Box>
      );
    })}
  </Box>
);

const HandMatrix = ({
  gridData,
  actions,
  loading: _loading,
  overlayMessage = null,
  onOverlayFold = null,
  onCellClick,
  selectedCell,
  viewMode,
  clickableAll = false,
  isPreflop = false,
  preflopFoldRed = false,
  handReminderLabel = null,
  showHandReminder = true,
  postflopCarriedRange = null,
  currentStreet = "preflop",
  demoFxEnabled = false,
}) => {
  const displayGrid = gridData || Array(13).fill(null).map((_, row) => 
    Array(13).fill(null).map((_, col) => {
      const r1 = RANKS[row], r2 = RANKS[col];
      const label = row === col ? `${r1}${r1}` : row < col ? `${r1}${r2}s` : `${r2}${r1}o`;
      return { label, inRange: false, frequencies: [], ev: 0 };
    })
  );
  
  const getActionColors = (cell) => {
    if (!cell.frequencies || cell.frequencies.length === 0) return [];
    const totalActions = actions?.length || 0;
    return cell.frequencies.map((_, idx) => getActionColor(actions?.[idx], idx, totalActions, { isPreflop, preflopFoldRed, actionList: actions }));
  };

  const getEVColor = (ev) => {
    // Positive EV = green, negative = red
    if (ev >= 10) return `rgb(30, ${Math.floor(100 + Math.min(ev / 10, 1) * 71)}, 80)`;
    if (ev <= -10) return `rgb(${Math.floor(130 + Math.min(Math.abs(ev) / 10, 1) * 100)}, 28, 42)`;
    // Middle range
    const t = (ev + 10) / 20;
    return `rgb(${Math.floor(180 - t * 50)}, ${Math.floor(100 + t * 50)}, 50)`;
  };
  const [hoveredCell, setHoveredCell] = React.useState(null);
  const getDemoWaveTransform = React.useCallback((rowIdx, colIdx) => {
    if (!demoFxEnabled || !hoveredCell) return "translateY(0px) scale(1)";
    const d = Math.abs(rowIdx - hoveredCell.row) + Math.abs(colIdx - hoveredCell.col);
    if (d === 0) return "translateY(-10px)";
    if (d === 1) return "translateY(-5px)";
    if (d === 2) return "translateY(-2.5px)";
    return "translateY(0px)";
  }, [demoFxEnabled, hoveredCell]);

  const carriedEff = rangeMapWithPositiveWeights(postflopCarriedRange);
  const cellIntersectsCarriedRange = (label) => {
    if (!carriedEff) return true;
    return generateAllCombos(label).some((h) => (carriedEff[h] || 0) > 0);
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {overlayMessage && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.55)", zIndex: 9, borderRadius: "4px", p: 2 }}>
          {onOverlayFold ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                maxWidth: "92%",
                rowGap: 0.85,
              }}
            >
              <Typography sx={{ color: COLORS.white, fontSize: "13px", fontWeight: 700, textAlign: "center", lineHeight: 1.35 }}>
                Two players have entered the hand, this player must fold for PokerMoose to provide postflop solutions - press
              </Typography>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onOverlayFold();
                }}
                sx={{
                  minHeight: 22,
                  minWidth: 52,
                  px: 1.1,
                  py: 0.25,
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "none",
                  lineHeight: 1.2,
                  backgroundColor: SOLVER_PREFS.fold,
                  color: "#111",
                  border: `1px solid ${SOLVER_PREFS.fold}`,
                  borderRadius: "3px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                  "&:hover": {
                    backgroundColor: SOLVER_PREFS.fold,
                    borderColor: SOLVER_PREFS.fold,
                    filter: "brightness(1.08)",
                  },
                }}
              >
                Fold
              </Button>
              <Typography sx={{ color: COLORS.white, fontSize: "13px", fontWeight: 700, textAlign: "center", lineHeight: 1.35 }}>
                to continue
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: COLORS.white, fontSize: "13px", fontWeight: 700, textAlign: "center", maxWidth: "92%", lineHeight: 1.35 }}>
              {overlayMessage}
            </Typography>
          )}
        </Box>
      )}
      <Box
        onMouseLeave={() => setHoveredCell(null)}
        sx={{ display: "grid", gridTemplateColumns: "repeat(13, 1fr)", gap: "1px", backgroundColor: COLORS.black, border: `1px solid ${COLORS.border}`, borderRadius: "4px", overflow: "hidden" }}
      >
        {displayGrid.map((row, rowIdx) =>
          row.map((cell, colIdx) => {
            const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx;
            const isReminder = showHandReminder && !!handReminderLabel && cell.label === handReminderLabel;
            const totalActions = actions?.length || 0;
            const normalizedFreqs = normalizeFrequenciesForDisplay(cell.frequencies, totalActions);
            const cellColors = getActionColors({ frequencies: normalizedFreqs });
            const renderOrder = getRenderIndexOrder(actions, totalActions, { isPreflop });
            const isEVView = viewMode === "ev";
            const evColor = getEVColor(cell.ev || 0);
            const inPlayerSlice =
              isPreflop || currentStreet === "preflop"
                ? cell.inRange
                : cell.inRange && cellIntersectsCarriedRange(cell.label);
            const canClick = !!onCellClick && (clickableAll || inPlayerSlice);
            
            return (
              <Box
                key={`${rowIdx}-${colIdx}`}
                onMouseEnter={() => demoFxEnabled && setHoveredCell({ row: rowIdx, col: colIdx })}
                onClick={() => canClick && onCellClick?.({ row: rowIdx, col: colIdx, cell })}
                sx={{ aspectRatio: "1", backgroundColor: inPlayerSlice ? (isEVView ? evColor : COLORS.cardBg) : COLORS.bg, position: "relative",
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: canClick ? "pointer" : "default",
                  outline: isSelected ? `2px solid ${COLORS.white}` : "none",
                  boxShadow: "none",
                  zIndex: isSelected ? 10 : 1,
                  transition: demoFxEnabled ? "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 180ms ease" : "none",
                  transform: getDemoWaveTransform(rowIdx, colIdx),
                  boxShadow: demoFxEnabled && hoveredCell && Math.abs(rowIdx - hoveredCell.row) + Math.abs(colIdx - hoveredCell.col) <= 1
                    ? "0 8px 18px rgba(0,0,0,0.35)"
                    : "none",
                }}
              >
                {isReminder && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      border: `1px solid rgba(208,208,208,0.55)`,
                      borderRadius: "1px",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  />
                )}
                {inPlayerSlice && !isEVView && normalizedFreqs.length > 0 && (<Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
                  {renderOrder.map((i) => (<Box key={i} sx={{ width: `${(normalizedFreqs[i] || 0) * 100}%`, height: "100%", backgroundColor: cellColors[i] }} />))}
                </Box>)}
                <Typography sx={{ position: "relative", zIndex: 1, color: inPlayerSlice ? "#fff" : COLORS.border, fontSize: "clamp(7px, 0.9vw, 10px)", fontWeight: 400 }}>{cell.label}</Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

// Card back component
const CardBack = () => (
  <Box sx={{ width: 12, height: 16, backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: "2px", position: "absolute" }} />
);

const ComboBreakdown = ({
  cell,
  actions,
  gridData,
  viewMode,
  usedCards,
  suitMapping,
  flopRankMap,
  activeRange,
  isPreflop = false,
  preflopFoldRed = false,
  reminderCombo = null,
  showHandReminder = true,
  desiredBoardCards = null,
  queriedBoardCards = null,
  textureResolutionMap = null,
  isMobile = false,
  loading: _loading = false,
  onComboSelect = null,
}) => {
  const displayCell = cell || (gridData ? gridData[0]?.[0] : null) || { label: "AA", combos: [], inRange: false };
  const allCombos = generateAllCombos(displayCell.label);
  const totalActions = actions?.length || 0;
  const gridCols = allCombos.length === 6 ? 3 : allCombos.length === 4 ? 2 : 4;
  const gridRows = Math.max(1, Math.ceil(allCombos.length / gridCols));
  const getComboColors = (frequencies) =>
    frequencies?.map((_, idx) => getActionColor(actions?.[idx], idx, totalActions, { isPreflop, preflopFoldRed, actionList: actions })) || [];
  const isEVView = viewMode === "ev";

  const usedSet = new Set((usedCards || []).filter(Boolean));
  const effectiveActiveRange = rangeMapWithPositiveWeights(activeRange);
  const activeComboSet = effectiveActiveRange
    ? new Set(Object.entries(effectiveActiveRange).filter(([, w]) => (w || 0) > 0).map(([h]) => h))
    : null;

  const getEVColor = (ev) => {
    if (ev >= 10) return `rgb(30, ${Math.floor(100 + Math.min(ev / 10, 1) * 71)}, 80)`;
    if (ev <= -10) return `rgb(${Math.floor(130 + Math.min(Math.abs(ev) / 10, 1) * 100)}, 28, 42)`;
    const t = (ev + 10) / 20;
    return `rgb(${Math.floor(180 - t * 50)}, ${Math.floor(100 + t * 50)}, 50)`;
  };

  return (
    <Box sx={{ width: "100%", height: "100%", minWidth: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
          alignItems: "stretch",
          alignContent: "stretch",
          gap: isMobile ? 0.6 : 0.75,
          flex: 1,
          height: "100%",
          minHeight: 0,
        }}
      >
        {allCombos.map((comboHand, idx) => {
          const card1 = comboHand.slice(0, 2), card2 = comboHand.slice(2, 4);
          const isBlockedByBoard = usedSet.has(card1) || usedSet.has(card2);
          const apiComboStr = isPreflop ? comboHand : comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap);
          const preflopComboData = isPreflop
            ? ((displayCell?.combos || []).find((c) => c.hand === comboHand) || null)
            : null;
          let comboData = null;
          let postflopKind = "none";
          let textureDonorHand = null;
          if (isPreflop) {
            comboData = preflopComboData;
          } else if (!isBlockedByBoard) {
            if (activeComboSet && !activeComboSet.has(comboHand)) {
              postflopKind = "folded";
            } else {
              // Important: selected `displayCell` may come from donor-blanked matrix data.
              // Resolve from raw `gridData` first so step-1 direct inheritance uses real API strategy.
              const resolved = resolvePostflopComboData(comboHand, {
                gridData,
                cell: displayCell,
                suitMapping,
                flopRankMap,
                desiredBoardCards,
                queriedBoardCards,
                textureResolutionMap,
              });
              comboData = resolved.comboData;
              postflopKind = resolved.kind;
              textureDonorHand = resolved.donorHand;
            }
          } else {
            postflopKind = "blocked";
          }
          const resolvedKind = isPreflop ? (preflopComboData ? "direct" : "missing") : postflopKind;
          const hasData = isPreflop
            ? !!(comboData && comboData.frequencies && comboData.frequencies.length > 0)
            : (!!(!isBlockedByBoard && comboData && comboData.frequencies && comboData.frequencies.length > 0));
          let normalizedComboFreqs = normalizeFrequenciesForDisplay(comboData?.frequencies, totalActions);
          if (hasData && resolvedKind === "texture") {
            const sum = normalizedComboFreqs.reduce((a, b) => a + (b || 0), 0);
            if (sum <= 0 && totalActions > 0) {
              const foldIdx = (actions || []).findIndex((a) => String(a || "").toLowerCase().includes("fold"));
              normalizedComboFreqs = new Array(totalActions).fill(0);
              if (foldIdx >= 0) normalizedComboFreqs[foldIdx] = 1;
              else normalizedComboFreqs[0] = 1;
            }
          }
          const comboColors = hasData ? getComboColors(normalizedComboFreqs) : [];
          const comboRenderOrder = getRenderIndexOrder(actions, totalActions, { isPreflop });
          const ev = comboData?.ev || 0;
          const isReminderCombo = showHandReminder && !!reminderCombo && comboHand === reminderCombo;
          const streetProf =
            SHOW_DEV_HAND_DEFINITION && !isPreflop && desiredBoardCards?.length >= 3
              ? textureResolutionMap?.[comboHand]?.streetProfile || null
              : null;
          
          return (
            <Box
              key={idx}
              onClick={() => onComboSelect?.(comboHand)}
              sx={{ minHeight: 0, height: "100%", position: "relative", display: "flex", flexDirection: "column", overflow: "visible",
                cursor: onComboSelect ? "pointer" : "default",
                "&:hover": onComboSelect ? { outline: `1px solid ${COLORS.border}`, outlineOffset: "-1px", borderRadius: "4px" } : {},
              "&::before": {
                content: '""', position: "absolute", inset: 0, backgroundColor: hasData ? (isEVView ? getEVColor(ev) : "transparent") : ACTION_COLORS.blocked,
                borderRadius: "4px", clipPath: "polygon(0 20%, 20% 0, 100% 0, 100% 100%, 0 100%)", zIndex: 0
              }
            }}
            >
              {isReminderCombo && (
                <Box sx={{ position: "absolute", inset: 0, border: `1px solid ${COLORS.silver}`, borderRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
              )}
              {hasData && !isEVView && (<Box sx={{ position: "absolute", inset: 0, display: "flex", clipPath: "polygon(0 20%, 20% 0, 100% 0, 100% 100%, 0 100%)", borderRadius: "4px" }}>
                {comboRenderOrder.map((i) => (<Box key={i} sx={{ width: `${(normalizedComboFreqs[i] || 0) * 100}%`, height: "100%", backgroundColor: comboColors[i] }} />))}
              </Box>)}
              
              {/* Combo cards in cutout area */}
              <Box sx={{ position: "relative", zIndex: 1, p: 0.5, backgroundColor: COLORS.bg, width: "fit-content", borderRadius: "0 0 4px 0" }}>
                <Box sx={{ display: "flex", gap: 0.25 }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: SUIT_COLORS[card1[1]] }}>{card1[0]}{SUIT_SYMBOLS[card1[1]]}</Typography>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: SUIT_COLORS[card2[1]] }}>{card2[0]}{SUIT_SYMBOLS[card2[1]]}</Typography>
                </Box>
              </Box>
              
              <Box sx={{ p: 0.5, pt: 0, position: "relative", zIndex: 1, mt: "auto" }}>
                {isEVView ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: 600 }}>{ev.toFixed(2)} BB</Typography>
                    {SHOW_DEV_COMBO_TRACE && (
                      <Typography sx={{ color: "#000", fontSize: "9px", lineHeight: 1 }}>api {apiComboStr}</Typography>
                    )}
                    {SHOW_DEV_COMBO_TRACE && textureDonorHand && (
                      <Typography sx={{ color: "#666", fontSize: "8px", lineHeight: 1 }}>via {textureDonorHand}</Typography>
                    )}
                    {SHOW_DEV_HAND_DEFINITION && streetProf && (
                      <>
                        <Typography sx={{ color: "#000", fontSize: "8px", lineHeight: 1.15 }}>{streetProf.valueLabel}</Typography>
                        <Typography sx={{ color: "#000", fontSize: "8px", lineHeight: 1.15 }}>{streetProf.drawLabel}</Typography>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    {hasData ? (
                      comboRenderOrder.map((i) => (
                        <Typography key={i} sx={{ color: "#fff", fontSize: "8px", fontWeight: 500 }}>
                          {formatPostflopActionLabel((actions?.[i] || `Action ${i + 1}`), isPreflop)} {Math.round((normalizedComboFreqs[i] || 0) * 100)}%
                        </Typography>
                      ))
                    ) : (
                      <Typography sx={{ color: "#666", fontSize: "8px", fontWeight: 500 }}>
                        {isPreflop ? "No data" : (isBlockedByBoard ? "Blocked" : (resolvedKind === "folded" ? "Folded" : "No data"))}
                      </Typography>
                    )}
                    {SHOW_DEV_COMBO_TRACE && (
                      <Typography sx={{ color: "#000", fontSize: "9px", lineHeight: 1 }}>
                        api {apiComboStr}
                      </Typography>
                    )}
                    {SHOW_DEV_COMBO_TRACE && textureDonorHand && (
                      <Typography sx={{ color: "#666", fontSize: "8px", lineHeight: 1 }}>via {textureDonorHand}</Typography>
                    )}
                    {SHOW_DEV_HAND_DEFINITION && streetProf && (
                      <>
                        <Typography sx={{ color: "#000", fontSize: "8px", lineHeight: 1.15 }}>{streetProf.valueLabel}</Typography>
                        <Typography sx={{ color: "#000", fontSize: "8px", lineHeight: 1.15 }}>{streetProf.drawLabel}</Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Hand selector for user's cards
const HandSelector = ({
  selectedHand,
  onSelectHand,
  usedCards,
  selectedCell,
  actions = [],
  isPreflop = false,
  preflopFoldRed = false,
  gridData = null,
  suitMapping = null,
  flopRankMap = null,
  desiredBoardCards = null,
  queriedBoardCards = null,
  textureResolutionMap = null,
  activeRange = null,
  variant = "panel", // "panel" | "tableInline"
  tableRef = null,
  isMobile = false,
  compactUnderTable = false, // phone table: smaller strip under the felt
  fullWidthRow = false, // stretch cards + perfect-play + tip to match action button row
  randomizerActive = true, // when false, freeze last hero roll/recommendation
  requiredHand = null, // tutorial: only these two cards may be selected
}) => {
  const isTable = variant === "tableInline";
  const under = !!(isTable && (compactUnderTable || fullWidthRow));
  const cardW = isTable ? (under ? (fullWidthRow ? 32 : 24) : (isMobile ? 28 : 34)) : 40;
  const cardH = isTable ? (under ? (fullWidthRow ? 44 : 34) : (isMobile ? 40 : 48)) : 54;
  const randW = isTable ? (under ? (fullWidthRow ? 160 : 88) : (isMobile ? 110 : 130)) : 150;
  const randH = isTable ? (under ? (fullWidthRow ? 44 : 28) : (isMobile ? 40 : 48)) : 54;
  const actionMinW = isTable ? (under ? (fullWidthRow ? 72 : 40) : 48) : 58;
  const [showPopup, setShowPopup] = useState(false);
  const [draftHand, setDraftHand] = useState([null, null]);
  const [isHandHovering, setIsHandHovering] = useState(false);
  const [pickedAction, setPickedAction] = useState(null);
  const [lockedBands, setLockedBands] = useState([]);
  const lastAnimatedRollKeyRef = React.useRef("");
  const randomizerBandsRef = React.useRef([]);
  const lockedBandsRef = React.useRef([]);
  const spinnerLineRef = React.useRef(null);
  const spinnerPosRef = React.useRef(0.5);
  const randBarRef = React.useRef(null);
  const [measuredRandW, setMeasuredRandW] = React.useState(randW);
  const randomizerActiveRef = React.useRef(randomizerActive);
  const wasRandomizerActiveRef = React.useRef(randomizerActive);
  const lockedComboRef = React.useRef("");
  const selectedCombo = comboStringFromTwoCards(selectedHand);
  const totalActions = actions?.length || 0;
  const selectedComboData = React.useMemo(() => {
    if (!selectedCombo) return null;
    // Exact chosen cards only: if either hole card is already on the board, there is no live combo.
    const c1 = selectedHand?.[0];
    const c2 = selectedHand?.[1];
    const used = usedCards || [];
    if ((c1 && used.includes(c1)) || (c2 && used.includes(c2))) return null;
    // Preflop grids are keyed directly in desired space.
    if (isPreflop) {
      if (!selectedCell) return null;
      return (selectedCell.combos || []).find((c) => c.hand === selectedCombo) || null;
    }
    // Postflop: the combo must be in the acting player's current range. If it isn't
    // (e.g. villain never arrives here with AA), show nothing -- exactly like the Combos view.
    const effectiveActiveRange = rangeMapWithPositiveWeights(activeRange);
    if (effectiveActiveRange && !(Number(effectiveActiveRange[selectedCombo] || 0) > 0)) return null;
    // Perfect-play bar: only the exact chosen combo (direct API hit). Never texture/donor substitutes.
    const resolved = resolvePostflopComboData(selectedCombo, {
      gridData,
      cell: selectedCell,
      suitMapping,
      flopRankMap,
      desiredBoardCards,
      queriedBoardCards,
      textureResolutionMap,
    });
    if (resolved.kind !== "direct") return null;
    return resolved.comboData;
  }, [selectedCombo, selectedHand, usedCards, selectedCell, isPreflop, gridData, suitMapping, flopRankMap, desiredBoardCards, queriedBoardCards, textureResolutionMap, activeRange]);
  // Drop any frozen recommendation once the chosen cards are dead / unresolved.
  React.useEffect(() => {
    if (selectedComboData) return;
    if (!lockedBands.length && !pickedAction) return;
    setPickedAction(null);
    setLockedBands([]);
    lockedBandsRef.current = [];
    lockedComboRef.current = "";
    lastAnimatedRollKeyRef.current = "";
  }, [selectedComboData, lockedBands.length, pickedAction]);
  const formatRandomizerLabel = React.useCallback((actionLabel) => {
    const raw = String(actionLabel || "");
    const lower = raw.toLowerCase();
    if (!isPreflop) return formatPostflopActionLabel(raw, false);
    if (lower.includes("fold")) return "Fold";
    // Keep the action's own sizing (e.g. "Open 2.5" vs SB "Open 3").
    if (lower.includes("open")) return /\d/.test(raw) ? raw : "Open";
    return formatPostflopActionLabel(raw, true);
  }, [isPreflop]);
  const comboFreqs = React.useMemo(() => {
    if (!selectedComboData?.frequencies?.length) return [];
    if (!isPreflop) return normalizeFrequenciesForDisplay(selectedComboData.frequencies, totalActions);
    // Preflop frequencies are authored as true percentages (0..100) in many spots.
    // Preserve absolute percent mass so open-only rows leave implicit fold remainder.
    const len = Math.max(0, Number(totalActions || 0));
    if (!len) return [];
    const raw = new Array(len).fill(0).map((_, i) => {
      const v = Number(selectedComboData.frequencies?.[i] ?? 0);
      return Number.isFinite(v) ? Math.max(0, v) : 0;
    });
    const max = raw.reduce((m, v) => Math.max(m, v), 0);
    const sum = raw.reduce((a, b) => a + b, 0);
    if (max > 1 && sum <= 100.000001) return raw.map((v) => Math.max(0, Math.min(1, v / 100)));
    return normalizeFrequenciesForDisplay(raw, len);
  }, [selectedComboData, totalActions, isPreflop]);
  const comboRenderOrder = React.useMemo(
    () => getRenderIndexOrder(actions, totalActions, { isPreflop }),
    [actions, totalActions, isPreflop]
  );
  const randomizerBands = React.useMemo(() => {
    if (!comboFreqs.length || !actions?.length) return [];
    const rawBands = comboRenderOrder.map((idx) => ({
      action: actions[idx],
      pct: Math.max(0, Number(comboFreqs[idx] || 0)),
      color: getActionColor(actions[idx], idx, totalActions, { isPreflop, preflopFoldRed, actionList: actions }),
      label: formatRandomizerLabel(actions[idx]),
    }));
    let adjustedBands = rawBands;
    if (isPreflop) {
      const explicitFoldIdx = rawBands.findIndex((b) => String(b.action || "").toLowerCase().includes("fold"));
      const sum = rawBands.reduce((s, b) => s + Math.max(0, Number(b.pct) || 0), 0);
      const missing = Math.max(0, 1 - sum);
      // Preflop can have implicit fold remainder (e.g. open-only rows). Keep hardcoded action % and use remainder as fold.
      if (missing > 1e-6) {
        if (explicitFoldIdx >= 0) {
          adjustedBands = rawBands.map((b, i) =>
            i === explicitFoldIdx ? { ...b, pct: b.pct + missing } : b
          );
        } else {
          adjustedBands = [
            ...rawBands,
            {
              action: "fold",
              pct: missing,
              color: getActionColor("fold", rawBands.length, totalActions + 1, {
                isPreflop,
                preflopFoldRed,
                actionList: [...actions, "fold"],
              }),
              label: formatRandomizerLabel("fold"),
            },
          ];
        }
      }
    }
    const filtered = adjustedBands.filter((b) => b.pct > 0);
    const total = filtered.reduce((s, b) => s + b.pct, 0);
    if (total <= 0) return [];
    // Normalize to 1.0 so visual bands and weighted pick are always in sync.
    return filtered.map((b) => ({ ...b, pct: b.pct / total }));
  }, [comboFreqs, actions, comboRenderOrder, totalActions, isPreflop, preflopFoldRed, formatRandomizerLabel]);
  React.useEffect(() => {
    randomizerBandsRef.current = randomizerBands;
  }, [randomizerBands]);
  React.useEffect(() => {
    lockedBandsRef.current = lockedBands;
  }, [lockedBands]);
  React.useEffect(() => {
    randomizerActiveRef.current = randomizerActive;
    // Hero just regained the turn; allow a fresh roll for the current context.
    if (randomizerActive && !wasRandomizerActiveRef.current) {
      lastAnimatedRollKeyRef.current = "";
    }
    // Leaving hero turn: freeze whatever bands we last had so the bar/line stay painted.
    if (!randomizerActive && wasRandomizerActiveRef.current) {
      const bands = lockedBandsRef.current.length
        ? lockedBandsRef.current
        : (randomizerBandsRef.current || []);
      if (bands.length && !lockedBandsRef.current.length) {
        setLockedBands(bands);
        lockedBandsRef.current = bands;
      }
    }
    wasRandomizerActiveRef.current = randomizerActive;
  }, [randomizerActive]);
  const hasRandomizerData = React.useMemo(
    () => randomizerBands.length > 0 && randomizerBands.some((b) => (Number(b.pct) || 0) > 0),
    [randomizerBands]
  );
  const displayBands = (!randomizerActive && lockedBands.length > 0) ? lockedBands : randomizerBands;
  const displayHasRandomizerData = displayBands.length > 0 && displayBands.some((b) => (Number(b.pct) || 0) > 0);
  React.useEffect(() => {
    if (!fullWidthRow) {
      setMeasuredRandW(randW);
      return undefined;
    }
    const el = randBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      setMeasuredRandW(randW);
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (Number.isFinite(w) && w > 8) setMeasuredRandW(w);
    });
    ro.observe(el);
    setMeasuredRandW(el.clientWidth || randW);
    return () => ro.disconnect();
  }, [fullWidthRow, randW, selectedHand?.[0], selectedHand?.[1]]);
  const spinnerBarW = fullWidthRow ? measuredRandW : randW;
  const spinnerToTx = React.useCallback(
    (pos) => `translateX(${Math.max(0, Math.min(1, pos)) * (spinnerBarW - 2) - 1}px)`,
    [spinnerBarW]
  );
  const randomizerBandsSignature = React.useMemo(
    () => randomizerBands.map((b) => `${b.label}:${Number(b.pct || 0).toFixed(6)}`).join("|"),
    [randomizerBands]
  );
  const randomizerRollKey = React.useMemo(() => {
    const row = Number.isFinite(Number(selectedCell?.row)) ? selectedCell.row : "x";
    const col = Number.isFinite(Number(selectedCell?.col)) ? selectedCell.col : "x";
    const cellLabel = selectedCell?.cell?.label || "";
    const comboEv = Number.isFinite(Number(selectedComboData?.ev)) ? Number(selectedComboData.ev).toFixed(4) : "na";
    const comboFreqSig = (selectedComboData?.frequencies || []).map((f) => Number(f || 0).toFixed(6)).join(",");
    return `${selectedCombo || ""}|${isPreflop ? "pre" : "post"}|${row}:${col}|${cellLabel}|${comboEv}|${comboFreqSig}|${randomizerBandsSignature}`;
  }, [selectedCombo, isPreflop, selectedCell, selectedComboData, randomizerBandsSignature]);
  // The line is positioned imperatively (never via React state), so re-renders of the
  // surrounding tree can never snap it back to a stale position mid-animation.
  // This layout effect re-applies the current position whenever React re-renders/remounts the node.
  React.useLayoutEffect(() => {
    const el = spinnerLineRef.current;
    if (el) el.style.transform = spinnerToTx(spinnerPosRef.current);
  });
  React.useEffect(() => {
    if (!selectedHand?.[0] || !selectedHand?.[1]) {
      setPickedAction(null);
      setLockedBands([]);
      lockedBandsRef.current = [];
      lockedComboRef.current = "";
      spinnerPosRef.current = 0.5;
      lastAnimatedRollKeyRef.current = "";
      return;
    }
    // Opponent / non-hero turn: keep the last hero recommendation frozen.
    if (!randomizerActive) {
      if (lockedComboRef.current && lockedComboRef.current !== (selectedCombo || "")) {
        setPickedAction(null);
        setLockedBands([]);
        lockedBandsRef.current = [];
        lockedComboRef.current = "";
      }
      return;
    }

    if (!hasRandomizerData) {
      // Don't wipe a frozen tip while the hero-turn grid is briefly empty.
      lastAnimatedRollKeyRef.current = "";
      return;
    }
    // Replay once per distinct loaded hand-grid context (street/node/cell/frequencies).
    if (lastAnimatedRollKeyRef.current === randomizerRollKey) return;
    lastAnimatedRollKeyRef.current = randomizerRollKey;

    let raf = null;
    let webAnim = null;
    let mounted = true;

    const startSweep = () => {
      if (!mounted || !randomizerActiveRef.current) return;
      // Sample the result up-front, then animate a fully deterministic path that
      // sweeps edge-to-edge and eases to a stop exactly on the sampled point --
      // no jump-cut at the end, no mid-flight direction snaps.
      const roll = Math.max(0.005, Math.min(0.995, Math.random()));
      const startPos = Math.max(0, Math.min(1, spinnerPosRef.current ?? 0.5));
      const waypoints = [startPos, 0.97, 0.03, roll];
      const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const totalMs = 1100;
      const dists = [];
      let totalDist = 0;
      for (let i = 1; i < waypoints.length; i++) {
        const d = Math.max(0.08, Math.abs(waypoints[i] - waypoints[i - 1]));
        dists.push(d);
        totalDist += d;
      }
      const segments = dists.map((d, i) => ({
        from: waypoints[i],
        to: waypoints[i + 1],
        ms: (d / totalDist) * totalMs,
      }));

      const posAt = (clockMs) => {
        let elapsed = clockMs;
        for (const s of segments) {
          if (elapsed <= s.ms) {
            const t = easeInOut(Math.max(0, Math.min(1, elapsed / s.ms)));
            return s.from + (s.to - s.from) * t;
          }
          elapsed -= s.ms;
        }
        return roll;
      };

      const drawPos = (pos) => {
        spinnerPosRef.current = pos;
        const el = spinnerLineRef.current;
        // Write straight to the DOM (transform only) so the sweep never waits on a React render.
        if (el) el.style.transform = spinnerToTx(pos);
      };

      const finish = () => {
        drawPos(roll);
        const bands = randomizerBandsRef.current || [];
        let cumulative = 0;
        let chosen = bands[bands.length - 1];
        for (const band of bands) {
          cumulative += band.pct;
          if (roll <= cumulative) {
            chosen = band;
            break;
          }
        }
        setPickedAction(chosen || null);
        setLockedBands(bands);
        lockedBandsRef.current = bands;
        lockedComboRef.current = comboStringFromTwoCards(selectedHand) || "";
        // Keep the resting line visible after WAAPI teardown.
        requestAnimationFrame(() => drawPos(roll));
      };

      // Preflop actions rebuild grids synchronously on the main thread, which starves
      // requestAnimationFrame. Hand the sweep to the compositor (Web Animations API):
      // it plays at true speed and stays smooth no matter how busy React is.
      const lineEl = spinnerLineRef.current;
      if (isPreflop && lineEl && typeof lineEl.animate === "function") {
        const SAMPLES = 60;
        const frames = [];
        for (let k = 0; k <= SAMPLES; k++) {
          const pos = Math.max(0, Math.min(1, posAt((k / SAMPLES) * totalMs)));
          frames.push({ transform: spinnerToTx(pos) });
        }
        webAnim = lineEl.animate(frames, { duration: totalMs, easing: "linear", fill: "forwards" });
        webAnim.onfinish = () => {
          if (!mounted) return;
          finish();
          // Commit final inline transform, then drop WAAPI so it can't clear the line.
          try { webAnim.cancel(); } catch (e) { /* already gone */ }
          webAnim = null;
          drawPos(roll);
        };
        return;
      }

      // Postflop driver: unchanged rAF sweep.
      let clock = 0;
      let lastFrameAt = null;
      const animate = (now) => {
        if (!mounted) return;
        if (lastFrameAt != null) clock += Math.min(34, now - lastFrameAt);
        lastFrameAt = now;
        if (clock >= totalMs) {
          finish();
          return;
        }
        drawPos(posAt(clock));
        raf = requestAnimationFrame(animate);
      };
      raf = requestAnimationFrame(animate);
    };

    // Start immediately; no settle delay (match tiles speed).
    startSweep();

    return () => {
      mounted = false;
      if (raf) cancelAnimationFrame(raf);
      if (webAnim) {
        // Capture the line's live compositor position so a restarted sweep
        // continues from where the eye last saw it.
        try {
          const el = spinnerLineRef.current;
          if (el) {
            const m = new DOMMatrixReadOnly(getComputedStyle(el).transform);
            spinnerPosRef.current = Math.max(0, Math.min(1, (m.m41 + 1) / Math.max(1, spinnerBarW - 2)));
          }
        } catch (e) { /* keep last known position */ }
        try { webAnim.cancel(); } catch (e) { /* already gone */ }
      }
    };
  }, [selectedHand, selectedCombo, randomizerRollKey, hasRandomizerData, isPreflop, randomizerActive, spinnerToTx, spinnerBarW]);
  const openSelector = () => {
    setDraftHand([selectedHand?.[0] || null, selectedHand?.[1] || null]);
    setShowPopup(true);
  };
  const toggleCard = (card) => {
    if (!card) return;
    if ((usedCards || []).includes(card)) return;
    if (Array.isArray(requiredHand) && requiredHand.filter(Boolean).length === 2 && !requiredHand.includes(card)) {
      return;
    }
    if (draftHand.includes(card)) {
      setDraftHand((prev) => prev.map((c) => (c === card ? null : c)));
      return;
    }
    const next = [...draftHand];
    const idx = next.findIndex((c) => !c);
    if (idx >= 0) next[idx] = card;
    else next[1] = card;
    setDraftHand(next);
  };
  const selectedDraftCount = draftHand.filter(Boolean).length;
  const canApplyHand = selectedDraftCount !== 1;
  const applyHand = () => {
    if (!canApplyHand) return;
    onSelectHand(selectedDraftCount === 0 ? [null, null] : draftHand);
    setShowPopup(false);
  };
  const clearHand = () => setDraftHand([null, null]);

  return (
    <Box ref={tableRef} sx={{ mb: isTable ? 0 : 2, width: fullWidthRow ? "100%" : undefined }}>
      {!isTable && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ color: COLORS.text, fontSize: "10px", fontWeight: 600 }}>Your Hand</Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          flexWrap: "nowrap",
          width: fullWidthRow ? "100%" : undefined,
        }}
      >
        <Box
          sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}
          onMouseEnter={() => setIsHandHovering(true)}
          onMouseLeave={() => setIsHandHovering(false)}
        >
          {[0, 1].map((i) => {
            const card = selectedHand[i];
            return (
              <Box key={i} onClick={openSelector}
                sx={{
                  width: cardW,
                  height: cardH,
                  backgroundColor: card ? COLORS.black : COLORS.bg,
                  border: card
                    ? `2px solid ${isHandHovering ? COLORS.white : SUIT_COLORS[card[1]]}`
                    : `2px dashed ${isHandHovering ? COLORS.white : COLORS.border}`,
                  borderRadius: "6px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: isTable ? "0 4px 10px rgba(0,0,0,0.35)" : "none",
                }}>
                {card ? (<><Typography sx={{ fontSize: under ? 12 : (isTable ? (isMobile ? 13 : 15) : 16), fontWeight: isTable ? 400 : 700, color: SUIT_COLORS[card[1]], lineHeight: 1, fontFamily: isTable ? FUTURA_BOOK : undefined }}>{card[0]}</Typography>
                  <Typography sx={{ fontSize: under ? 10 : (isTable ? (isMobile ? 11 : 12) : 12), color: SUIT_COLORS[card[1]], fontFamily: isTable ? FUTURA_BOOK : undefined }}>{SUIT_SYMBOLS[card[1]]}</Typography></>) : (<Typography sx={{ color: COLORS.border, fontSize: under ? 12 : 14 }}>?</Typography>)}
              </Box>
            );
          })}
        </Box>
        {!!selectedHand?.[0] && !!selectedHand?.[1] && (
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.4, flex: fullWidthRow ? 1 : undefined, minWidth: 0 }}>
            <Box
              ref={randBarRef}
              sx={{
                width: fullWidthRow ? "auto" : randW,
                flex: fullWidthRow ? 1 : undefined,
                minWidth: fullWidthRow ? 48 : undefined,
                height: randH,
                borderRadius: "6px",
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.bg,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
                {displayHasRandomizerData
                  ? displayBands.map((band, idx) => (
                    <Box key={`rand-${idx}`} sx={{ width: `${Math.max(0, band.pct) * 100}%`, height: "100%", backgroundColor: band.color }} />
                  ))
                  : <Box sx={{ width: "100%", height: "100%", backgroundColor: COLORS.cardBg }} />}
              </Box>
              {displayHasRandomizerData && (
                <Box
                  ref={spinnerLineRef}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    willChange: "transform",
                    width: "2px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
                    zIndex: 2,
                    pointerEvents: "none",
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                minWidth: actionMinW,
                width: fullWidthRow ? actionMinW : undefined,
                flexShrink: 0,
                height: randH,
                borderRadius: "6px",
                border: `1px solid ${COLORS.border}`,
                backgroundColor: displayHasRandomizerData ? (pickedAction?.color || COLORS.cardBg) : COLORS.bg,
                px: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: under ? "8px" : "9px", fontWeight: isTable ? 400 : 700, textAlign: "center", lineHeight: 1.1, fontFamily: isTable ? FUTURA_BOOK : undefined }}>
                {displayHasRandomizerData ? (pickedAction?.label || "-") : ""}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {/* Portaled above table seats (hero circle z-index / transform nesting). */}
      <Modal
        open={showPopup}
        onClose={() => setShowPopup(false)}
        sx={{ zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
        slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.85)" } } }}
      >
        <Fade in={showPopup}>
          <Box onClick={(e) => e.stopPropagation()} sx={{ backgroundColor: COLORS.cardBg, borderRadius: "12px", p: 2, border: `1px solid ${COLORS.border}`, maxWidth: 360, outline: "none", zIndex: 10001, fontFamily: isTable ? FUTURA_BOOK : undefined }}>
            <Typography sx={{ color: COLORS.text, fontSize: "12px", fontWeight: isTable ? 400 : 600, mb: 1, textAlign: "center", fontFamily: isTable ? FUTURA_BOOK : undefined }}>Select Your Hand</Typography>
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", mb: 1 }}>
              {[0, 1].map((i) => {
                const card = draftHand[i];
                return (
                  <Box key={`draft-${i}`} sx={{ width: 34, height: 46, backgroundColor: card ? COLORS.black : COLORS.bg, border: card ? `2px solid ${SUIT_COLORS[card[1]]}` : `2px dashed ${COLORS.border}`, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {card ? (
                      <Typography sx={{ fontSize: 12, fontWeight: isTable ? 400 : 700, color: SUIT_COLORS[card[1]], fontFamily: isTable ? FUTURA_BOOK : undefined }}>
                        {card[0]}{SUIT_SYMBOLS[card[1]]}
                      </Typography>
                    ) : (
                      <Typography sx={{ color: COLORS.border, fontSize: 12 }}>?</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {SUITS.map((suit) => (
                <Box key={suit} sx={{ display: "flex", gap: "2px" }}>
                  {RANKS.map((rank) => {
                    const card = `${rank}${suit}`;
                    const isBoardUsed = (usedCards || []).includes(card);
                    const isSelected = draftHand.includes(card);
                    return (
                      <Button
                        key={card}
                        onClick={() => !isBoardUsed && toggleCard(card)}
                        disabled={isBoardUsed}
                        sx={{
                          minWidth: 24,
                          height: 28,
                          p: 0,
                          backgroundColor: isSelected ? SUIT_COLORS[suit] : (isBoardUsed ? COLORS.border : COLORS.black),
                          border: "none",
                          borderRadius: "3px",
                          color: isSelected ? "#000" : (isBoardUsed ? "#444" : SUIT_COLORS[suit]),
                          fontSize: "11px",
                          fontWeight: 600,
                          opacity: isBoardUsed ? 0.3 : 1,
                          "&:hover": { backgroundColor: isSelected ? SUIT_COLORS[suit] : (isBoardUsed ? COLORS.border : COLORS.cardBg) },
                          "&.Mui-disabled": { color: "#333" },
                        }}
                      >
                        {rank}
                      </Button>
                    );
                  })}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "center", mt: 1 }}>
              <Button onClick={clearHand} size="small" sx={{ minWidth: 66, fontSize: "10px", fontWeight: isTable ? 400 : undefined, fontFamily: isTable ? FUTURA_BOOK : undefined, color: COLORS.textMuted, borderColor: COLORS.border }} variant="outlined">Clear</Button>
              <Button onClick={applyHand} size="small" disabled={!canApplyHand} sx={{ minWidth: 66, fontSize: "10px", fontWeight: isTable ? 400 : undefined, fontFamily: isTable ? FUTURA_BOOK : undefined }} variant="contained">Done</Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

// ============================================================================
// Main Solver Page
// ============================================================================

const Solver = ({ initialLookupMode = null, blankMode = false, allowLookupModeSwitch = true }) => {
  const initialMode = initialLookupMode === "mtt" ? "mtt" : initialLookupMode === "cash" ? "cash" : INITIAL_SOLVER_MODE;
  const [lookupMode, setLookupMode] = useState(initialMode);
  // Solver colour preferences live at module level (SOLVER_PREFS / SUIT_COLORS);
  // re-apply and re-render when the global Settings dialog changes them.
  const [, setColorsVersion] = useState(0);
  useEffect(() => {
    const onColorsChange = () => {
      applySolverPrefs(loadSolverPrefs());
      setColorsVersion((v) => v + 1);
    };
    window.addEventListener(SOLVER_COLORS_EVENT, onColorsChange);
    return () => window.removeEventListener(SOLVER_COLORS_EVENT, onColorsChange);
  }, []);
  const [lookupStackSize, setLookupStackSize] = useState(INITIAL_STACK_SIZE);
  const [preflopHistory, setPreflopHistory] = useState([]);
  const [detectedSpot, setDetectedSpot] = useState(null);
  const [flop, setFlop] = useState([]); // User's original flop (display)
  const [requestedCanonicalFlop, setRequestedCanonicalFlop] = useState([]); // Canonicalized user flop (for debug/mapping)
  const [canonicalFlop, setCanonicalFlop] = useState([]); // Queried flop for API calls (may be closest-board)
  const [suitMapping, setSuitMapping] = useState(null); // Suit mapping for isomorphism
  const [turn, setTurn] = useState(null); // User's original turn (display)
  const [canonicalTurn, setCanonicalTurn] = useState(null); // Canonical turn for API
  const [river, setRiver] = useState(null); // User's original river (display)
  const [canonicalRiver, setCanonicalRiver] = useState(null); // Canonical river for API
  const [boardLookup, setBoardLookup] = useState(null); // { requestedFlopStr, candidates:[...], idx }
  const [gridOverlayMessage, setGridOverlayMessage] = useState(null);
  const [showFlopModal, setShowFlopModal] = useState(false);
  const [showTurnModal, setShowTurnModal] = useState(false);
  const [showRiverModal, setShowRiverModal] = useState(false);
  const [selectedFlopCards, setSelectedFlopCards] = useState([]);
  const [flopActionHistory, setFlopActionHistory] = useState([]);
  const [turnActionHistory, setTurnActionHistory] = useState([]);
  const [riverActionHistory, setRiverActionHistory] = useState([]);
  const [currentStreet, setCurrentStreet] = useState("preflop");
  const [path, setPath] = useState("root");
  const [player, setPlayer] = useState(0);
  const [gridData, setGridData] = useState(null);
  const [actions, setActions] = useState([]);
  const [textureResolutionMap, setTextureResolutionMap] = useState(null); // desiredCombo -> { fallbackData, donorHand, streetProfile }
  const [playerRanges, setPlayerRanges] = useState({ 0: null, 1: null }); // combo->weight carried range per player
  const [currentDecisionActionRanges, setCurrentDecisionActionRanges] = useState(null); // { actionLabel: { combo: weight } }
  const [currentDecisionPlayer, setCurrentDecisionPlayer] = useState(null);
  const [streetStartPot, setStreetStartPot] = useState({ flop: null, turn: null, river: null });
  const [v6NodeId, setV6NodeId] = useState("root");
  const [v6Nav, setV6Nav] = useState(null);
  const [pendingPostflopStreet, setPendingPostflopStreet] = useState(null);
  const [lastTurnPickAt, setLastTurnPickAt] = useState(0);
  const modalTimeoutRef = useRef(null);
  const pendingAuthGateAfterFlopActionRef = useRef(false);
  // Cache of already-fetched V6 grid responses / texture maps, keyed by nodeId.
  // Lets history navigation snap between previously-visited nodes instantly instead
  // of re-fetching, and is cleared whenever the underlying tree changes (new flop / reset).
  const nodeCacheRef = useRef(new Map());
  const textureCacheRef = useRef(new Map());
  // NodeId of the chance-child reached right after picking the turn/river card.
  // Used so "return to current" can jump back to a street's frontier even when
  // that street has no actions recorded yet.
  const turnRootNodeIdRef = useRef(null);
  const riverRootNodeIdRef = useRef(null);
  // Increments per grid fetch so a slow texture-resolve response from a
  // previous node can never overwrite the texture map of the current node.
  const textureRequestIdRef = useRef(0);
  // UI LOCK: while a node is loading (grid fetch AND background texture
  // resolve), clicks on game-state-changing controls are silently ignored so
  // rapid clicking can't desync the traversal state. Ref (not state) so the
  // guard is exact within the same render tick.
  const nodeBusyRef = useRef(false);
  const nodeBusyTimerRef = useRef(null);
  const beginNodeBusy = useCallback(() => {
    nodeBusyRef.current = true;
    if (nodeBusyTimerRef.current) clearTimeout(nodeBusyTimerRef.current);
    // Safety valve: never leave the UI locked if something hangs.
    nodeBusyTimerRef.current = setTimeout(() => {
      nodeBusyRef.current = false;
    }, 12000);
  }, []);
  const endNodeBusy = useCallback(() => {
    nodeBusyRef.current = false;
    if (nodeBusyTimerRef.current) {
      clearTimeout(nodeBusyTimerRef.current);
      nodeBusyTimerRef.current = null;
    }
  }, []);
  const [nodeInfo, setNodeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedNavigation, setSelectedNavigation] = useState({ street: null, index: -1 });
  const [viewMode, setViewMode] = useState("strategy");
  const [rangeBreakdownView, setRangeBreakdownView] = useState("graph");
  const [showDevDebugPopup, setShowDevDebugPopup] = useState(false);
  const [deferredRangeEquityMap, setDeferredRangeEquityMap] = useState({});
  const [userHand, setUserHand] = useState(["Ah", "Ad"]);
  const [solverLayoutMode, setSolverLayoutMode] = useState(() => loadSolverLayoutMode());
  const [heroSeat, setHeroSeat] = useState("btn");
  const [showFullRangeDialog, setShowFullRangeDialog] = useState(false);
  const handProgressedRef = useRef(false);
  const [gridRef, setGridRef] = useState(null);
  const [shouldApplyDefaultSelection, setShouldApplyDefaultSelection] = useState(true);
  const pendingTrainerHandoffRef = useRef(null);
  const suppressFlopModalRef = useRef(false);
  // Trainer "Solve this spot" → skip solver first-run tutorial for this visit.
  const suppressTutorialFromTrainerRef = useRef(false);
  const pendingTargetNodeIdRef = useRef(null);
  // Which street's action row/grid is currently on screen. Defaults to the street solving
  // has actually reached, but explicit history/tab navigation can point it elsewhere
  // without disturbing the real progress underneath.
  const viewingStreet = selectedNavigation.street || currentStreet;
  const isViewingPreflop = viewingStreet === "preflop";
  // The street solving has truly progressed furthest into, independent of whatever
  // is currently being viewed/reviewed. Drives the "live" dot in the street tabs.
  const liveStreet = river ? "river" : turn ? "turn" : (flop.length === 3 ? "flop" : "preflop");
  const [mobileGridCollapsed, setMobileGridCollapsed] = useState(true);
  const [rangePanelCollapsed, setRangePanelCollapsed] = useState(true);
  const [flopQuotaInfo, setFlopQuotaInfo] = useState(null); // { used, remaining, dailyMax }
  const [showFlopQuotaModal, setShowFlopQuotaModal] = useState(false);
  const isMobilePhone = useMediaQuery("(max-width: 767px)");
  // Smaller laptops: prioritize combo breakdown over the range graph/combos pane.
  const isSmallLaptopScreen = useMediaQuery("(max-height: 900px), (max-width: 1366px)");
  const flopActionHistoryRef = useRef(flopActionHistory);
  const turnActionHistoryRef = useRef(turnActionHistory);
  React.useEffect(() => { flopActionHistoryRef.current = flopActionHistory; }, [flopActionHistory]);
  React.useEffect(() => { turnActionHistoryRef.current = turnActionHistory; }, [turnActionHistory]);
  React.useEffect(() => {
    if (isMobilePhone) setMobileGridCollapsed(true);
  }, [isMobilePhone]);
  React.useEffect(() => {
    if (isSmallLaptopScreen) setRangePanelCollapsed(true);
  }, [isSmallLaptopScreen]);
  React.useEffect(() => {
    if ((preflopHistory || []).length > 0 || (flop || []).length > 0 || turn || river) {
      handProgressedRef.current = true;
    }
  }, [preflopHistory, flop, turn, river]);
  const setLayoutMode = React.useCallback((mode) => {
    const next = mode === "tiles" ? "tiles" : "table";
    setSolverLayoutMode(next);
    try { localStorage.setItem(SOLVER_LAYOUT_STORAGE_KEY, next); } catch (_) {}
  }, []);
  const demoFxEnabled = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    const host = String(window.location?.hostname || "").toLowerCase();
    const isLocalHost = host === "localhost" || host === "127.0.0.1";
    if (!isLocalHost) return false;
    return new URLSearchParams(window.location.search).get("demoFx") === "1";
  }, []);
  const [demoBurst, setDemoBurst] = useState(null);

  // First-run / on-demand page tutorial (spotlight tour).
  const tutorialActionsRef = React.useRef(null);
  const tutorialTableRef = React.useRef(null);
  const tutorialComboRef = React.useRef(null);
  const tutorialRangeRef = React.useRef(null);
  const tutorialHandRef = React.useRef(null);
  const tutorialHeroActionsRef = React.useRef(null);
  const tutorialEvRef = React.useRef(null);
  const tutorialPreflopRef = React.useRef(null);
  const tutorialFlopRef = React.useRef(null);
  const tutorialOutputRef = React.useRef(null);
  const tutorialRangeCellBaselineRef = React.useRef(null);
  const actionsScrollRef = React.useRef(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialFirstRun, setTutorialFirstRun] = useState(true);
  const [actionTileFlash, setActionTileFlash] = useState(0);
  const nudgeActionTile = React.useCallback(() => {
    setActionTileFlash((n) => n + 1);
  }, []);

  const bindActionsScroller = React.useCallback((el) => {
    actionsScrollRef.current = el;
    tutorialActionsRef.current = el;
    if (viewingStreet === "preflop") tutorialPreflopRef.current = el;
    if (viewingStreet === "flop") tutorialFlopRef.current = el;
  }, [viewingStreet]);

  // Keep the live/current action tile centred in the Reset→table scroll strip
  // once it reaches the midpoint; earlier tiles stay flush next to Reset.
  React.useEffect(() => {
    const scroller = actionsScrollRef.current || tutorialActionsRef.current;
    if (!scroller) return undefined;
    let cancelled = false;
    const run = (behavior = "smooth") => {
      if (cancelled) return;
      const tile = scroller.querySelector?.('[data-pm-active-tile="true"]');
      if (tile) centerChildInScroller(scroller, tile, behavior);
    };
    const t0 = window.requestAnimationFrame(() => run("auto"));
    const t1 = window.setTimeout(() => run("smooth"), 80);
    const t2 = window.setTimeout(() => run("smooth"), 220);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [
    viewingStreet,
    selectedNavigation?.street,
    selectedNavigation?.index,
    preflopHistory?.length,
    flopActionHistory?.length,
    turnActionHistory?.length,
    riverActionHistory?.length,
    player,
    showTurnModal,
    showRiverModal,
    isMobilePhone,
  ]);

  const tutorialSteps = React.useMemo(
    () => [
      {
        id: "intro",
        title: "The Solver",
        body: "The solver shows you the mathematically perfect way to play no matter the situation.",
        center: true,
      },
      {
        id: "pick-hand",
        title: "Select your hand",
        body: "Tap Hero's hole cards and pick any two cards.",
        targetRef: tutorialHandRef,
      },
      {
        id: "fold-before-hero",
        title: "Fold to Hero",
        body: "Press Fold for every player acting before Hero. Only the Fold button is offered in this tutorial.",
        targetRef: tutorialTableRef,
      },
      {
        id: "hero-randomizer",
        title: "Perfect play bar",
        body: "This bar shows the perfect play for you. Often the perfect strategy is mixed. In that case the randomiser picks so our strategy stays mathematically perfect.",
        targetRef: tutorialHandRef,
      },
      {
        id: "hero-actions-explain",
        title: "Action buttons",
        body: "These are the action buttons for us. We use them to take the action that moves us to the next decision.",
        targetRef: tutorialHeroActionsRef,
      },
      {
        id: "hero-act",
        title: "Take Hero's action",
        body: "Press raise in this case for Hero so we can see how postflop works too. Only the Raise button is offered in this tutorial.",
        targetRef: tutorialHeroActionsRef,
      },
      {
        id: "sb-fold",
        title: "Small Blind folds",
        body: "Press Fold for the Small Blind. Only the Fold button is offered in this tutorial.",
        targetRef: tutorialTableRef,
      },
      {
        id: "bb-call",
        title: "Big Blind calls",
        body: "Let's imagine this player called. Press Call. Only the Call button is offered in this tutorial.",
        targetRef: tutorialTableRef,
      },
      {
        id: "pick-flop",
        title: "Pick the flop",
        body: "Now we are at the flop. Pick any three cards.",
        targetRef: tutorialTableRef,
      },
      {
        id: "flop-check",
        title: "First player checks",
        body: "Now choose the first player's action. Let's say they check. Press Check. Only the Check button is offered in this tutorial.",
        targetRef: tutorialTableRef,
      },
      {
        id: "flop-strategy",
        title: "Perfect strategy again",
        body: "Again we see the mathematically perfect strategy for you in this bar area.",
        targetRef: tutorialHandRef,
      },
      {
        id: "see-range",
        title: "Full range",
        body: "This area shows how every hand plays in this situation (of the hands we haven't folded).",
        targetRef: tutorialOutputRef,
      },
      {
        id: "click-range",
        title: "Change hands",
        body: "By clicking other squares here we can change our hand to whatever we like and view that strategy. Click another square to continue.",
        targetRef: tutorialOutputRef,
      },
      {
        id: "press-ev",
        title: "Expected value",
        body: "For the maths on our expected value for each hand, click the EV button.",
        targetRef: tutorialEvRef,
      },
      {
        id: "finale",
        title: "You're ready",
        body: "This is how the solver tells you how to play perfectly for any situation instantly.",
        center: true,
      },
    ],
    []
  );

  const tutorialStepId = tutorialOpen ? tutorialSteps[tutorialStep]?.id : null;

  const tutorialActionAllowlist = React.useMemo(() => {
    if (!tutorialOpen) return null;
    switch (tutorialStepId) {
      case "fold-before-hero":
      case "sb-fold":
        return ["fold"];
      case "bb-call":
        return ["call"];
      case "hero-act":
        return ["raise"];
      case "flop-check":
        return ["check"];
      case "intro":
      case "pick-hand":
      case "hero-randomizer":
      case "hero-actions-explain":
      case "pick-flop":
      case "flop-strategy":
      case "see-range":
      case "click-range":
      case "press-ev":
      case "finale":
        return [];
      default:
        return null;
    }
  }, [tutorialOpen, tutorialStepId]);

  // Arm a hold when the flop check is done so prompt 11 can't continue on stale/pre-load data.
  const [tutorialHoldForSituation, setTutorialHoldForSituation] = useState(false);
  const tutorialSawSituationLoadingRef = React.useRef(false);

  React.useEffect(() => {
    if (!tutorialOpen) {
      setTutorialHoldForSituation(false);
      tutorialSawSituationLoadingRef.current = false;
      return;
    }
    if (tutorialStepId !== "flop-check") return;
    const checked = (flopActionHistory || []).some((h) => {
      const a = String(h?.action || "").toLowerCase();
      return a.includes("check") || a === "x";
    });
    if (checked) {
      tutorialSawSituationLoadingRef.current = false;
      setTutorialHoldForSituation(true);
    }
  }, [tutorialOpen, tutorialStepId, flopActionHistory]);

  React.useEffect(() => {
    if (!tutorialHoldForSituation) return;
    if (tutorialStepId !== "flop-strategy" && tutorialStepId !== "see-range") return;
    if (loading) {
      tutorialSawSituationLoadingRef.current = true;
      return;
    }
    // Release only after we actually observed a load cycle for this situation.
    if (tutorialSawSituationLoadingRef.current && gridData) {
      setTutorialHoldForSituation(false);
    }
  }, [tutorialHoldForSituation, tutorialStepId, loading, gridData]);

  // Fallback: cached/instant responses may never flip loading - settle so we don't soft-lock.
  React.useEffect(() => {
    if (!tutorialHoldForSituation) return;
    if (tutorialStepId !== "flop-strategy" && tutorialStepId !== "see-range") return;
    const t = window.setTimeout(() => {
      if (!loading && gridData) setTutorialHoldForSituation(false);
    }, 900);
    return () => window.clearTimeout(t);
  }, [tutorialHoldForSituation, tutorialStepId, loading, gridData]);

  // Use gridData (not displayGridData) - displayGridData is declared later in this component.
  const tutorialSituationReady =
    !tutorialHoldForSituation && !loading && !!gridData;

  const tutorialTaskDone = React.useMemo(() => {
    if (!tutorialOpen) return true;
    const livePreflop = processPreflop(preflopHistory || []);
    const active = String(livePreflop?.position || "").toLowerCase();
    const hero = String(heroSeat || "btn").toLowerCase();
    const hasHand = !!(userHand?.[0] && userHand?.[1]);
    switch (tutorialStepId) {
      case "intro":
      case "hero-randomizer":
      case "hero-actions-explain":
      case "finale":
        return true;
      case "flop-strategy":
      case "see-range":
        // Prompt 11+: wait until the solved situation has finished loading.
        return tutorialSituationReady;
      case "pick-hand":
        return hasHand;
      case "fold-before-hero":
        return !!active && active === hero;
      case "hero-act":
        return (preflopHistory || []).some((h) => {
          if (String(h?.position || "").toLowerCase() !== hero) return false;
          const a = String(h?.action || "").toLowerCase();
          return a.includes("raise") || a.includes("open") || a.includes("bet");
        });
      case "sb-fold": {
        const sbFolded = (preflopHistory || []).some(
          (h) => String(h?.position || "").toLowerCase() === "sb" && String(h?.action || "").toLowerCase() === "fold"
        );
        return sbFolded || active === "bb" || !!livePreflop?.isComplete;
      }
      case "bb-call":
        return !!livePreflop?.isComplete || flop.length === 3 || showFlopModal;
      case "pick-flop":
        return Array.isArray(flop) && flop.length === 3;
      case "flop-check": {
        const checked = (flopActionHistory || []).some((h) => {
          const a = String(h?.action || "").toLowerCase();
          return a.includes("check") || a === "x";
        });
        return checked;
      }
      case "click-range": {
        const baseline = tutorialRangeCellBaselineRef.current;
        if (!baseline) return !!selectedCell;
        const cur = selectedCell ? `${selectedCell.row},${selectedCell.col}` : "";
        return !!cur && cur !== baseline;
      }
      case "press-ev":
        return viewMode === "ev";
      default:
        return true;
    }
  }, [
    tutorialOpen,
    tutorialStepId,
    userHand,
    heroSeat,
    preflopHistory,
    flop,
    showFlopModal,
    flopActionHistory,
    selectedCell,
    viewMode,
    tutorialSituationReady,
  ]);

  // Continue is always offered; disabled until the step's task is done (or always on for info steps).
  const tutorialContinueEnabled = tutorialTaskDone;
  const tutorialShowContinue = true;

  // Steps that auto-advance once their task is complete (Continue remains as fallback).
  const TUTORIAL_AUTO_ADVANCE_IDS = React.useMemo(
    () =>
      new Set([
        "pick-hand",
        "fold-before-hero",
        "hero-act",
        "sb-fold",
        "bb-call",
        "pick-flop",
        "flop-check",
        "click-range",
        "press-ev",
      ]),
    []
  );

  const selectedCellRef = React.useRef(selectedCell);
  selectedCellRef.current = selectedCell;

  const advanceTutorialStep = React.useCallback((fromStepId = null) => {
    setTutorialStep((prev) => {
      const curId = tutorialSteps[prev]?.id;
      if (fromStepId && curId !== fromStepId) return prev;
      if (prev >= tutorialSteps.length - 1) return prev;
      const nextId = tutorialSteps[prev + 1]?.id;
      if (nextId === "click-range") {
        const cell = selectedCellRef.current;
        tutorialRangeCellBaselineRef.current = cell ? `${cell.row},${cell.col}` : "";
      }
      return prev + 1;
    });
  }, [tutorialSteps]);

  const startTutorial = React.useCallback((opts = {}) => {
    if (isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    // Manual replay clears Trainer handoff suppress so the tour can run.
    suppressTutorialFromTrainerRef.current = false;
    const firstRun = opts?.firstRun !== false && !hasCompletedTutorial(SOLVER_TUTORIAL_STORAGE_KEY);
    setTutorialFirstRun(!!firstRun || opts?.forceUnskippable === true);
    setLayoutMode("table");
    setHeroSeat("btn");
    setViewMode("strategy");
    setTutorialStep(0);
    setTutorialOpen(true);
    setUserHand([null, null]);
    setShouldApplyDefaultSelection(false);
    tutorialRangeCellBaselineRef.current = null;
    // Soft reset of the hand state for a clean guided path.
    try {
      setPreflopHistory([]);
      setFlop([]);
      setTurn(null);
      setRiver(null);
      setFlopActionHistory([]);
      setTurnActionHistory([]);
      setRiverActionHistory([]);
      setShowFlopModal(false);
      setShowTurnModal(false);
      setShowRiverModal(false);
      setSelectedCell(null);
      setDetectedSpot(null);
      setCurrentStreet("preflop");
      setSelectedNavigation({ street: null, index: -1 });
    } catch (_) {}
  }, [setLayoutMode]);

  const finishTutorial = React.useCallback(() => {
    setTutorialOpen(false);
    setTutorialStep(0);
    markTutorialComplete(SOLVER_TUTORIAL_STORAGE_KEY);
    setTutorialFirstRun(false);
  }, []);

  const continueTutorial = React.useCallback(() => {
    if (!tutorialContinueEnabled) return;
    if (tutorialStep >= tutorialSteps.length - 1) {
      finishTutorial();
      return;
    }
    advanceTutorialStep(tutorialStepId);
  }, [
    advanceTutorialStep,
    finishTutorial,
    tutorialContinueEnabled,
    tutorialStep,
    tutorialStepId,
    tutorialSteps.length,
  ]);

  React.useEffect(() => {
    if (!tutorialOpen) return;
    if (solverLayoutMode !== "table") setLayoutMode("table");
  }, [tutorialOpen, solverLayoutMode, setLayoutMode]);

  React.useEffect(() => {
    if (!tutorialOpen) return;
    if (tutorialStepId === "pick-flop" && flop.length < 3 && !showFlopModal) {
      setShowFlopModal(true);
    }
  }, [tutorialOpen, tutorialStepId, flop.length, showFlopModal]);

  // Auto-advance when the step task is done. Depends only on primitives so the timer
  // is not cancelled every render by unstable callback identities.
  React.useEffect(() => {
    if (!tutorialOpen || !tutorialTaskDone) return;
    if (!tutorialStepId || !TUTORIAL_AUTO_ADVANCE_IDS.has(tutorialStepId)) return;
    const fromId = tutorialStepId;
    const t = window.setTimeout(() => {
      advanceTutorialStep(fromId);
    }, 250);
    return () => window.clearTimeout(t);
  }, [
    tutorialOpen,
    tutorialTaskDone,
    tutorialStepId,
    TUTORIAL_AUTO_ADVANCE_IDS,
    advanceTutorialStep,
  ]);

  React.useEffect(() => {
    if (!tutorialOpen) return;
    if (tutorialStep > tutorialSteps.length - 1) setTutorialStep(0);
  }, [tutorialOpen, tutorialStep, tutorialSteps.length]);

  React.useEffect(() => {
    if (blankMode) return undefined;
    if (isPaywallRequired()) return undefined;
    if (hasCompletedTutorial(SOLVER_TUTORIAL_STORAGE_KEY)) return undefined;
    try {
      if (sessionStorage.getItem(TRAINER_SOLVE_HANDOFF_KEY)) {
        suppressTutorialFromTrainerRef.current = true;
      }
    } catch (_) {}
    if (suppressTutorialFromTrainerRef.current) return undefined;
    const t = window.setTimeout(() => {
      if (suppressTutorialFromTrainerRef.current) return;
      startTutorial({ firstRun: true, forceUnskippable: true });
    }, 600);
    return () => window.clearTimeout(t);
  }, [blankMode, startTutorial]);

  const handleDemoFxClickCapture = React.useCallback((event) => {
    if (!demoFxEnabled) return;
    const source = event?.target?.closest?.("button, .MuiToggleButton-root, [role='button']");
    if (!source) return;
    const rect = source.getBoundingClientRect?.();
    if (!rect) return;
    setDemoBurst({
      key: Date.now(),
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
  }, [demoFxEnabled]);

  React.useEffect(() => {
    if (!demoBurst) return undefined;
    const t = window.setTimeout(() => setDemoBurst(null), 760);
    return () => window.clearTimeout(t);
  }, [demoBurst]);

  const ensureTurnRiverAccess = useCallback((stage = "turn_or_river") => {
    if (tutorialOpen) return true;
    if (isPaywallRequired()) {
      openSubscriptionGate(stage);
      return false;
    }
    if (!TURN_RIVER_PAYWALL_ENABLED) return true;
    const billingState = window.__pmBillingState || null;
    const authed = !!billingState?.authed;
    const allowed = billingState?.hasAccess
      ? !!billingState.hasAccess.solver
      : !!billingState?.hasPaidAccess;
    if (authed && allowed) return true;
    try {
      if (typeof window.__pmOpenSubscriptionGate === "function") {
        window.__pmOpenSubscriptionGate();
      } else {
        window.dispatchEvent(new CustomEvent("pm:require-subscription", { detail: { stage } }));
      }
    } catch (_) {}
    return false;
  }, [tutorialOpen]);
  
  const preflopState = processPreflop(preflopHistory);
  // Reviewing index i means "show the decision as it stood right before box i's action" --
  // i.e. exclusive of that action -- so the strategy shown and the editable tile both
  // correspond to the exact box that was clicked, not the one after it.
  const preflopViewState = React.useMemo(() => {
    if (selectedNavigation.street !== "preflop" || selectedNavigation.index < 0) return null;
    const upTo = preflopHistory.slice(0, selectedNavigation.index);
    return processPreflop(upTo);
  }, [selectedNavigation.street, selectedNavigation.index, preflopHistory]);
  const preflopDecisionHistory = React.useMemo(() => {
    if (selectedNavigation.street !== "preflop" || selectedNavigation.index < 0) return preflopHistory;
    return preflopHistory.slice(0, selectedNavigation.index);
  }, [selectedNavigation.street, selectedNavigation.index, preflopHistory]);
  const preflopActiveSeat = (preflopViewState || preflopState)?.position || null;
  const preflopFoldRed = React.useMemo(() => {
    if (!isViewingPreflop || !preflopActiveSeat) return false;
    return preflopDecisionHistory.some(
      (a) => a.position === preflopActiveSeat && a.action !== "fold"
    );
  }, [isViewingPreflop, preflopActiveSeat, preflopDecisionHistory]);
  const preflopDecision = React.useMemo(() => {
    if (blankMode) return null;
    if (!isViewingPreflop) return null;
    return getPreflopDecisionGrid(preflopDecisionHistory, preflopViewState || preflopState);
  }, [blankMode, isViewingPreflop, preflopDecisionHistory, preflopViewState, preflopState]);
  // Seat whose strategy the grid shows; full name (e.g. Hijack), shown right of Strategy/EV.
  const strategySeatLabel = React.useMemo(() => {
    if (isViewingPreflop) return fullTableSeatLabel(preflopActiveSeat);
    let acting = player;
    const navStreet = selectedNavigation.street;
    const navIdx = selectedNavigation.index;
    if (navStreet && navStreet !== "preflop" && navIdx >= 0) {
      const hist =
        navStreet === "flop" ? flopActionHistory
          : navStreet === "turn" ? turnActionHistory
            : navStreet === "river" ? riverActionHistory
              : null;
      const entryPlayer = hist?.[navIdx]?.player;
      if (entryPlayer === 0 || entryPlayer === 1) acting = entryPlayer;
    }
    const code = acting === 0 ? detectedSpot?.positions?.oop : detectedSpot?.positions?.ip;
    return fullTableSeatLabel(code) || (acting === 0 ? "Out of Position" : "In Position");
  }, [
    isViewingPreflop,
    preflopActiveSeat,
    player,
    selectedNavigation.street,
    selectedNavigation.index,
    flopActionHistory,
    turnActionHistory,
    riverActionHistory,
    detectedSpot?.positions?.oop,
    detectedSpot?.positions?.ip,
  ]);
  const isBlankPreflopGrid = React.useMemo(() => {
    if (!isViewingPreflop || blankMode) return false;
    return isBlankPreflopStrategyGrid(preflopDecision?.grid);
  }, [isViewingPreflop, blankMode, preflopDecision]);

  const effectiveGridOverlayMessage = gridOverlayMessage
    || (isBlankPreflopGrid ? PREFLOP_MUST_FOLD_OVERLAY : null);

  const displayActions = isViewingPreflop ? (preflopDecision?.actions || []) : actions;
  const userHandLabel = handLabelFromTwoCards(userHand);
  const userHandCombo = comboStringFromTwoCards(userHand);
  const showHandReminder = !!userHand?.[0] && !!userHand?.[1];

  /** Desired flop ranks ↔ queried flop ranks (strength order); null when no DB flop. */
  const flopRankMap = React.useMemo(() => {
    if (!flop || flop.length !== 3 || !canonicalFlop || canonicalFlop.length !== 3) return null;
    return createFlopRankMappingStrengthPaired(flop, canonicalFlop);
  }, [flop, canonicalFlop]);

  const desiredBoardCards = React.useMemo(
    () => [...(flop || []), ...(turn ? [turn] : []), ...(river ? [river] : [])].filter(Boolean),
    [flop, turn, river]
  );
  const queriedBoardCards = React.useMemo(
    () =>
      [...(canonicalFlop || []), ...(canonicalTurn ? [canonicalTurn] : []), ...(canonicalRiver ? [canonicalRiver] : [])].filter(
        Boolean
      ),
    [canonicalFlop, canonicalTurn, canonicalRiver]
  );

  const stepOneDonorHands = React.useMemo(() => {
    if (isViewingPreflop || !gridData) return new Set();
    return collectStepOneDonorHands(
      gridData,
      suitMapping,
      flopRankMap,
      desiredBoardCards,
      queriedBoardCards
    );
  }, [gridData, suitMapping, flopRankMap, isViewingPreflop, desiredBoardCards, queriedBoardCards]);

  const matrixGridData = React.useMemo(() => {
    if (isViewingPreflop || !gridData) return gridData;
    return applyStepOneDonorBlanking(gridData, stepOneDonorHands);
  }, [gridData, stepOneDonorHands, isViewingPreflop]);

  const baseDisplayGridData = isViewingPreflop ? preflopDecision?.grid : matrixGridData;
  const resolutionGridData = isViewingPreflop ? preflopDecision?.grid : gridData;
  const usedCards = React.useMemo(() => [...flop, turn, river].filter(Boolean), [flop, turn, river]);
  const activeRangeForDisplay = (player === 0 || player === 1) ? (playerRanges[player] || null) : null;
  const displayGridData = React.useMemo(() => {
    if (isViewingPreflop) return baseDisplayGridData;
    return buildPostflopHandMatrixGrid(resolutionGridData, displayActions, {
      activeRange: activeRangeForDisplay,
      usedCards,
      suitMapping,
      flopRankMap,
      desiredBoardCards,
      queriedBoardCards,
      textureResolutionMap,
    });
  }, [
    isViewingPreflop,
    baseDisplayGridData,
    resolutionGridData,
    displayActions,
    activeRangeForDisplay,
    usedCards,
    suitMapping,
    flopRankMap,
    desiredBoardCards,
    queriedBoardCards,
    textureResolutionMap,
  ]);
  const rangeEquityTargetMap = React.useMemo(() => {
    const map = {};
    const rangeKeys = Array.from(
      new Set([
        ...Object.keys(playerRanges?.[0] || {}),
        ...Object.keys(playerRanges?.[1] || {}),
      ])
    );
    if (!rangeKeys.length || !resolutionGridData) return map;
    for (const comboHand of rangeKeys) {
      if (!comboHand || comboHand.length !== 4) continue;
      let ev = null;
      if (isViewingPreflop) {
        const direct = findComboDataInGrid(resolutionGridData, comboHand);
        if (Number.isFinite(Number(direct?.ev))) ev = Number(direct.ev);
      } else {
        const apiComboStr = comboDesiredToApiLookup(comboHand, suitMapping, flopRankMap);
        const forceStepTwo = shouldSkipStepOneDirect(
          comboHand,
          flopRankMap,
          desiredBoardCards,
          queriedBoardCards,
          apiComboStr
        );
        const primary = findComboDataInGrid(resolutionGridData, apiComboStr);
        if (!forceStepTwo && isPrimaryApiComboUsable(apiComboStr, primary, comboHand) && Number.isFinite(Number(primary?.ev))) {
          ev = Number(primary.ev);
        } else {
          const fallback = textureResolutionMap?.[comboHand]?.fallbackData;
          if (fallback?.frequencies?.some((f) => (f || 0) > 0) && Number.isFinite(Number(fallback?.ev))) {
            ev = Number(fallback.ev);
          }
        }
      }
      if (Number.isFinite(ev)) map[comboHand] = ev;
    }
    return map;
  }, [
    isViewingPreflop,
    playerRanges,
    resolutionGridData,
    suitMapping,
    flopRankMap,
    desiredBoardCards,
    queriedBoardCards,
    textureResolutionMap,
  ]);
  React.useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      setDeferredRangeEquityMap(rangeEquityTargetMap || {});
    }, 120);
    return () => clearTimeout(t);
  }, [loading, rangeEquityTargetMap]);

  const selectorActions = React.useMemo(() => buildSelectorActions(displayActions, displayGridData), [displayActions, displayGridData]);

  const getPreflopActionIndex = useCallback((actionId, actionLabels) => {
    const labels = actionLabels || [];
    const lowerId = (actionId || "").toLowerCase();
    if (!labels.length) return -1;
    if (lowerId === "fold") return labels.findIndex((a) => (a || "").toLowerCase().includes("fold"));
    if (lowerId === "call") return labels.findIndex((a) => (a || "").toLowerCase().includes("call"));
    // open / raise / 3bet / 4bet / all-in all map to aggressive action slot
    return labels.findIndex((a) => {
      const l = (a || "").toLowerCase();
      return l.includes("open") || l.includes("raise") || l.includes("bet") || l.includes("all-in") || l.includes("allin");
    });
  }, []);

  const buildPreflopRangesFromHistory = useCallback((history) => {
    const out = {};
    const actions = history || [];
    for (let i = 0; i < actions.length; i++) {
      const acted = actions[i];
      const priorHistory = actions.slice(0, i);
      const state = processPreflop(priorHistory);
      const decision = getPreflopDecisionGrid(priorHistory, state);
      const actionIdx = getPreflopActionIndex(acted?.action, decision?.actions || []);

      if (!decision?.grid || actionIdx < 0) {
        out[acted.position] = {};
        continue;
      }

      const priorRange = out[acted.position] || null;
      const nextRange = {};
      for (const row of decision.grid) {
        for (const cell of row) {
          for (const combo of cell?.combos || []) {
            const base = priorRange ? (priorRange[combo.hand] || 0) : 1;
            const freq = Number(combo?.frequencies?.[actionIdx] ?? 0);
            const w = base * (Number.isFinite(freq) ? Math.max(0, Math.min(1, freq)) : 0);
            if (w > 0) nextRange[combo.hand] = w;
          }
        }
      }
      out[acted.position] = acted?.action === "fold" ? {} : nextRange;
    }
    return out;
  }, [getPreflopActionIndex]);

  useEffect(() => {
    if (!isViewingPreflop || !displayGridData?.[0]?.[0]) return;
    setSelectedCell((prev) => {
      const pick = shouldApplyDefaultSelection
        ? pickPreferredSelectedCell(displayGridData, userHand, prev)
        : (prev && typeof prev.row === "number" && typeof prev.col === "number" && displayGridData?.[prev.row]?.[prev.col]
          ? { row: prev.row, col: prev.col, cell: displayGridData[prev.row][prev.col] }
          : prev);
      if (!pick) return prev;
      if (prev?.row === pick.row && prev?.col === pick.col && prev?.cell === pick.cell) return prev;
      return pick;
    });
    if (shouldApplyDefaultSelection) setShouldApplyDefaultSelection(false);
  }, [isViewingPreflop, displayGridData, userHand, shouldApplyDefaultSelection]);

  useEffect(() => {
    // EV is postflop-only: force strategy view while reviewing preflop.
    if (isViewingPreflop && viewMode === "ev") setViewMode("strategy");
  }, [isViewingPreflop, viewMode]);

  const stackOptions = React.useMemo(
    () => (lookupMode === "mtt" ? SOLVER_MTT_STACK_OPTIONS : SOLVER_CASH_STACK_OPTIONS),
    [lookupMode]
  );
  const lookupContext = React.useMemo(
    () => ({
      mode: lookupMode,
      tableSize: lookupMode === "mtt" ? SOLVER_MTT_TABLE_SIZE : SOLVER_CASH_TABLE_SIZE,
      stackSize: lookupStackSize,
      openSize: lookupMode === "mtt" ? SOLVER_MTT_OPEN_SIZE : SOLVER_CASH_OPEN_SIZE,
    }),
    [lookupMode, lookupStackSize]
  );

  useEffect(() => {
    if (!stackOptions.includes(lookupStackSize) && stackOptions.length > 0) {
      setLookupStackSize(stackOptions[0]);
    }
  }, [stackOptions, lookupStackSize]);

  const handleReset = (options = {}) => {
    // UI LOCK: no resets while a node load is mid-flight.
    if (nodeBusyRef.current) return;
    const rotateHero = options?.rotateHero !== false;
    const shouldRotateHero =
      rotateHero
      && solverLayoutMode === "table"
      && handProgressedRef.current;
    nodeCacheRef.current.clear();
    textureCacheRef.current.clear();
    turnRootNodeIdRef.current = null;
    riverRootNodeIdRef.current = null;
    setPreflopHistory([]);
    setDetectedSpot(null);
    setFlop([]);
    setRequestedCanonicalFlop([]);
    setCanonicalFlop([]);
    setSuitMapping(null);
    setTurn(null);
    setCanonicalTurn(null);
    setRiver(null);
    setCanonicalRiver(null);
    setBoardLookup(null);
    setGridOverlayMessage(null);
    setShowFlopModal(false);
    setShowTurnModal(false);
    setShowRiverModal(false);
    setSelectedFlopCards([]);
    setFlopActionHistory([]);
    setTurnActionHistory([]);
    setRiverActionHistory([]);
    setCurrentStreet("preflop");
    setPath("root");
    setPlayer(0);
    setGridData(null);
    setActions([]);
    setPlayerRanges({ 0: null, 1: null });
    setCurrentDecisionActionRanges(null);
    setCurrentDecisionPlayer(null);
    setStreetStartPot({ flop: null, turn: null, river: null });
    setNodeInfo(null);
    setPendingPostflopStreet(null);
    setError(null);
    setSelectedCell(null);
    setSelectedNavigation({ street: null, index: -1 });
    setUserHand(["Ah", "Ad"]);
    setShouldApplyDefaultSelection(true);
    setMobileGridCollapsed(true);
    setShowFullRangeDialog(false);
    if (shouldRotateHero) setHeroSeat((prev) => nextHeroSeat(prev));
    handProgressedRef.current = false;
  };

  const handleHeroSeatChange = (seatId) => {
    const next = String(seatId || "").toLowerCase();
    if (!next || next === heroSeat) return;
    setHeroSeat(next);
    handleReset({ rotateHero: false });
  };

  const handleLookupModeChange = (nextMode) => {
    if (!allowLookupModeSwitch) return;
    const normalized = nextMode === "mtt" ? "mtt" : "cash";
    if (normalized === lookupMode) return;
    const nextStackOptions = normalized === "mtt" ? SOLVER_MTT_STACK_OPTIONS : SOLVER_CASH_STACK_OPTIONS;
    setLookupMode(normalized);
    setLookupStackSize(nextStackOptions[0] || (normalized === "mtt" ? "30bbs" : "100bbs"));
    handleReset();
  };

  const handleLookupStackChange = (nextStack) => {
    const normalized = String(nextStack || "").trim().toLowerCase();
    if (!normalized || normalized === lookupStackSize) return;
    setLookupStackSize(normalized);
    handleReset();
  };

  const handleUserHandSelect = (newHand) => {
    if (!blankMode && isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    setUserHand(newHand);
    setShouldApplyDefaultSelection(false);
    const pick = findCellByLabel(displayGridData, handLabelFromTwoCards(newHand));
    if (pick) setSelectedCell(pick);
    // Tutorial pick-hand: advance as soon as a full hand is committed (Done).
    // The shared auto-advance effect also covers this; this is an immediate nudge.
    if (tutorialOpen && tutorialStepId === "pick-hand" && newHand?.[0] && newHand?.[1]) {
      window.setTimeout(() => advanceTutorialStep("pick-hand"), 200);
    }
  };

  const handlePreflopAction = (actionId, raiseSize) => {
    if (blankMode) return;
    if (isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    if (tutorialOpen && Array.isArray(tutorialActionAllowlist) && !tutorialActionMatches(actionId, tutorialActionAllowlist)) {
      return;
    }
    // UI LOCK: preflop history edits reset postflop state; don't allow them mid-load.
    if (nodeBusyRef.current) return;
    setShouldApplyDefaultSelection(true);
    let nextHistory;
    if (selectedNavigation.street === "preflop" && selectedNavigation.index >= 0 && preflopViewState?.position) {
      // Replace the reviewed decision (and discard everything that happened after it),
      // rather than appending a second box alongside the original one.
      const base = preflopHistory.slice(0, selectedNavigation.index);
      nextHistory = [...base, { position: preflopViewState.position, action: actionId, raiseSize }];
    } else {
      nextHistory = [...preflopHistory, { position: preflopState.position, action: actionId, raiseSize }];
    }
    setPreflopHistory(nextHistory);
    setSelectedNavigation({ street: null, index: -1 });

    // Tutorial: nudge auto-advance from the action itself (effect is backup).
    if (tutorialOpen && tutorialStepId && TUTORIAL_AUTO_ADVANCE_IDS.has(tutorialStepId)) {
      const live = processPreflop(nextHistory);
      const active = String(live?.position || "").toLowerCase();
      const hero = String(heroSeat || "btn").toLowerCase();
      const aid = String(actionId || "").toLowerCase();
      let done = false;
      if (tutorialStepId === "fold-before-hero") done = !!active && active === hero;
      else if (tutorialStepId === "hero-act") {
        done = (nextHistory || []).some((h) => {
          if (String(h?.position || "").toLowerCase() !== hero) return false;
          const a = String(h?.action || "").toLowerCase();
          return a.includes("raise") || a.includes("open") || a.includes("bet");
        });
      } else if (tutorialStepId === "sb-fold") {
        done = (nextHistory || []).some(
          (h) => String(h?.position || "").toLowerCase() === "sb" && String(h?.action || "").toLowerCase() === "fold"
        ) || active === "bb" || !!live?.isComplete;
      } else if (tutorialStepId === "bb-call") {
        done = !!live?.isComplete || aid.includes("call");
      }
      if (done) {
        const fromId = tutorialStepId;
        window.setTimeout(() => advanceTutorialStep(fromId), 200);
      }
    }
  };

  const blankPreflopOverlayFold =
    isBlankPreflopGrid && !blankMode && (preflopViewState || preflopState)?.canFold !== false
      ? () => handlePreflopAction("fold")
      : null;

  const handleCellClick = (cellData) => {
    setSelectedCell(cellData);
    if (isMobilePhone) setMobileGridCollapsed(true);
  };

  const handleComboSelect = (comboHand) => {
    const [c1, c2] = handCardsFromComboString(comboHand);
    if (!c1 || !c2) return;
    if ((usedCards || []).includes(c1) || (usedCards || []).includes(c2)) return;
    setUserHand([c1, c2]);
    setShouldApplyDefaultSelection(false);
  };

  const buildPathFromHistory = useCallback((history) => {
    if (!history || history.length === 0) return "root";
    return history.map(a => actionToPathSegment(a.action)).join("-");
  }, []);

  const handleNavigate = useCallback(async (street, actionIndex) => {
    // UI LOCK: history tiles are inert while a node load is in progress.
    if (nodeBusyRef.current) return;
    if (street === "preflop") {
      setSelectedNavigation({ street, index: actionIndex });
      return;
    }
    if (!detectedSpot || flop.length < 3) return;
    if (USE_V6) {
      setSelectedNavigation({ street, index: actionIndex });
      const resolveDecisionNodeId = (history, idx) => {
        const entry = history?.[idx];
        if (!entry) return null;
        // New entries store the node where the action was taken (desired for tile review).
        if (entry.decisionNodeId) return entry.decisionNodeId;
        // Backward-compat fallback for older in-memory entries:
        // the prior action's next node is the current action's decision node.
        const prev = idx > 0 ? history?.[idx - 1] : null;
        if (prev?.nextNodeId) return prev.nextNodeId;
        // Last resort (legacy behavior): show post-action node.
        return entry.nextNodeId || null;
      };
      if (street === "flop") {
        const nodeId = resolveDecisionNodeId(flopActionHistory, actionIndex);
        if (nodeId) {
          if (nodeId !== v6NodeId) beginNodeBusy();
          setV6NodeId(nodeId);
          setShowTurnModal(false);
          setShowRiverModal(false);
        }
      } else if (street === "turn") {
        const nodeId = resolveDecisionNodeId(turnActionHistory, actionIndex);
        if (nodeId) {
          if (nodeId !== v6NodeId) beginNodeBusy();
          setV6NodeId(nodeId);
          setShowRiverModal(false);
        }
      } else if (street === "river") {
        const nodeId = resolveDecisionNodeId(riverActionHistory, actionIndex);
        if (nodeId) {
          if (nodeId !== v6NodeId) beginNodeBusy();
          setV6NodeId(nodeId);
        }
      }
      return;
    }
    setSelectedNavigation({ street, index: actionIndex });
    let navPath = "root", navPlayer = 0, navTurn = null;
    
    if (street === "flop" && actionIndex >= 0) {
      const historyBefore = flopActionHistory.slice(0, actionIndex);
      navPath = buildPathFromHistory(historyBefore);
      navPlayer = flopActionHistory[actionIndex]?.player ?? 0;
    } else if (street === "turn" && actionIndex >= 0) {
      const flopPath = buildPathFromHistory(flopActionHistory);
      const turnHistoryBefore = turnActionHistory.slice(0, actionIndex);
      const turnPath = buildPathFromHistory(turnHistoryBefore);
      navPath = `${flopPath}|${turn}${turnPath ? `-${turnPath}` : ""}`;
      navPlayer = turnActionHistory[actionIndex]?.player ?? 0;
      navTurn = turn;
    }
    
    beginNodeBusy();
    setLoading(true);
    try {
      const flopStr = flop.join("");
      
      // V3-only navigation (V6 uses nodeId-based traversal)
      let url = `${SOLVER_API_URL}/api/v3/grid?spot=${detectedSpot.spot}&flop=${flopStr}&path=${navPath}&player=${navPlayer}`;
      if (navTurn) url += `&turn=${navTurn}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setGridData(data.grid); setActions(data.actions || []);
        setNodeInfo({ playerName: data.header?.playerName, streetName: data.header?.streetName, pot: data.pot || data.header?.pot });
        if (data.grid?.[0]?.[0]) {
          setSelectedCell((prev) =>
            shouldApplyDefaultSelection
              ? pickPreferredSelectedCell(data.grid, userHand, prev)
              : (prev && typeof prev.row === "number" && typeof prev.col === "number" && data.grid?.[prev.row]?.[prev.col]
                ? { row: prev.row, col: prev.col, cell: data.grid[prev.row][prev.col] }
                : prev)
          );
          if (shouldApplyDefaultSelection) setShouldApplyDefaultSelection(false);
        }
      }
    } catch (err) { console.error("Navigation error:", err); }
    finally { setLoading(false); endNodeBusy(); }
  }, [detectedSpot, flop, flopActionHistory, turnActionHistory, riverActionHistory, turn, buildPathFromHistory, USE_V6, userHand, v6NodeId, beginNodeBusy, endNodeBusy]);

  // Jumps a postflop street back to wherever solving actually stands on it right now
  // (i.e. the node right after its last recorded action, or its very start if none yet).
  // This is what the row's trailing "Current" tile / the street tabs use to escape review mode.
  const handleReturnToFrontier = useCallback((street) => {
    if (street === "preflop") {
      setSelectedNavigation({ street: null, index: -1 });
      return;
    }
    if (!USE_V6) {
      setSelectedNavigation({ street: null, index: -1 });
      return;
    }
    const arr = street === "flop" ? flopActionHistory : street === "turn" ? turnActionHistory : street === "river" ? riverActionHistory : [];
    const rootNodeId = street === "flop" ? "root" : street === "turn" ? turnRootNodeIdRef.current : riverRootNodeIdRef.current;
    const nodeId = (arr.length > 0 ? arr[arr.length - 1].nextNodeId : null) || rootNodeId;
    setSelectedNavigation({ street: null, index: -1 });
    if (nodeId) setV6NodeId(nodeId);
  }, [flopActionHistory, turnActionHistory, riverActionHistory]);

  const handleSelectStreetTab = useCallback((street) => {
    if (street === "preflop") {
      if (currentStreet === "preflop") {
        setSelectedNavigation({ street: null, index: -1 });
      } else {
        setSelectedNavigation({ street: "preflop", index: Math.max(0, preflopHistory.length - 1) });
      }
      return;
    }
    if (street === currentStreet) {
      setSelectedNavigation({ street: null, index: -1 });
      return;
    }
    handleReturnToFrontier(street);
  }, [currentStreet, preflopHistory.length, handleReturnToFrontier]);

  const navigateToLastPreflopDecision = useCallback(() => {
    const boxes = preflopState?.decisionBoxes || [];
    for (let i = boxes.length - 1; i >= 0; i--) {
      if (boxes[i]?.action != null) {
        handleNavigate("preflop", i);
        return true;
      }
    }
    if (preflopHistory.length > 0) {
      handleNavigate("preflop", Math.max(0, preflopHistory.length - 1));
      return true;
    }
    return false;
  }, [preflopState?.decisionBoxes, preflopHistory.length, handleNavigate]);

  /** Step one decision earlier on the whole-hand table (Back control). */
  const handleTableBack = useCallback(() => {
    const street = selectedNavigation.street || viewingStreet;
    const idx = selectedNavigation.street != null && selectedNavigation.index >= 0
      ? selectedNavigation.index
      : -1;

    const goPreflopLast = () => navigateToLastPreflopDecision();

    if (street === "preflop") {
      if (idx < 0) {
        goPreflopLast();
        return;
      }
      if (idx > 0) handleNavigate("preflop", idx - 1);
      return;
    }

    const histFor = (s) => (
      s === "flop" ? flopActionHistory
        : s === "turn" ? turnActionHistory
          : s === "river" ? riverActionHistory
            : []
    );
    const hist = histFor(street);

    if (idx < 0) {
      if (hist.length > 0) {
        handleNavigate(street, hist.length - 1);
        return;
      }
      if (street === "river") {
        if (turnActionHistory.length) handleNavigate("turn", turnActionHistory.length - 1);
        else if (flopActionHistory.length) handleNavigate("flop", flopActionHistory.length - 1);
        else goPreflopLast();
        return;
      }
      if (street === "turn") {
        if (flopActionHistory.length) handleNavigate("flop", flopActionHistory.length - 1);
        else goPreflopLast();
        return;
      }
      if (street === "flop") goPreflopLast();
      return;
    }

    if (idx > 0) {
      handleNavigate(street, idx - 1);
      return;
    }
    // First action on this street → previous street's last decision
    if (street === "river") {
      if (turnActionHistory.length) handleNavigate("turn", turnActionHistory.length - 1);
      else if (flopActionHistory.length) handleNavigate("flop", flopActionHistory.length - 1);
      else goPreflopLast();
      return;
    }
    if (street === "turn") {
      if (flopActionHistory.length) handleNavigate("flop", flopActionHistory.length - 1);
      else goPreflopLast();
      return;
    }
    if (street === "flop") goPreflopLast();
  }, [
    selectedNavigation.street,
    selectedNavigation.index,
    viewingStreet,
    flopActionHistory,
    turnActionHistory,
    riverActionHistory,
    handleNavigate,
    navigateToLastPreflopDecision,
  ]);

  useEffect(() => {
    // V3-only street transition (V6 uses explicit chance nodes)
    if (!USE_V6) {
      if (currentStreet === "flop" && needsStreetTransition(flopActionHistory)) {
        if (ensureTurnRiverAccess("turn")) setShowTurnModal(true);
      } else if (currentStreet === "turn" && needsStreetTransition(turnActionHistory)) {
        if (ensureTurnRiverAccess("river")) setShowRiverModal(true);
      }
    }
  }, [flopActionHistory, turnActionHistory, currentStreet, USE_V6, ensureTurnRiverAccess]);

  useEffect(() => {
    // Once a board-card chooser is up, clear pending tile-hide state for prior street.
    if (showTurnModal || showRiverModal) setPendingPostflopStreet(null);
  }, [showTurnModal, showRiverModal]);

  useEffect(() => {
    if (preflopHistory.length > 0) {
      const voluntaryParticipants = getPreflopVoluntaryParticipants(preflopHistory);
      const isMultiwayPreflop = preflopState.isComplete && voluntaryParticipants.size > 2;
      if (isMultiwayPreflop) {
        if (detectedSpot) setDetectedSpot(null);
        if (showFlopModal) setShowFlopModal(false);
        setGridOverlayMessage("PokerMoose does not have solutions for multiway action preflop.");
        return;
      }
      if (gridOverlayMessage === "PokerMoose does not have solutions for multiway action preflop.") {
        setGridOverlayMessage(null);
      }

      const spotKey = actionHistoryToSpotKey(preflopHistory);
      let matchedSpot = SPOT_MAPPING[spotKey];
      // Explicit SPOT_MAPPING first, then generic 4-bet/3-bet/open-call pots.
      if (!matchedSpot) {
        matchedSpot = detectFourBetCallSpotFromPreflopHistory(preflopHistory);
      }
      if (!matchedSpot) {
        matchedSpot = detectThreeBetCallSpotFromPreflopHistory(preflopHistory);
      }
      if (!matchedSpot) {
        matchedSpot = detectOpenCallSpotFromPreflopHistory(preflopHistory);
      }
      if (matchedSpot?.spot) {
        matchedSpot = { ...matchedSpot, spot: mapPostflopLookupSpot(matchedSpot.spot) };
      }
      if (matchedSpot && !isSameDetectedSpot(matchedSpot, detectedSpot)) setDetectedSpot(matchedSpot);
      else if (!matchedSpot && detectedSpot) setDetectedSpot(null);

      // Flop picker: spot resolved and true preflop betting round complete (all seats acted / matched).
      if (preflopState.isComplete && matchedSpot && !showFlopModal && flop.length === 0 && !suppressFlopModalRef.current) {
        setShowFlopModal(true);
      }
    }
  }, [preflopHistory, preflopState.isComplete, detectedSpot, showFlopModal, flop.length, gridOverlayMessage]);

  // Trainer "Solve this spot" deep-link: restore preflop/board/node into /cash.
  React.useEffect(() => {
    if (blankMode) return undefined;
    let raw = null;
    try {
      raw = sessionStorage.getItem(TRAINER_SOLVE_HANDOFF_KEY);
      if (raw) sessionStorage.removeItem(TRAINER_SOLVE_HANDOFF_KEY);
    } catch (_) {
      raw = null;
    }
    if (!raw) return undefined;
    let handoff = null;
    try {
      handoff = JSON.parse(raw);
    } catch (_) {
      return undefined;
    }
    if (!handoff || Number(handoff.version) !== 1) return undefined;

    // Don't interrupt a Trainer → Solver handoff with the first-run tutorial.
    suppressTutorialFromTrainerRef.current = true;
    setTutorialOpen(false);
    setTutorialStep(0);

    const pf = Array.isArray(handoff.preflopHistory)
      ? handoff.preflopHistory.map((h) => ({
          position: String(h?.position || "").toLowerCase(),
          action: String(h?.action || "").toLowerCase(),
          raiseSize: h?.raiseSize ?? null,
        }))
      : [];
    const street = String(handoff.street || "preflop").toLowerCase();
    if (Array.isArray(handoff.userHand) && handoff.userHand[0] && handoff.userHand[1]) {
      setUserHand([handoff.userHand[0], handoff.userHand[1]]);
    }
    if (street !== "preflop") suppressFlopModalRef.current = true;
    setPreflopHistory(pf);
    if (street === "preflop") {
      pendingTrainerHandoffRef.current = null;
      return undefined;
    }
    pendingTrainerHandoffRef.current = handoff;
    return undefined;
  }, [blankMode]);

  const handleFlopSelect = async (cards) => {
    // UI LOCK: one flop selection at a time.
    if (nodeBusyRef.current) return;
    if (!blankMode && isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    beginNodeBusy();
    // New board tree: previously cached nodes/root pointers no longer apply.
    nodeCacheRef.current.clear();
    textureCacheRef.current.clear();
    turnRootNodeIdRef.current = null;
    riverRootNodeIdRef.current = null;
    // Store user's original flop for display
    setFlop(cards);
    setError(null);
    setGridOverlayMessage(null);

    // Canonicalize user input to 1755-key space (keeps suit-isomorphism pipeline).
    const { canonicalCards: requestedCanonical, canonicalStr: requestedStr } = canonicalizeUserFlopTo1755(cards);
    setRequestedCanonicalFlop(requestedCanonical);

    // Server-side mapping: canonical(1755) -> queried flop in DB.
    const queriedFlop = await lookupQueriedFlopFromApi(requestedStr);
    const queriedCards = queriedFlop ? flopStrToCards(queriedFlop) : null;
    setBoardLookup({ requestedFlopStr: requestedStr, candidates: queriedFlop ? [queriedFlop] : [], idx: 0 });

    if (!queriedCards || queriedCards.length !== 3) {
      // Blank/unmapped entries should render explicit message over hand grid.
      setCanonicalFlop([]);
      setGridData(null);
      setActions([]);
      setGridOverlayMessage("Something went wrong please try again");
      // Keep mappings initialized from desired->canonical key so turn/river logic remains deterministic.
      setSuitMapping(createSuitMappingBetweenFlops(cards, requestedCanonical));
      // No grid fetch will follow, so release the UI lock here.
      endNodeBusy();
    } else {
      setCanonicalFlop(queriedCards);
      setSuitMapping(createSuitMappingBetweenFlops(cards, queriedCards));

      // Record for the Home dashboard's "Recent Solves" card.
      try {
        const KEY = "pm_recent_solves_v1";
        const raw = window.localStorage.getItem(KEY);
        const prev = raw ? JSON.parse(raw) : [];
        const entry = {
          id: `${Date.now()}_${cards.join("")}`,
          spot: detectedSpot?.spot || null,
          spotName: detectedSpot?.name || null,
          flop: cards,
          mode: lookupMode,
          at: new Date().toISOString(),
        };
        const next = [entry, ...(Array.isArray(prev) ? prev : [])].slice(0, 12);
        window.localStorage.setItem(KEY, JSON.stringify(next));
      } catch (_) {}
    }
    
    setShowFlopModal(false);
    setCurrentStreet("flop");
    setStreetStartPot({ flop: null, turn: null, river: null });
    const handoffNodeId = pendingTargetNodeIdRef.current;
    if (handoffNodeId) {
      pendingTargetNodeIdRef.current = null;
      setV6NodeId(String(handoffNodeId));
    } else {
      setV6NodeId("root");
    }
    const preflopRangesByPos = buildPreflopRangesFromHistory(preflopHistory || []);
    const seededRanges = { 0: {}, 1: {} };
    if (detectedSpot?.positions) {
      const bySeat = {};
      for (const [pos, rangeMap] of Object.entries(preflopRangesByPos)) {
        const seat = SEAT_CODE[pos];
        if (seat) bySeat[seat] = rangeMap || {};
      }
      seededRanges[0] = bySeat[detectedSpot.positions.oop] || {};
      seededRanges[1] = bySeat[detectedSpot.positions.ip] || {};
    }
    setPlayerRanges(seededRanges);
    setCurrentDecisionActionRanges(null);
    setCurrentDecisionPlayer(null);
    
    console.log("User Flop:", cards.join(""), "-> Canonical1755:", requestedStr, "-> Queried:", queriedFlop || "(none)");
  };

  // Apply postflop board/node from Trainer "Solve this spot" once the spot is detected.
  React.useEffect(() => {
    const handoff = pendingTrainerHandoffRef.current;
    if (!handoff || blankMode) return undefined;
    if (!detectedSpot) return undefined;
    if (flop.length >= 3) return undefined;
    const board = Array.isArray(handoff.boardCards) ? handoff.boardCards.filter(Boolean) : [];
    if (board.length < 3) {
      pendingTrainerHandoffRef.current = null;
      suppressFlopModalRef.current = false;
      return undefined;
    }

    let cancelled = false;
    (async () => {
      pendingTrainerHandoffRef.current = null;
      pendingTargetNodeIdRef.current = handoff.nodeId ? String(handoff.nodeId) : null;
      const street = String(handoff.street || "flop").toLowerCase();
      try {
        await handleFlopSelect(board.slice(0, 3));
      } catch (_) {}
      if (cancelled) return;
      if (board[3]) setTurn(board[3]);
      if (board[4]) setRiver(board[4]);
      setCurrentStreet(street === "turn" || street === "river" ? street : "flop");
      setShowFlopModal(false);
      setShowTurnModal(false);
      setShowRiverModal(false);
      suppressFlopModalRef.current = false;
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally depends on spot becoming available after Trainer handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blankMode, detectedSpot, flop.length]);
  
  const handleTurnSelect = (card) => {
    // UI LOCK: ignore card picks while a load is in flight.
    if (nodeBusyRef.current) return;
    if (!blankMode && isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    beginNodeBusy();
    // Store user's original turn for display
    setTurn(card);
    
    // Convert to queried-space for API calls (pair-aware rank map + texture match).
    const canonicalCard = resolveRunoutCanonicalCard(card, flop, canonicalFlop, suitMapping, flopRankMap);
    setCanonicalTurn(canonicalCard);
    
    setShowTurnModal(false);
    setLastTurnPickAt(Date.now());
    setCurrentStreet("turn");
    setStreetStartPot((prev) => ({ ...prev, turn: null, river: null }));
    setPlayer(0);
    
    const flopPath = buildPathFromHistory(flopActionHistory);
    setPath(`${flopPath}|${canonicalCard}`);

    // V6: if we're at a chance node and have mapping, advance via nodeId.
    if (USE_V6 && v6Nav?.kind === "chance" && v6Nav?.chanceChildren?.[canonicalCard]?.nodeId) {
      const nextNodeId = v6Nav.chanceChildren[canonicalCard].nodeId;
      turnRootNodeIdRef.current = nextNodeId;
      setV6NodeId(nextNodeId);
    }
    
    console.log("User Turn:", card, "-> Canonical:", canonicalCard);
  };
  
  const handleRiverSelect = (card) => {
    // UI LOCK: ignore card picks while a load is in flight.
    if (nodeBusyRef.current) return;
    if (!blankMode && isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    beginNodeBusy();
    // Store user's original river for display
    setRiver(card);
    
    // Convert to queried-space for API calls (pair-aware rank map + texture match).
    const desiredBoardSoFar = [...(flop || []), ...(turn ? [turn] : [])];
    const queriedBoardSoFar = [...(canonicalFlop || []), ...(canonicalTurn ? [canonicalTurn] : [])];
    const canonicalCard = resolveRunoutCanonicalCard(card, desiredBoardSoFar, queriedBoardSoFar, suitMapping, flopRankMap);
    setCanonicalRiver(canonicalCard);
    
    setShowRiverModal(false);
    setCurrentStreet("river");
    setStreetStartPot((prev) => ({ ...prev, river: null }));
    setPlayer(0);
    
    const flopPath = buildPathFromHistory(flopActionHistory);
    const turnPath = buildPathFromHistory(turnActionHistory);
    setPath(`${flopPath}|${canonicalTurn}${turnPath ? `-${turnPath}` : ""}|${canonicalCard}`);

    // V6: if we're at a chance node and have mapping, advance via nodeId.
    if (USE_V6 && v6Nav?.kind === "chance" && v6Nav?.chanceChildren?.[canonicalCard]?.nodeId) {
      const nextNodeId = v6Nav.chanceChildren[canonicalCard].nodeId;
      riverRootNodeIdRef.current = nextNodeId;
      setV6NodeId(nextNodeId);
    }
    
    console.log("User River:", card, "-> Canonical:", canonicalCard);
  };

  const resolveTextureMapFromApi = useCallback(
    async ({ rawGrid, spot, flopStr, desiredBoard, queriedBoard }) => {
      if (!Array.isArray(rawGrid) || !desiredBoard?.length || !queriedBoard?.length) return null;
      const seen = new Set();
      const comboRequests = [];
      for (const row of rawGrid || []) {
        for (const cell of row || []) {
          const label = cell?.label;
          if (!label) continue;
          for (const desiredCombo4 of generateAllCombos(label)) {
            if (seen.has(desiredCombo4)) continue;
            seen.add(desiredCombo4);
            comboRequests.push({
              desiredCombo4,
              preferredApiHand: comboDesiredToApiLookup(desiredCombo4, suitMapping, flopRankMap),
            });
          }
        }
      }
      if (!comboRequests.length) return null;

      const body = {
        spot,
        versionId: SOLVER_V6_VERSION_ID,
        flop: flopStr,
        nodeId: USE_V6 ? v6NodeId : "root",
        mode: lookupMode,
        tableSize: lookupContext.tableSize,
        stackSize: lookupStackSize,
        openSize: lookupContext.openSize,
        desiredBoardCards: desiredBoard,
        queriedBoardCards: queriedBoard,
        comboRequests,
      };
      const res = await fetch(`${SOLVER_API_URL}/api/v6/texture-resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || !payload?.success) return null;
      return payload.textureByCombo || null;
    },
    [suitMapping, flopRankMap, v6NodeId, lookupMode, lookupStackSize, lookupContext]
  );

  const fetchGrid = useCallback(async () => {
    if (blankMode) {
      setGridData(null);
      setActions([]);
      setTextureResolutionMap(null);
      endNodeBusy();
      return;
    }
    if (!detectedSpot || canonicalFlop.length < 3) {
      endNodeBusy();
      return;
    }
    beginNodeBusy();
    // Invalidate any in-flight texture request from a previous node right away,
    // so its completion can neither patch the grid nor unlock the UI early.
    textureRequestIdRef.current += 1;
    let textureOwnsUnlock = false;
    // History navigation frequently re-visits the same nodeId (e.g. clicking back and
    // forth between decisions). Serve those instantly from cache instead of re-hitting the API.
    const cacheKey = USE_V6 ? String(v6NodeId || "root") : null;
    const cachedData = cacheKey ? nodeCacheRef.current.get(cacheKey) : null;
    setLoading(!cachedData); setError(null);
    try {
      // Use canonical flop/turn/river for API call
      const flopStr = canonicalFlop.join("");
      
      let url;
      if (USE_V6) {
        const qs = new URLSearchParams({
          spot: String(detectedSpot.spot || "").toLowerCase(),
          versionId: SOLVER_V6_VERSION_ID,
          flop: flopStr,
          nodeId: String(v6NodeId || "root"),
          mode: lookupContext.mode,
          tableSize: lookupContext.tableSize,
          stackSize: lookupContext.stackSize,
          openSize: lookupContext.openSize,
        });
        url = `${SOLVER_API_URL}/api/v6/grid?${qs.toString()}`;
      } else {
        url = `${SOLVER_API_URL}/api/v3/grid?spot=${detectedSpot.spot}&flop=${flopStr}&path=${path}&player=${player}`;
        if (canonicalTurn) url += `&turn=${canonicalTurn}`;
        if (canonicalRiver) url += `&river=${canonicalRiver}`;
      }

      let data = null;
      let responseOk = true;
      let responseStatus = 200;
      if (cachedData) {
        data = cachedData;
      } else {
        console.log("Fetching grid with canonical flop:", flopStr, "turn:", canonicalTurn, "river:", canonicalRiver);
        const response = await fetch(url, { credentials: "include" });
        responseOk = response.ok;
        responseStatus = response.status;
        try {
          data = await response.json();
        } catch (_) {
          data = null;
        }
        if (responseOk && data?.success && cacheKey) {
          nodeCacheRef.current.set(cacheKey, data);
        }
      }

      // Hard fail if the exact board isn't present in this spot/version.
      if (!responseOk) {
        const errMsg = data?.error || `HTTP ${responseStatus}`;
        // Quota exhausted; show paywall modal instead of generic error.
        if (responseStatus === 402 || data?.code === "QUOTA_EXHAUSTED") {
          if (data?.quotaInfo) setFlopQuotaInfo(data.quotaInfo);
          setShowFlopQuotaModal(true);
          setLoading(false);
          return;
        }
        // Not authenticated; prompt login.
        if (responseStatus === 401 || data?.code === "UNAUTHENTICATED") {
          window.dispatchEvent(new CustomEvent("pm:require-auth", { detail: { stage: "postflop" } }));
          setLoading(false);
          return;
        }
        if (responseStatus === 404 || /not found/i.test(errMsg)) {
          setError(`Board does not exist in solutions (${detectedSpot.spot}/${SOLVER_V6_VERSION_ID}/${flopStr})`);
          return;
        }
        setError(errMsg);
        return;
      }
      // Update quota info from a successful response (backend sends remaining count).
      if (data?.quotaInfo) setFlopQuotaInfo(data.quotaInfo);

      if (data.success) {
        // Turn/river paywall: if the next node needs a runout card the user can't pick,
        // stay on the prior street decision so the mid-solve flop/turn investigation persists.
        if (USE_V6 && data.v6?.kind === "chance") {
          const needsTurnPick = data.street === 0 && !canonicalTurn;
          const needsRiverPick = data.street === 1 && !canonicalRiver;
          if (needsTurnPick && !ensureTurnRiverAccess("turn")) {
            const hist = flopActionHistoryRef.current || [];
            const last = hist[hist.length - 1];
            const stayId = last?.decisionNodeId ? String(last.decisionNodeId) : null;
            if (hist.length) setFlopActionHistory(hist.slice(0, -1));
            setShowTurnModal(false);
            setPendingPostflopStreet(null);
            setSelectedNavigation({ street: null, index: -1 });
            setLoading(false);
            if (stayId && stayId !== String(v6NodeId || "")) {
              setV6NodeId(stayId);
            } else {
              endNodeBusy();
            }
            return;
          }
          if (needsRiverPick && !ensureTurnRiverAccess("river")) {
            const hist = turnActionHistoryRef.current || [];
            const last = hist[hist.length - 1];
            const stayId = last?.decisionNodeId ? String(last.decisionNodeId) : null;
            if (hist.length) setTurnActionHistory(hist.slice(0, -1));
            setShowRiverModal(false);
            setPendingPostflopStreet(null);
            setSelectedNavigation({ street: null, index: -1 });
            setLoading(false);
            if (stayId && stayId !== String(v6NodeId || "")) {
              setV6NodeId(stayId);
            } else {
              endNodeBusy();
            }
            return;
          }
        }
        if (USE_V6 && data.v6?.kind === "terminal") {
          if (!canonicalTurn && !ensureTurnRiverAccess("turn")) {
            const hist = flopActionHistoryRef.current || [];
            const last = hist[hist.length - 1];
            const stayId = last?.decisionNodeId ? String(last.decisionNodeId) : null;
            if (hist.length) setFlopActionHistory(hist.slice(0, -1));
            setShowTurnModal(false);
            setPendingPostflopStreet(null);
            setSelectedNavigation({ street: null, index: -1 });
            setLoading(false);
            if (stayId && stayId !== String(v6NodeId || "")) {
              setV6NodeId(stayId);
            } else {
              endNodeBusy();
            }
            return;
          }
          if (canonicalTurn && !canonicalRiver && !ensureTurnRiverAccess("river")) {
            const hist = turnActionHistoryRef.current || [];
            const last = hist[hist.length - 1];
            const stayId = last?.decisionNodeId ? String(last.decisionNodeId) : null;
            if (hist.length) setTurnActionHistory(hist.slice(0, -1));
            setShowRiverModal(false);
            setPendingPostflopStreet(null);
            setSelectedNavigation({ street: null, index: -1 });
            setLoading(false);
            if (stayId && stayId !== String(v6NodeId || "")) {
              setV6NodeId(stayId);
            } else {
              endNodeBusy();
            }
            return;
          }
        }

        setGridOverlayMessage(null);
        const nextActions = data.actions || [];
        const actingPlayer = (data.player === 0 || data.player === 1) ? data.player : player;
        const incomingRange = rangeMapWithPositiveWeights(playerRanges?.[actingPlayer]);
        const desiredBoard = [
          ...(flop || []),
          ...(turn ? [turn] : []),
          ...(river ? [river] : []),
        ].filter(Boolean);
        const queriedBoard = [
          ...(canonicalFlop || []),
          ...(canonicalTurn ? [canonicalTurn] : []),
          ...(canonicalRiver ? [canonicalRiver] : []),
        ].filter(Boolean);
        // SPEED IMPROVEMENT #1: paint the grid immediately after the grid data
        // is available. Texture resolution (a second, slower API round-trip)
        // runs in the background and patches the grid when it arrives, instead
        // of blocking the first paint. Cached texture maps apply synchronously.
        const cachedTexture = cacheKey && textureCacheRef.current.has(cacheKey)
          ? textureCacheRef.current.get(cacheKey)
          : undefined;
        const needsTexture =
          currentStreet !== "preflop" && desiredBoard.length >= 3 && queriedBoard.length >= 3;
        const initialTextureMap = needsTexture && cachedTexture !== undefined ? cachedTexture : null;

        setTextureResolutionMap(initialTextureMap);
        const carryPack = buildCarryRangeFromRawGrid(data.grid, suitMapping, flopRankMap, nextActions, incomingRange, {
          desiredBoard,
          queriedBoard,
          textureResolutionMap: initialTextureMap,
        });
        setGridData(data.grid);
        setActions(nextActions);
        setCurrentDecisionActionRanges(carryPack.actionRanges || null);
        setCurrentDecisionPlayer(actingPlayer);
        if (!incomingRange) {
          setPlayerRanges((prev) => ({ ...prev, [actingPlayer]: carryPack.baseResolvedRange || {} }));
        }

        if (needsTexture && cachedTexture === undefined) {
          const textureRequestId = textureRequestIdRef.current;
          // UI LOCK: on decision nodes the controls stay locked until texture
          // resolution finishes, so users always act on the final grid state.
          // Chance/terminal nodes unlock immediately (their next interaction is
          // a card-picker modal, which texture data doesn't affect).
          if (data.v6?.kind === "decision" || !USE_V6) textureOwnsUnlock = true;
          resolveTextureMapFromApi({
            rawGrid: data.grid,
            spot: String(detectedSpot.spot || "").toLowerCase(),
            flopStr,
            desiredBoard,
            queriedBoard,
          })
            .then((textureMap) => {
              if (cacheKey) textureCacheRef.current.set(cacheKey, textureMap);
              // Ignore stale responses if the user has already moved to another node.
              if (textureRequestIdRef.current !== textureRequestId || !textureMap) return;
              setTextureResolutionMap(textureMap);
              const texturedCarryPack = buildCarryRangeFromRawGrid(data.grid, suitMapping, flopRankMap, nextActions, incomingRange, {
                desiredBoard,
                queriedBoard,
                textureResolutionMap: textureMap,
              });
              setCurrentDecisionActionRanges(texturedCarryPack.actionRanges || null);
              if (!incomingRange) {
                setPlayerRanges((prev) => ({ ...prev, [actingPlayer]: texturedCarryPack.baseResolvedRange || {} }));
              }
            })
            .catch(() => {})
            .finally(() => {
              // Only the latest request may unlock the UI.
              if (textureRequestIdRef.current === textureRequestId) endNodeBusy();
            });
        }
        setNodeInfo({ playerName: data.header?.playerName, streetName: data.header?.streetName, pot: data.pot || data.header?.pot });
        {
          const p = Number(data.pot ?? data.header?.pot);
          if (Number.isFinite(p)) {
            let streetKey = currentStreet;
            if (USE_V6) {
              if (data.street === 0) streetKey = "flop";
              else if (data.street === 1) streetKey = "turn";
              else if (data.street === 2) streetKey = "river";
            }
            if (streetKey === "flop" || streetKey === "turn" || streetKey === "river") {
              setStreetStartPot((prev) => (prev[streetKey] == null ? { ...prev, [streetKey]: p } : prev));
            }
          }
        }
        if (data.grid?.[0]?.[0]) {
          setSelectedCell((prev) =>
            shouldApplyDefaultSelection
              ? pickPreferredSelectedCell(data.grid, userHand, prev)
              : (prev && typeof prev.row === "number" && typeof prev.col === "number" && data.grid?.[prev.row]?.[prev.col]
                ? { row: prev.row, col: prev.col, cell: data.grid[prev.row][prev.col] }
                : prev)
          );
          if (shouldApplyDefaultSelection) setShouldApplyDefaultSelection(false);
        }
        if (USE_V6) {
          setV6Nav(data.v6 || null);
          // If API returned root node id, sync it so subsequent calls work.
          if (v6NodeId === "root" && data.nodeId) setV6NodeId(data.nodeId);
          // V6 source of truth: update whose turn + street from API.
          if (data.v6?.kind !== "terminal") {
            if (data.player === 0 || data.player === 1) {
              setPlayer(data.player);
            }
            if (data.street === 0) setCurrentStreet("flop");
            else if (data.street === 1) setCurrentStreet("turn");
            else if (data.street === 2) setCurrentStreet("river");
          }

          // If this node is a chance node, prompt for the next street card.
          // Chance-node street is the *current* street (flop=0 => selecting TURN, turn=1 => selecting RIVER).
          if (data.v6?.kind === "chance") {
            if (data.street === 0 && !canonicalTurn) {
              if (ensureTurnRiverAccess("turn")) setShowTurnModal(true);
            } else if (data.street === 1 && !canonicalRiver) {
              if (ensureTurnRiverAccess("river")) setShowRiverModal(true);
            }
          }

          // If we reached a terminal node before river (e.g. all-in + call),
          // still allow selecting runout cards so the UI can test full street flow.
          if (data.v6?.kind === "terminal") {
            // Prefer using known selected cards rather than currentStreet (which we don't update for terminal nodes).
            if (!canonicalTurn) {
              if (ensureTurnRiverAccess("turn")) setShowTurnModal(true);
            } else if (!canonicalRiver) {
              // Avoid immediately stacking modals: if the user just picked the turn card,
              // wait a moment so they can see the board update, then prompt for river.
              const msSinceTurnPick = Date.now() - (lastTurnPickAt || 0);
              const delayMs = msSinceTurnPick < 1200 ? 650 : 0;
              if (modalTimeoutRef.current) clearTimeout(modalTimeoutRef.current);
              modalTimeoutRef.current = setTimeout(() => {
                if (ensureTurnRiverAccess("river")) setShowRiverModal(true);
              }, delayMs);
            }
          }
          // Action tile should stay hidden until either:
          // - a new decision node is ready, or
          // - a card chooser modal opens.
          if (data.v6?.kind === "decision") {
            setPendingPostflopStreet(null);
          }
        } else {
          // V3: once response has loaded, next action tile can reappear.
          setPendingPostflopStreet(null);
        }
        if (pendingAuthGateAfterFlopActionRef.current) {
          pendingAuthGateAfterFlopActionRef.current = false;
          window.dispatchEvent(new CustomEvent("pm:require-auth", { detail: { stage: "postflop_after_first_flop_action" } }));
        }
      } else {
        setTextureResolutionMap(null);
        const errMsg = data?.error || "Unknown error";
        if (/not found/i.test(errMsg)) {
          setError(`Board does not exist in solutions (${detectedSpot.spot}/${SOLVER_V6_VERSION_ID}/${flopStr})`);
        } else {
          setError(errMsg);
        }
      }
    } catch (err) {
      setTextureResolutionMap(null);
      setError(err.message);
      setPendingPostflopStreet(null);
      textureOwnsUnlock = false;
    }
    finally {
      setLoading(false);
      if (!textureOwnsUnlock) endNodeBusy();
    }
  }, [
    detectedSpot,
    canonicalFlop,
    path,
    player,
    canonicalTurn,
    canonicalRiver,
    suitMapping,
    flopRankMap,
    v6NodeId,
    USE_V6,
    currentStreet,
    lastTurnPickAt,
    userHand,
    flop,
    turn,
    river,
    lookupContext,
    blankMode,
    resolveTextureMapFromApi,
    ensureTurnRiverAccess,
    beginNodeBusy,
    endNodeBusy,
  ]);

  useEffect(() => {
    // V3-only: Don't fetch if we need a street transition but don't have the required card yet.
    // V6 uses explicit chance nodes + children mapping, so we must still fetch even without turn/river selected.
    if (!USE_V6) {
      if (currentStreet === "flop" && needsStreetTransition(flopActionHistory) && !canonicalTurn) {
        return; // Wait for turn card to be selected
      }
      if (currentStreet === "turn" && needsStreetTransition(turnActionHistory) && !canonicalRiver) {
        return; // Wait for river card to be selected
      }
    }
    if (canonicalFlop.length === 3 && detectedSpot) fetchGrid();
  }, [fetchGrid, canonicalFlop, detectedSpot, path, canonicalTurn, canonicalRiver, currentStreet, flopActionHistory, turnActionHistory]);

  const handlePostflopAction = (action, actionPlayer) => {
    // UI LOCK: ignore clicks while the previous node is still loading.
    if (nodeBusyRef.current) return;
    if (!blankMode && isPaywallRequired()) {
      openSubscriptionGate("solver");
      return;
    }
    if (tutorialOpen && Array.isArray(tutorialActionAllowlist) && !tutorialActionMatches(action, tutorialActionAllowlist)) {
      return;
    }
    beginNodeBusy();
    setShouldApplyDefaultSelection(true);
    if (currentStreet === "flop") pendingAuthGateAfterFlopActionRef.current = true;
    // Hide "next action" tile immediately after click until next state is ready.
    setPendingPostflopStreet(currentStreet);
    if (currentStreet === "flop") setStreetStartPot((prev) => ({ ...prev, turn: null, river: null }));
    else if (currentStreet === "turn") setStreetStartPot((prev) => ({ ...prev, river: null }));
    // Capture brought-through range for the acting player based on selected action.
    if (currentDecisionActionRanges && (currentDecisionPlayer === 0 || currentDecisionPlayer === 1)) {
      const nextRange = currentDecisionActionRanges[action] || {};
      setPlayerRanges((prev) => ({ ...prev, [currentDecisionPlayer]: nextRange }));
    }

    let v6NextNodeId = null;
    if (USE_V6 && v6Nav?.kind === "decision") {
      const idx = actions.indexOf(action);
      const next = idx >= 0 ? v6Nav.actionChildren?.[idx] : null;
      if (next) {
        v6NextNodeId = next;
        setV6NodeId(next);
      } else {
        console.warn("V6: reached terminal or missing child for action", action);
        // No navigation will happen, so don't leave the UI locked.
        endNodeBusy();
      }
    } else {
      const segment = actionToPathSegment(action);
      setPath(path === "root" ? segment : `${path}-${segment}`);
      setPlayer(player === 0 ? 1 : 0);
    }
    // For V6, `player` is updated from API and represents the acting player at this node.
    const actionEntry = {
      action,
      player,
      // Decision node is where this action was chosen; used for tile review navigation.
      decisionNodeId: USE_V6 ? String(v6NodeId || "root") : null,
      // Next node is the response node after this action.
      nextNodeId: v6NextNodeId,
    };
    if (currentStreet === "flop") {
      // Replace the reviewed decision (and discard everything after it) instead of
      // appending a second entry alongside the original one.
      const nextHist =
        selectedNavigation.street === "flop" && selectedNavigation.index >= 0
          ? [...flopActionHistory.slice(0, selectedNavigation.index), actionEntry]
          : [...flopActionHistory, actionEntry];
      setFlopActionHistory(nextHist);
      if (selectedNavigation.street === "flop" && selectedNavigation.index >= 0) {
        // Branching from an earlier flop decision invalidates everything downstream:
        // the turn/river cards themselves (they were dealt off a different flop line)
        // and their cached "frontier" node pointers.
        setTurnActionHistory([]);
        setRiverActionHistory([]);
        setTurn(null);
        setCanonicalTurn(null);
        setRiver(null);
        setCanonicalRiver(null);
        turnRootNodeIdRef.current = null;
        riverRootNodeIdRef.current = null;
      }
    } else if (currentStreet === "turn") {
      const nextHist =
        selectedNavigation.street === "turn" && selectedNavigation.index >= 0
          ? [...turnActionHistory.slice(0, selectedNavigation.index), actionEntry]
          : [...turnActionHistory, actionEntry];
      setTurnActionHistory(nextHist);
      if (selectedNavigation.street === "turn" && selectedNavigation.index >= 0) {
        setRiverActionHistory([]);
        setRiver(null);
        setCanonicalRiver(null);
        riverRootNodeIdRef.current = null;
      }
    } else if (currentStreet === "river") {
      const nextHist =
        selectedNavigation.street === "river" && selectedNavigation.index >= 0
          ? [...riverActionHistory.slice(0, selectedNavigation.index), actionEntry]
          : [...riverActionHistory, actionEntry];
      setRiverActionHistory(nextHist);
    }
    setSelectedNavigation({ street: null, index: -1 });
  };

  const isStreetWaitingForGrid = (street) => pendingPostflopStreet === street;
  const gridHeight = gridRef?.offsetHeight;
  const isCompactLayout = useMediaQuery("(max-width: 1024px)");
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const isCompactLandscape = isCompactLayout && isLandscape;
  const displayStreetPot = React.useMemo(() => {
    const flopPot = Number.isFinite(Number(streetStartPot?.flop)) ? Number(streetStartPot.flop) : null;
    const baseTurnPot = computeStreetEndPot(flopPot, flopActionHistory);
    const hasReachedTurn = !!canonicalTurn || turnActionHistory.length > 0 || riverActionHistory.length > 0 || currentStreet === "turn" || currentStreet === "river";

    const turnPotFromApi = Number(streetStartPot?.turn);
    const turnPot =
      hasReachedTurn
        ? (Number.isFinite(baseTurnPot) ? baseTurnPot : (Number.isFinite(turnPotFromApi) ? turnPotFromApi : null))
        : null;

    const baseRiverPot = computeStreetEndPot(turnPot, turnActionHistory);
    const hasReachedRiver = !!canonicalRiver || riverActionHistory.length > 0 || currentStreet === "river";
    const riverPotFromApi = Number(streetStartPot?.river);
    const riverPot =
      hasReachedRiver
        ? (Number.isFinite(baseRiverPot) ? baseRiverPot : (Number.isFinite(riverPotFromApi) ? riverPotFromApi : null))
        : null;

    return { flop: flopPot, turn: turnPot, river: riverPot };
  }, [
    streetStartPot,
    flopActionHistory,
    turnActionHistory,
    riverActionHistory,
    canonicalTurn,
    canonicalRiver,
    currentStreet,
  ]);

  const renderWholeHandTable = (opts = {}) => {
    const beginnerMode = !!opts.beginnerMode;
    const postflopActive =
      (viewingStreet === "flop" && flop.length === 3 && !showTurnModal)
      || (viewingStreet === "turn" && !!turn && !showRiverModal)
      || (viewingStreet === "river" && !!river);
    return (
      <Box
        ref={(el) => {
          tutorialPreflopRef.current = el;
          tutorialFlopRef.current = el;
          if (beginnerMode) tutorialTableRef.current = el;
        }}
        sx={{ flexShrink: beginnerMode ? 1 : 0, width: beginnerMode ? "100%" : undefined, minWidth: 0 }}
      >
        <WholeHandTable
          viewingStreet={viewingStreet}
          preflopHistory={preflopHistory}
          preflopState={preflopState}
          preflopViewState={preflopViewState}
          selectedNavigation={beginnerMode ? { street: null, index: -1 } : selectedNavigation}
          selectorActions={selectorActions}
          onPreflopAction={handlePreflopAction}
          onPostflopAction={handlePostflopAction}
          onNavigate={beginnerMode ? undefined : handleNavigate}
          onReturnToFrontier={beginnerMode ? undefined : handleReturnToFrontier}
          onBack={beginnerMode ? undefined : handleTableBack}
          disablePreflopActions={blankMode}
          player={player}
          positions={detectedSpot?.positions}
          flop={flop}
          turn={turn}
          river={river}
          flopActionHistory={flopActionHistory}
          turnActionHistory={turnActionHistory}
          riverActionHistory={riverActionHistory}
          displayStreetPot={displayStreetPot}
          isPostflopActive={postflopActive && !isViewingPreflop}
          isWaitingForGrid={isStreetWaitingForGrid(viewingStreet)}
          needsTurnCard={showTurnModal}
          needsRiverCard={showRiverModal}
          isMobile={isMobilePhone}
          readOnly={!beginnerMode}
          onHintClick={beginnerMode ? null : nudgeActionTile}
          beginnerMode={beginnerMode}
          heroSeatId={heroSeat}
          heroHoleCards={userHand?.[0] && userHand?.[1] ? userHand : null}
          handSelectorRef={beginnerMode ? tutorialHandRef : null}
          heroActionsRef={beginnerMode ? tutorialHeroActionsRef : null}
          actionAllowlist={beginnerMode ? tutorialActionAllowlist : null}
          onHeroSeatChange={beginnerMode ? handleHeroSeatChange : null}
          onReset={beginnerMode ? handleReset : null}
          heroHandProps={beginnerMode ? {
            selectedHand: userHand,
            onSelectHand: handleUserHandSelect,
            usedCards,
            selectedCell: selectedCell?.cell,
            actions: displayActions,
            isPreflop: isViewingPreflop,
            preflopFoldRed,
            gridData: resolutionGridData,
            suitMapping,
            flopRankMap,
            desiredBoardCards,
            queriedBoardCards,
            textureResolutionMap,
            activeRange: activeRangeForDisplay,
            requiredHand: null,
          } : null}
        />
      </Box>
    );
  };

  const chromeButtons = (
    <TutorialChromeButtons
      onTutorial={() => startTutorial({ firstRun: false, forceUnskippable: false })}
      layoutMode={solverLayoutMode}
      onLayoutModeChange={blankMode ? undefined : setLayoutMode}
      onReset={handleReset}
    />
  );

  const renderRangeAndCombosBlock = ({ compact = false, showToggle = false, showMaximise = false } = {}) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: compact ? 0.75 : 1.25,
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        fontFamily: compact ? FUTURA_BOOK : FUTURA_FONT,
      }}
    >
      {(showToggle || showMaximise) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.75, width: "100%" }}>
          {showToggle ? (
            <StrategyViewToggle
              value={viewMode}
              onChange={(e, v) => v && setViewMode(v)}
              evDisabled={isViewingPreflop}
              seatLabel={strategySeatLabel}
              containerRef={tutorialEvRef}
            />
          ) : <Box />}
          {showMaximise && (
            <IconButton
              size="small"
              onClick={() => setShowFullRangeDialog(true)}
              aria-label="Maximise range and combos"
              title="Maximise"
              sx={{
                color: COLORS.white,
                border: `1px solid ${COLORS.white}`,
                borderRadius: "6px",
                width: isMobilePhone ? 28 : 32,
                height: isMobilePhone ? 28 : 32,
                p: 0,
                flexShrink: 0,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 14,
                  height: 14,
                  border: "1.5px solid currentColor",
                  borderRadius: "2px",
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: -3,
                    bottom: -3,
                    width: 8,
                    height: 8,
                    borderRight: "1.5px solid currentColor",
                    borderBottom: "1.5px solid currentColor",
                  },
                }}
              />
            </IconButton>
          )}
        </Box>
      )}
      <Box
        ref={(el) => {
          if (solverLayoutMode === "table") {
            setGridRef(el);
            tutorialOutputRef.current = el;
          }
        }}
        sx={{ width: "100%", flexShrink: 0 }}
      >
        <HandMatrix
          gridData={displayGridData}
          actions={displayActions}
          loading={loading}
          overlayMessage={effectiveGridOverlayMessage}
          onOverlayFold={blankPreflopOverlayFold}
          onCellClick={handleCellClick}
          selectedCell={selectedCell}
          viewMode={viewMode}
          clickableAll={isViewingPreflop}
          isPreflop={isViewingPreflop}
          preflopFoldRed={preflopFoldRed}
          handReminderLabel={userHandLabel}
          showHandReminder={showHandReminder}
          postflopCarriedRange={activeRangeForDisplay}
          currentStreet={viewingStreet}
          demoFxEnabled={demoFxEnabled}
        />
        <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
      </Box>
      <Box
        ref={tutorialComboRef}
        sx={{
          minHeight: compact ? (isMobilePhone ? 180 : 200) : 240,
          height: compact ? (isMobilePhone ? 200 : 220) : { xs: 260, sm: "100%" },
          flex: compact ? "0 0 auto" : 1,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <ComboBreakdown
          cell={selectedCell?.cell}
          actions={displayActions}
          gridData={resolutionGridData}
          viewMode={viewMode}
          usedCards={usedCards}
          suitMapping={suitMapping}
          flopRankMap={flopRankMap}
          activeRange={activeRangeForDisplay}
          isPreflop={isViewingPreflop}
          preflopFoldRed={preflopFoldRed}
          reminderCombo={userHandCombo}
          showHandReminder={showHandReminder}
          desiredBoardCards={desiredBoardCards}
          queriedBoardCards={queriedBoardCards}
          textureResolutionMap={textureResolutionMap}
          isMobile={isMobilePhone}
          loading={loading}
          onComboSelect={handleComboSelect}
        />
      </Box>
    </Box>
  );

  const renderHeroControlPanel = () => (
    <Box
      sx={{
        width: isMobilePhone ? "100%" : 380,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        minHeight: 0,
        alignSelf: isMobilePhone ? "stretch" : "stretch",
        maxHeight: isMobilePhone ? "none" : "100%",
        overflow: isMobilePhone ? "visible" : "hidden",
        fontFamily: FUTURA_BOOK,
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: isMobilePhone ? "0 0 auto" : 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          ...THIN_SCROLLBAR_SX,
        }}
      >
        {renderRangeAndCombosBlock({ compact: true, showToggle: true, showMaximise: true })}
      </Box>
    </Box>
  );

  const renderTableModeLayout = () => (
    <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1, flex: 1, overflow: "auto", fontFamily: FUTURA_BOOK, ...THIN_SCROLLBAR_SX }}>
      {!blankMode && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 0.6, sm: 1.5 },
            width: "100%",
            minWidth: 0,
            // Keep chrome reachable on phone while scrolling the table/grids.
            position: { xs: "sticky", sm: "static" },
            top: { xs: 0, sm: "auto" },
            zIndex: { xs: 20, sm: "auto" },
            backgroundColor: { xs: COLORS.bg, sm: "transparent" },
            py: { xs: 0.35, sm: 0 },
          }}
        >
          {lookupMode === "cash" && (
            <Typography sx={{ color: COLORS.textMuted, fontSize: "11px", fontWeight: 400, fontFamily: FUTURA_BOOK, flexShrink: 0 }}>
              {`Cash ${String(lookupStackSize || "100bbs").toLowerCase()}`}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-start" },
              width: { xs: "100%", sm: "auto" },
              minWidth: 0,
              overflowX: "auto",
              ...THIN_SCROLLBAR_SX,
            }}
          >
            {chromeButtons}
          </Box>
        </Box>
      )}
      {isMobilePhone ? (
        <>
          {renderWholeHandTable({ beginnerMode: true })}
          {renderHeroControlPanel()}
        </>
      ) : (
        <Box sx={{ display: "flex", alignItems: "stretch", gap: 1.5, width: "100%", minWidth: 0, flex: 1, minHeight: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", minHeight: 0 }}>
            {renderWholeHandTable({ beginnerMode: true })}
          </Box>
          {renderHeroControlPanel()}
        </Box>
      )}
    </Box>
  );

  const renderCurrentStreetActions = () => {
    if (viewingStreet === "preflop") {
      return (
        <PreflopActionsRow
          actionHistory={preflopHistory}
          preflopState={preflopState}
          viewState={preflopViewState}
          selectorActions={isViewingPreflop ? selectorActions : []}
          onAction={handlePreflopAction}
          onNavigate={handleNavigate}
          onReturnToFrontier={handleReturnToFrontier}
          selectedIndex={selectedNavigation.street === "preflop" ? selectedNavigation.index : -1}
          disableActions={blankMode}
          isMobile={isMobilePhone}
          demoFxEnabled={demoFxEnabled}
          flashSignal={actionTileFlash}
        />
      );
    }
    if (viewingStreet === "flop") {
      return (
        <PostflopActionsRow
          title="Flop Actions"
          streetCards={flop}
          cardCount={3}
          pot={displayStreetPot.flop}
          solverActions={viewingStreet === "flop" && flop.length === 3 && !showTurnModal ? selectorActions : []}
          actionHistory={flopActionHistory}
          onAction={handlePostflopAction}
          isActive={viewingStreet === "flop" && flop.length === 3 && !showTurnModal}
          isWaitingForGrid={isStreetWaitingForGrid("flop")}
          isLiveStreet={liveStreet === "flop" && selectedNavigation.street !== "preflop"}
          player={player}
          positions={detectedSpot?.positions}
          onNavigate={handleNavigate}
          onReturnToFrontier={handleReturnToFrontier}
          street="flop"
          selectedActionIndex={selectedNavigation.street === "flop" ? selectedNavigation.index : -1}
          needsTurnCard={showTurnModal}
          isMobile={isMobilePhone}
          demoFxEnabled={demoFxEnabled}
          flashSignal={actionTileFlash}
        />
      );
    }
    if (viewingStreet === "turn") {
      return (
        <PostflopActionsRow
          title="Turn Actions"
          streetCards={turn ? [turn] : []}
          cardCount={1}
          pot={displayStreetPot.turn}
          solverActions={viewingStreet === "turn" && turn && !showRiverModal ? selectorActions : []}
          actionHistory={turnActionHistory}
          onAction={handlePostflopAction}
          isActive={viewingStreet === "turn" && turn && !showRiverModal}
          isWaitingForGrid={isStreetWaitingForGrid("turn")}
          isLiveStreet={liveStreet === "turn" && selectedNavigation.street !== "preflop"}
          player={player}
          positions={detectedSpot?.positions}
          onNavigate={handleNavigate}
          onReturnToFrontier={handleReturnToFrontier}
          street="turn"
          selectedActionIndex={selectedNavigation.street === "turn" ? selectedNavigation.index : -1}
          needsTurnCard={false}
          isMobile={isMobilePhone}
          demoFxEnabled={demoFxEnabled}
          flashSignal={actionTileFlash}
        />
      );
    }
    return (
      <PostflopActionsRow
        title="River Actions"
        streetCards={river ? [river] : []}
        cardCount={1}
        pot={displayStreetPot.river}
        solverActions={viewingStreet === "river" && river ? selectorActions : []}
        actionHistory={riverActionHistory}
        onAction={handlePostflopAction}
        isActive={viewingStreet === "river" && river}
        isWaitingForGrid={isStreetWaitingForGrid("river")}
        isLiveStreet={liveStreet === "river" && selectedNavigation.street !== "preflop"}
        player={player}
        positions={detectedSpot?.positions}
        onNavigate={handleNavigate}
        onReturnToFrontier={handleReturnToFrontier}
        street="river"
        selectedActionIndex={selectedNavigation.street === "river" ? selectedNavigation.index : -1}
        needsTurnCard={false}
        isMobile={isMobilePhone}
        demoFxEnabled={demoFxEnabled}
        flashSignal={actionTileFlash}
      />
    );
  };

  // Only flop/turn/river are tabbed here: the grid/strategy panel above is still driven
  // directly by `currentStreet`, so once preflop is done it stops being an independently
  // reviewable "tab" (its own row still supports click-to-review internally).
  const reachedStreets = React.useMemo(() => {
    const list = [];
    if (currentStreet === "preflop") list.push("preflop");
    if (flop.length === 3) list.push("flop");
    if (turn) list.push("turn");
    if (river) list.push("river");
    return list;
  }, [currentStreet, flop.length, turn, river]);

  const renderStreetTabs = () => {
    if (reachedStreets.length < 2) return null;
    return (
      <Box sx={{ display: "flex", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
        {reachedStreets.map((s) => {
          const isViewing = viewingStreet === s;
          const isLive = liveStreet === s;
          return (
            <Box
              key={s}
              onClick={() => handleSelectStreetTab(s)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                px: 0.9,
                py: 0.3,
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "8px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                border: `1px solid ${isViewing ? COLORS.white : COLORS.border}`,
                color: isViewing ? COLORS.white : COLORS.textMuted,
                backgroundColor: isViewing ? COLORS.cardBg : "transparent",
                "&:hover": { borderColor: COLORS.white, color: COLORS.white },
              }}
            >
              {s}
              {isLive && (
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#3ddc71", flexShrink: 0 }} />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      onClickCapture={handleDemoFxClickCapture}
      sx={{
        height: "100vh",
        backgroundColor: COLORS.bg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(demoFxEnabled
          ? {
              "& button, & .MuiToggleButton-root": {
                transition: "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 220ms ease, filter 220ms ease",
                transformOrigin: "center bottom",
                willChange: "transform",
              },
              "& button:hover, & .MuiToggleButton-root:hover": {
                transform: "translateY(-7px) scale(1.035)",
                boxShadow: "0 14px 30px rgba(0,0,0,0.45)",
                filter: "brightness(1.08)",
                zIndex: 2,
              },
              "& button:active, & .MuiToggleButton-root:active": {
                transform: "translateY(-2px) scale(0.99)",
              },
            }
          : {}),
      }}
    >
      <Header
        mode={lookupMode}
        onModeChange={allowLookupModeSwitch ? handleLookupModeChange : undefined}
        stackSize={lookupStackSize}
        stackOptions={stackOptions}
        onStackChange={blankMode ? undefined : handleLookupStackChange}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          px: `${PAGE_SIDE_PADDING_PX}px`,
          py: isCompactLayout ? 1 : 2,
          gap: isCompactLayout ? 1 : 4,
          minHeight: 0,
          overflowX: "hidden",
          overflowY: isCompactLayout ? "auto" : "hidden",
          ...(isCompactLayout ? THIN_SCROLLBAR_SX : {}),
        }}
      >
        {solverLayoutMode === "table" && !blankMode ? (
          renderTableModeLayout()
        ) : isCompactLayout ? (
          isCompactLandscape && !isMobilePhone ? (
            <>
              <Box sx={{ width: "56%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                  <StrategyViewToggle
                    value={viewMode}
                    onChange={(e, v) => v && setViewMode(v)}
                    evDisabled={isViewingPreflop}
                    seatLabel={strategySeatLabel}
                  containerRef={tutorialEvRef}
                  />
                </Box>
                <Box
                  ref={(el) => {
                    setGridRef(el);
                    tutorialOutputRef.current = el;
                    tutorialHandRef.current = el;
                  }}
                  sx={{ width: "100%" }}
                >
                  <HandMatrix
                    gridData={displayGridData}
                    actions={displayActions}
                    loading={loading}
                    overlayMessage={effectiveGridOverlayMessage}
                    onOverlayFold={blankPreflopOverlayFold}
                    onCellClick={handleCellClick}
                    selectedCell={selectedCell}
                    viewMode={viewMode}
                    clickableAll={isViewingPreflop}
                    isPreflop={isViewingPreflop}
                    preflopFoldRed={preflopFoldRed}
                    handReminderLabel={userHandLabel}
                    showHandReminder={showHandReminder}
                    postflopCarriedRange={activeRangeForDisplay}
                    currentStreet={viewingStreet}
                    demoFxEnabled={demoFxEnabled}
                  />
                </Box>
                <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                {!blankMode && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    {lookupMode === "cash" && (
                      <Typography sx={{ color: COLORS.textMuted, fontSize: "11px", fontWeight: 700, mr: 0.5 }}>
                        {`Cash ${String(lookupStackSize || "100bbs").toLowerCase()}`}
                      </Typography>
                    )}
                    <TutorialChromeButtons onTutorial={() => startTutorial({ firstRun: false, forceUnskippable: false })} layoutMode={solverLayoutMode} onLayoutModeChange={blankMode ? undefined : setLayoutMode} onReset={handleReset} />
                  </Box>
                )}
                {renderStreetTabs()}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box
                    ref={(el) => {
                      actionsScrollRef.current = el;
                      tutorialActionsRef.current = el;
                      if (viewingStreet === "preflop") tutorialPreflopRef.current = el;
                      if (viewingStreet === "flop") tutorialFlopRef.current = el;
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflowX: "auto",
                      overflowY: "hidden",
                      pb: 0.35,
                      ...THIN_SCROLLBAR_SX,
                    }}
                  >
                    {renderCurrentStreetActions()}
                  </Box>
                  <Box ref={tutorialTableRef} sx={{ flexShrink: 0, alignSelf: "flex-start" }}>{renderWholeHandTable()}</Box>
                </Box>
                <Box ref={tutorialComboRef}>
                <ComboBreakdown
                  cell={selectedCell?.cell}
                  actions={displayActions}
                  gridData={resolutionGridData}
                  viewMode={viewMode}
                  usedCards={usedCards}
                  suitMapping={suitMapping}
                  flopRankMap={flopRankMap}
                  activeRange={activeRangeForDisplay}
                  isPreflop={isViewingPreflop}
                  preflopFoldRed={preflopFoldRed}
                  reminderCombo={userHandCombo}
                  showHandReminder={showHandReminder}
                  desiredBoardCards={desiredBoardCards}
                  queriedBoardCards={queriedBoardCards}
                  textureResolutionMap={textureResolutionMap}
                  onComboSelect={handleComboSelect}
                />
                </Box>
              </Box>
            </>
          ) : isMobilePhone ? (
            <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {!blankMode && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {lookupMode === "cash" && (
                    <Typography sx={{ color: COLORS.textMuted, fontSize: "11px", fontWeight: 700, mr: 0.5 }}>
                      {`Cash ${String(lookupStackSize || "100bbs").toLowerCase()}`}
                    </Typography>
                  )}
                  <TutorialChromeButtons onTutorial={() => startTutorial({ firstRun: false, forceUnskippable: false })} layoutMode={solverLayoutMode} onLayoutModeChange={blankMode ? undefined : setLayoutMode} onReset={handleReset} />
                </Box>
              )}
              {renderStreetTabs()}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box
                  ref={bindActionsScroller}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 0.35,
                    ...THIN_SCROLLBAR_SX,
                  }}
                >
                  {renderCurrentStreetActions()}
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                <StrategyViewToggle
                  value={viewMode}
                  onChange={(e, v) => v && setViewMode(v)}
                  evDisabled={isViewingPreflop}
                  seatLabel={strategySeatLabel}
                  containerRef={tutorialEvRef}
                />
              </Box>
              <Box
                ref={(el) => {
                  setGridRef(el);
                  tutorialOutputRef.current = el;
                }}
                sx={{ width: "100%" }}
              >
                <HandMatrix
                  gridData={displayGridData}
                  actions={displayActions}
                  loading={loading}
                  overlayMessage={effectiveGridOverlayMessage}
                  onOverlayFold={blankPreflopOverlayFold}
                  onCellClick={handleCellClick}
                  selectedCell={selectedCell}
                  viewMode={viewMode}
                  clickableAll={isViewingPreflop}
                  isPreflop={isViewingPreflop}
                  preflopFoldRed={preflopFoldRed}
                  handReminderLabel={userHandLabel}
                  showHandReminder={showHandReminder}
                  postflopCarriedRange={activeRangeForDisplay}
                  currentStreet={viewingStreet}
                  demoFxEnabled={demoFxEnabled}
                />
              </Box>
              <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
              <Box ref={tutorialComboRef} sx={{ width: "100%", minHeight: 200, height: 220, flexShrink: 0, overflow: "hidden" }}>
                <ComboBreakdown
                  cell={selectedCell?.cell}
                  actions={displayActions}
                  gridData={resolutionGridData}
                  viewMode={viewMode}
                  usedCards={usedCards}
                  suitMapping={suitMapping}
                  flopRankMap={flopRankMap}
                  activeRange={activeRangeForDisplay}
                  isPreflop={isViewingPreflop}
                  preflopFoldRed={preflopFoldRed}
                  reminderCombo={userHandCombo}
                  showHandReminder={showHandReminder}
                  desiredBoardCards={desiredBoardCards}
                  queriedBoardCards={queriedBoardCards}
                  textureResolutionMap={textureResolutionMap}
                  isMobile={isMobilePhone}
                  loading={loading}
                  onComboSelect={handleComboSelect}
                />
              </Box>
              <Box ref={tutorialHandRef} sx={{ width: "100%", flexShrink: 0, overflow: "hidden" }}>
                <HandSelector
                  selectedHand={userHand}
                  onSelectHand={handleUserHandSelect}
                  usedCards={usedCards}
                  selectedCell={selectedCell?.cell}
                  actions={displayActions}
                  isPreflop={isViewingPreflop}
                  preflopFoldRed={preflopFoldRed}
                  gridData={resolutionGridData}
                  suitMapping={suitMapping}
                  flopRankMap={flopRankMap}
                  desiredBoardCards={desiredBoardCards}
                  queriedBoardCards={queriedBoardCards}
                  textureResolutionMap={textureResolutionMap}
                  activeRange={activeRangeForDisplay}
                  requiredHand={null}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {!blankMode && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  {lookupMode === "cash" && (
                    <Typography sx={{ color: COLORS.textMuted, fontSize: "11px", fontWeight: 700, mr: 0.5 }}>
                      {`Cash ${String(lookupStackSize || "100bbs").toLowerCase()}`}
                    </Typography>
                  )}
                  <TutorialChromeButtons onTutorial={() => startTutorial({ firstRun: false, forceUnskippable: false })} layoutMode={solverLayoutMode} onLayoutModeChange={blankMode ? undefined : setLayoutMode} onReset={handleReset} />
                </Box>
              )}
              {renderStreetTabs()}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box
                  ref={(el) => {
                    actionsScrollRef.current = el;
                    tutorialActionsRef.current = el;
                    if (viewingStreet === "preflop") tutorialPreflopRef.current = el;
                    if (viewingStreet === "flop") tutorialFlopRef.current = el;
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 0.35,
                    ...THIN_SCROLLBAR_SX,
                  }}
                >
                  {renderCurrentStreetActions()}
                </Box>
                <Box ref={tutorialTableRef} sx={{ flexShrink: 0, alignSelf: "flex-start" }}>{renderWholeHandTable()}</Box>
              </Box>
              <>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                    <StrategyViewToggle
                      value={viewMode}
                      onChange={(e, v) => v && setViewMode(v)}
                      evDisabled={isViewingPreflop}
                      seatLabel={strategySeatLabel}
                  containerRef={tutorialEvRef}
                    />
                  </Box>
                  <Box
                    ref={(el) => {
                      setGridRef(el);
                      tutorialOutputRef.current = el;
                    }}
                    sx={{ width: "100%" }}
                  >
                    <HandMatrix
                      gridData={displayGridData}
                      actions={displayActions}
                      loading={loading}
                      overlayMessage={effectiveGridOverlayMessage}
                      onOverlayFold={blankPreflopOverlayFold}
                      onCellClick={handleCellClick}
                      selectedCell={selectedCell}
                      viewMode={viewMode}
                      clickableAll={isViewingPreflop}
                      isPreflop={isViewingPreflop}
                      preflopFoldRed={preflopFoldRed}
                      handReminderLabel={userHandLabel}
                      showHandReminder={showHandReminder}
                      postflopCarriedRange={activeRangeForDisplay}
                      currentStreet={viewingStreet}
                      demoFxEnabled={demoFxEnabled}
                    />
                  </Box>
                  <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
                  <Box ref={tutorialComboRef}>
                  <ComboBreakdown
                    cell={selectedCell?.cell}
                    actions={displayActions}
                    gridData={resolutionGridData}
                    viewMode={viewMode}
                    usedCards={usedCards}
                    suitMapping={suitMapping}
                    flopRankMap={flopRankMap}
                    activeRange={activeRangeForDisplay}
                    isPreflop={isViewingPreflop}
                    preflopFoldRed={preflopFoldRed}
                    reminderCombo={userHandCombo}
                    showHandReminder={showHandReminder}
                    desiredBoardCards={desiredBoardCards}
                    queriedBoardCards={queriedBoardCards}
                    textureResolutionMap={textureResolutionMap}
                    isMobile={isMobilePhone}
                    loading={loading}
                    onComboSelect={handleComboSelect}
                  />
                  </Box>
                  <Box ref={tutorialHandRef} sx={{ width: "100%", flexShrink: 0, overflow: "hidden" }}>
                    <HandSelector
                      selectedHand={userHand}
                      onSelectHand={handleUserHandSelect}
                      usedCards={usedCards}
                      selectedCell={selectedCell?.cell}
                      actions={displayActions}
                      isPreflop={isViewingPreflop}
                      preflopFoldRed={preflopFoldRed}
                      gridData={resolutionGridData}
                      suitMapping={suitMapping}
                      flopRankMap={flopRankMap}
                      desiredBoardCards={desiredBoardCards}
                      queriedBoardCards={queriedBoardCards}
                      textureResolutionMap={textureResolutionMap}
                      activeRange={activeRangeForDisplay}
                      requiredHand={null}
                    />
                  </Box>
              </>
            </Box>
          )
        ) : (
          <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.25, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 0.1, mb: -0.2, minHeight: 22, flexWrap: "wrap" }}>
              {lookupMode === "cash" && (
                <Typography sx={{ color: COLORS.textMuted, fontSize: "11px", fontWeight: 700, mr: 0.5 }}>
                  {`Cash ${String(lookupStackSize || "100bbs").toLowerCase()}`}
                </Typography>
              )}
              {!blankMode && (
                <TutorialChromeButtons onTutorial={() => startTutorial({ firstRun: false, forceUnskippable: false })} layoutMode={solverLayoutMode} onLayoutModeChange={blankMode ? undefined : setLayoutMode} onReset={handleReset} />
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, min(50vw, calc(100dvh - 320px))) minmax(0, 1fr)",
                gridTemplateRows: "auto minmax(0, 1fr)",
                gap: `${DESKTOP_PANEL_GAP_PX}px`,
                minHeight: 0,
                flex: 1,
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  overflow: "hidden",
                }}
              >
                {SHOW_DEV_DEBUG_UI && (
                  <Button
                    onClick={() => setShowDevDebugPopup(true)}
                    variant="outlined"
                    size="small"
                    sx={{
                      flexShrink: 0,
                      color: COLORS.textMuted,
                      borderColor: COLORS.border,
                      fontSize: "9px",
                      py: 0.5,
                      mt: 2.3,
                      "&:hover": { borderColor: COLORS.white, color: COLORS.white },
                    }}
                  >
                    Debug
                  </Button>
                )}
                <Box
                  ref={(el) => {
                    actionsScrollRef.current = el;
                    tutorialActionsRef.current = el;
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "flex-start",
                    flexWrap: "nowrap",
                    gap: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 0.6,
                    ...THIN_SCROLLBAR_SX,
                  }}
                >
                  <Box ref={tutorialPreflopRef} sx={{ flexShrink: 0 }}>
                    <PreflopActionsRow actionHistory={preflopHistory} preflopState={preflopState} viewState={preflopViewState} selectorActions={isViewingPreflop ? selectorActions : []} onAction={handlePreflopAction} onNavigate={handleNavigate} onReturnToFrontier={handleReturnToFrontier} selectedIndex={selectedNavigation.street === "preflop" ? selectedNavigation.index : -1} disableActions={blankMode} flashSignal={actionTileFlash} />
                  </Box>
                  <Box ref={tutorialFlopRef} sx={{ flexShrink: 0 }}>
                    <PostflopActionsRow title="Flop Actions" streetCards={flop} cardCount={3} pot={displayStreetPot.flop} solverActions={viewingStreet === "flop" && flop.length === 3 && !showTurnModal ? selectorActions : []}
                      actionHistory={flopActionHistory} onAction={handlePostflopAction} isActive={viewingStreet === "flop" && flop.length === 3 && !showTurnModal}
                      isWaitingForGrid={isStreetWaitingForGrid("flop")} isLiveStreet={liveStreet === "flop" && selectedNavigation.street !== "preflop"}
                      player={player} positions={detectedSpot?.positions} onNavigate={handleNavigate} onReturnToFrontier={handleReturnToFrontier} street="flop" selectedActionIndex={selectedNavigation.street === "flop" ? selectedNavigation.index : -1} needsTurnCard={showTurnModal} flashSignal={actionTileFlash} />
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <PostflopActionsRow title="Turn Actions" streetCards={turn ? [turn] : []} cardCount={1} pot={displayStreetPot.turn} solverActions={viewingStreet === "turn" && turn && !showRiverModal ? selectorActions : []}
                      actionHistory={turnActionHistory} onAction={handlePostflopAction} isActive={viewingStreet === "turn" && turn && !showRiverModal}
                      isWaitingForGrid={isStreetWaitingForGrid("turn")} isLiveStreet={liveStreet === "turn" && selectedNavigation.street !== "preflop"}
                      player={player} positions={detectedSpot?.positions} onNavigate={handleNavigate} onReturnToFrontier={handleReturnToFrontier} street="turn" selectedActionIndex={selectedNavigation.street === "turn" ? selectedNavigation.index : -1} needsTurnCard={false} flashSignal={actionTileFlash} />
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    <PostflopActionsRow title="River Actions" streetCards={river ? [river] : []} cardCount={1} pot={displayStreetPot.river} solverActions={viewingStreet === "river" && river ? selectorActions : []}
                      actionHistory={riverActionHistory} onAction={handlePostflopAction} isActive={viewingStreet === "river" && river}
                      isWaitingForGrid={isStreetWaitingForGrid("river")} isLiveStreet={liveStreet === "river" && selectedNavigation.street !== "preflop"}
                      player={player} positions={detectedSpot?.positions} onNavigate={handleNavigate} onReturnToFrontier={handleReturnToFrontier} street="river" selectedActionIndex={selectedNavigation.street === "river" ? selectedNavigation.index : -1} needsTurnCard={false} flashSignal={actionTileFlash} />
                  </Box>
                </Box>
              </Box>

              <Box ref={tutorialTableRef} sx={{ flexShrink: 0, alignSelf: "start", justifySelf: "start" }}>
                {renderWholeHandTable()}
              </Box>

              <Box sx={{ minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden", pr: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                  <StrategyViewToggle
                    value={viewMode}
                    onChange={(e, v) => v && setViewMode(v)}
                    evDisabled={isViewingPreflop}
                    seatLabel={strategySeatLabel}
                  containerRef={tutorialEvRef}
                  />
                </Box>
                <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
                  <Box
                    ref={(el) => {
                      setGridRef(el);
                      tutorialOutputRef.current = el;
                    }}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                  <HandMatrix
                    gridData={displayGridData}
                    actions={displayActions}
                    loading={loading}
                    overlayMessage={effectiveGridOverlayMessage}
                    onOverlayFold={blankPreflopOverlayFold}
                    onCellClick={handleCellClick}
                    selectedCell={selectedCell}
                    viewMode={viewMode}
                    clickableAll={isViewingPreflop}
                    isPreflop={isViewingPreflop}
                    preflopFoldRed={preflopFoldRed}
                    handReminderLabel={userHandLabel}
                    showHandReminder={showHandReminder}
                    postflopCarriedRange={activeRangeForDisplay}
                    currentStreet={viewingStreet}
                    demoFxEnabled={demoFxEnabled}
                  />
                  </Box>
                </Box>
                <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: `${DESKTOP_RIGHT_STACK_GAP_PX}px`,
                  overflow: "hidden",
                  // Align combo/range pane top with the hand-grid top (not the Strategy/EV row).
                  mt: "30px",
                  height: gridHeight || "auto",
                }}
              >
                <Box ref={tutorialComboRef} sx={{ minHeight: isSmallLaptopScreen ? 160 : 0, flex: 1, overflow: "hidden", display: "flex" }}>
                  <ComboBreakdown
                    cell={selectedCell?.cell}
                    actions={displayActions}
                    gridData={resolutionGridData}
                    viewMode={viewMode}
                    usedCards={usedCards}
                    suitMapping={suitMapping}
                    flopRankMap={flopRankMap}
                    activeRange={activeRangeForDisplay}
                    isPreflop={isViewingPreflop}
                    preflopFoldRed={preflopFoldRed}
                    reminderCombo={userHandCombo}
                    showHandReminder={showHandReminder}
                    desiredBoardCards={desiredBoardCards}
                    queriedBoardCards={queriedBoardCards}
                    textureResolutionMap={textureResolutionMap}
                    onComboSelect={handleComboSelect}
                  />
                </Box>
                <Box ref={tutorialHandRef} sx={{ flexShrink: 0, overflow: "hidden" }}>
                  <HandSelector
                    selectedHand={userHand}
                    onSelectHand={handleUserHandSelect}
                    usedCards={usedCards}
                    selectedCell={selectedCell?.cell}
                    actions={displayActions}
                    isPreflop={isViewingPreflop}
                    preflopFoldRed={preflopFoldRed}
                    gridData={resolutionGridData}
                    suitMapping={suitMapping}
                    flopRankMap={flopRankMap}
                    desiredBoardCards={desiredBoardCards}
                    queriedBoardCards={queriedBoardCards}
                    textureResolutionMap={textureResolutionMap}
                    activeRange={activeRangeForDisplay}
                    requiredHand={null}
                  />
                </Box>
                {isSmallLaptopScreen ? (
                  <Box
                    ref={tutorialRangeRef}
                    sx={{
                      flexShrink: 0,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "6px",
                      backgroundColor: COLORS.bg,
                      overflow: "hidden",
                    }}
                  >
                    <Button
                      fullWidth
                      onClick={() => setRangePanelCollapsed((v) => !v)}
                      sx={{
                        justifyContent: "center",
                        textTransform: "none",
                        color: COLORS.textMuted,
                        border: "none",
                        borderRadius: 0,
                        borderBottom: rangePanelCollapsed ? "none" : `1px solid ${COLORS.border}`,
                        minHeight: 30,
                        py: 0.35,
                        fontSize: "10px",
                        fontWeight: 700,
                        position: "relative",
                        backgroundColor: "rgba(255,255,255,0.02)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.05)", color: COLORS.text },
                      }}
                    >
                      {rangePanelCollapsed ? "Show range" : "Hide range"}
                      <Box component="span" sx={{ fontSize: 12, lineHeight: 1, position: "absolute", right: 10 }}>
                        {rangePanelCollapsed ? "▲" : "▼"}
                      </Box>
                    </Button>
                    <Box
                      sx={{
                        maxHeight: rangePanelCollapsed ? 0 : 280,
                        opacity: rangePanelCollapsed ? 0 : 1,
                        overflow: "hidden",
                        transition: "max-height 280ms ease, opacity 200ms ease",
                        pointerEvents: rangePanelCollapsed ? "none" : "auto",
                      }}
                    >
                      <Box sx={{ height: 240, minHeight: 240 }}>
                        <RangeCarryDebug
                          playerRanges={playerRanges}
                          positions={detectedSpot?.positions}
                          viewMode={rangeBreakdownView}
                          onViewModeChange={setRangeBreakdownView}
                          equityMap={deferredRangeEquityMap}
                          usedCards={usedCards}
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box ref={tutorialRangeRef} sx={{ minHeight: 0, flex: "0 1 34%", overflow: "hidden" }}>
                    <RangeCarryDebug
                      playerRanges={playerRanges}
                      positions={detectedSpot?.positions}
                      viewMode={rangeBreakdownView}
                      onViewModeChange={setRangeBreakdownView}
                      equityMap={deferredRangeEquityMap}
                      usedCards={usedCards}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      
      {demoFxEnabled && demoBurst && (
        <Box
          key={demoBurst.key}
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 2200,
            "@keyframes demoFxScreenBurst": {
              "0%": { transform: "translate(-50%, -50%) scale(0.6)", opacity: 0.65 },
              "65%": { transform: "translate(-50%, -50%) scale(70)", opacity: 0.22 },
              "100%": { transform: "translate(-50%, -50%) scale(120)", opacity: 0 },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: `${demoBurst.x}px`,
              top: `${demoBurst.y}px`,
              width: 26,
              height: 26,
              borderRadius: "999px",
              background: "radial-gradient(circle, rgba(185,158,79,0.95) 0%, rgba(86,236,207,0.45) 42%, rgba(7,115,150,0.12) 100%)",
              boxShadow: "0 0 40px rgba(185,158,79,0.55)",
              animation: "demoFxScreenBurst 760ms ease-out forwards",
            }}
          />
        </Box>
      )}
      <Dialog
        open={showFullRangeDialog}
        onClose={() => setShowFullRangeDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, backgroundImage: "none", maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ color: COLORS.white, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, fontFamily: FUTURA_FONT }}>
          Range & combos
          <IconButton size="small" onClick={() => setShowFullRangeDialog(false)} sx={{ color: COLORS.textMuted }}>
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.15fr) minmax(0, 0.85fr)" },
              gap: 1.25,
              alignItems: "stretch",
              flex: 1,
              minHeight: { xs: 420, md: 520 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ mb: 1 }}>
                <StrategyViewToggle
                  value={viewMode}
                  onChange={(e, v) => v && setViewMode(v)}
                  evDisabled={isViewingPreflop}
                  seatLabel={strategySeatLabel}
                  containerRef={tutorialEvRef}
                />
              </Box>
              <HandMatrix
                gridData={displayGridData}
                actions={displayActions}
                loading={loading}
                overlayMessage={effectiveGridOverlayMessage}
                onOverlayFold={blankPreflopOverlayFold}
                onCellClick={handleCellClick}
                selectedCell={selectedCell}
                viewMode={viewMode}
                clickableAll={isViewingPreflop}
                isPreflop={isViewingPreflop}
                preflopFoldRed={preflopFoldRed}
                handReminderLabel={userHandLabel}
                showHandReminder={showHandReminder}
                postflopCarriedRange={activeRangeForDisplay}
                currentStreet={viewingStreet}
                demoFxEnabled={demoFxEnabled}
              />
              <GridLegend actions={displayActions} isPreflop={isViewingPreflop} preflopFoldRed={preflopFoldRed} />
            </Box>
            <Box sx={{ minHeight: 240, height: { xs: 280, md: "100%" }, overflow: "hidden", minWidth: 0 }}>
              <ComboBreakdown
                cell={selectedCell?.cell}
                actions={displayActions}
                gridData={resolutionGridData}
                viewMode={viewMode}
                usedCards={usedCards}
                suitMapping={suitMapping}
                flopRankMap={flopRankMap}
                activeRange={activeRangeForDisplay}
                isPreflop={isViewingPreflop}
                preflopFoldRed={preflopFoldRed}
                reminderCombo={userHandCombo}
                showHandReminder={showHandReminder}
                desiredBoardCards={desiredBoardCards}
                queriedBoardCards={queriedBoardCards}
                textureResolutionMap={textureResolutionMap}
                isMobile={isMobilePhone}
                loading={loading}
                onComboSelect={handleComboSelect}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <FlopCardPopup open={showFlopModal} onClose={() => setShowFlopModal(false)} onSelect={handleFlopSelect} selectedCards={selectedFlopCards} setSelectedCards={setSelectedFlopCards} availableFlops={detectedSpot?.flops} requiredCards={null} />
      <CardSelectorPopup open={showTurnModal} onClose={() => setShowTurnModal(false)} onSelect={handleTurnSelect} title="Select Turn Card" usedCards={usedCards} requiredCard={null} />
      <CardSelectorPopup open={showRiverModal} onClose={() => setShowRiverModal(false)} onSelect={handleRiverSelect} title="Select River Card" usedCards={usedCards} requiredCard={null} />
      {FLOP_PAYWALL_ENABLED && (
        <FlopQuotaModal
          open={showFlopQuotaModal}
          onClose={() => setShowFlopQuotaModal(false)}
          quotaInfo={flopQuotaInfo}
          onUpgrade={() => {
            if (typeof window.__pmOpenSubscriptionGate === "function") window.__pmOpenSubscriptionGate();
            else window.dispatchEvent(new CustomEvent("pm:require-subscription", { detail: { stage: "postflop_quota" } }));
          }}
        />
      )}
      {SHOW_DEV_DEBUG_UI && showDevDebugPopup && (
        <Backdrop open onClick={() => setShowDevDebugPopup(false)} sx={{ zIndex: 1320, backgroundColor: "rgba(0,0,0,0.8)" }}>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: 560,
              maxWidth: "94vw",
              maxHeight: "88vh",
              overflow: "hidden",
              backgroundColor: COLORS.cardBg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              p: 1.25,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ color: COLORS.text, fontSize: "11px", fontWeight: 700 }}>Debug</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowDevDebugPopup(false)}
                sx={{ color: COLORS.textMuted, borderColor: COLORS.border, fontSize: "9px", py: 0.2, minWidth: "unset", px: 0.8 }}
              >
                Close
              </Button>
            </Box>
            <Box sx={{ overflow: "hidden", pr: 0.4 }}>
              <BoardQueryDebug
                flop={flop}
                turn={turn}
                river={river}
                canonicalFlop={canonicalFlop}
                canonicalTurn={canonicalTurn}
                canonicalRiver={canonicalRiver}
              />
              <SuitMappingDebug suitMapping={suitMapping} />
            </Box>
          </Box>
        </Backdrop>
      )}
      {SHOW_DEV_ERROR_BANNER && error && (<Box sx={{ mx: 2, mb: 1, p: 1, backgroundColor: "#2a1515", borderRadius: "4px", flexShrink: 0 }}><Typography sx={{ color: COLORS.red, fontSize: "10px" }}>{error}</Typography></Box>)}
      <PageTutorial
        open={tutorialOpen}
        steps={tutorialSteps}
        stepIndex={tutorialStep}
        onContinue={continueTutorial}
        onSkip={finishTutorial}
        skippable={!tutorialFirstRun}
        continueEnabled={tutorialContinueEnabled}
        showContinue={tutorialShowContinue}
        continueLabel={tutorialStepId === "finale" ? "Got it" : null}
      />
    </Box>
  );
};

export default Solver;
